import { useLayoutEffect, useMemo, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveStdFilesData, saveQcFilesData, saveUnkFilesData, saveAreaUnderPeaks, saveAreaUnderPeaksQc, saveAreaUnderPeaksUnk, batchUpdatePeakConcentrations, beginResultsEdit, discardResultsEdit, finalizeResultsEdit, savePeaksObject, togglePeakVisibility, setHiddenPeaks, updatePeakConcentration } from '../../reduxSlices/results/resultsSlice';
import { peakProcessingAPI, targetConcentrationPredictorAPI } from '../../api/api';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

function ChartPreview(props) {
  const dispatch = useDispatch();

  // Force chart recreation state - incrementing this will trigger useLayoutEffect
  const [chartRecreationCounter, setChartRecreationCounter] = useState(0);

  // Chart loading state for animations
  const [isChartLoading, setIsChartLoading] = useState(true);

  const stdFiles = useSelector((s) => s.results.stdFiles);
  const qcFiles = useSelector((s) => s.results.qcFiles);
  const unkFiles = useSelector((s) => s.results.unkFiles);
  const stdAreasState = useSelector((s) => s.results.areaUnderPeaks);
  const qcAreasState = useSelector((s) => s.results.areaUnderPeaksQc);
  const unkAreasState = useSelector((s) => s.results.areaUnderPeaksUnk);
  const sigma = useSelector((s) => s.results.sigma);
  const gamma = useSelector((s) => s.results.gamma);
  const allReportingPeaks = useSelector((s) => s.results.allReportingPeaks);
  const peaksObject = useSelector((s) => s.results.peaksObject);
  const isEditingResults = useSelector((s) => s.results._isEditingResults);
  const selectedFileName = useSelector((s) => s.results.selectedFileName);
  const selectedFileType = useSelector((s) => s.results.selectedFileType);

  // Helpers: reporting group lookup and peakIndex resolver
  const getGroupKeyForVal = (peakVal) => {
    if (peakVal == null) return null;
    const pv = Number(peakVal);
    const entries = Object.entries(allReportingPeaks || {});
    for (const [rpKey, list] of entries) {
      const arr = Array.isArray(list) ? list : [];
      if (arr.some((nom) => Math.abs(Number(nom) - pv) <= 1e-3)) return rpKey;
    }
    return null;
  };

  const resolvePeakIndex = (peakVal) => {
    if (!Array.isArray(peaksObject)) return -1;
    const kv = Number(peakVal);
    // 1) Try tolerant numeric match on "peak" value
    let idx = peaksObject.findIndex((p) => {
      const pv = Number(p?.peak);
      return Number.isFinite(pv) && Number.isFinite(kv) && Math.abs(pv - kv) <= 1e-3;
    });
    if (idx >= 0) return idx;
    // 2) Fallback: match by reporting group
    const targetGroup = getGroupKeyForVal(kv);
    if (!targetGroup) return -1;
    const entries = Object.entries(allReportingPeaks || {});
    idx = peaksObject.findIndex((p) => {
      const pv = Number(p?.peak);
      if (!Number.isFinite(pv)) return false;
      // find group for this p.peak
      let g = null;
      for (const [rpKey, list] of entries) {
        const arr = Array.isArray(list) ? list : [];
        if (arr.some((nom) => Math.abs(Number(nom) - pv) <= 1e-3)) { g = rpKey; break; }
      }
      return g === targetGroup;
    });
    return idx;
  };

  // Establish file context early so refs can depend on it safely
  const fileName = props.name;
  const currentFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
  const currentFileType = currentFile ? (stdFiles[fileName] ? 'standard' : qcFiles[fileName] ? 'qualityControl' : 'unknown') : null;
  const chartId = `chartdiv-${(fileName || 'chart').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  // Keep latest Redux file maps in refs to avoid stale closures in async flows
  const stdFilesRef = useRef(stdFiles);
  const qcFilesRef = useRef(qcFiles);
  const unkFilesRef = useRef(unkFiles);
  useEffect(() => { stdFilesRef.current = stdFiles; }, [stdFiles]);
  useEffect(() => { qcFilesRef.current = qcFiles; }, [qcFiles]);
  useEffect(() => { unkFilesRef.current = unkFiles; }, [unkFiles]);
  // Also keep the latest currentFile in a ref
  const currentFileRef = useRef(currentFile);
  useEffect(() => { currentFileRef.current = currentFile; }, [currentFile]);

  // Build ranges view from current file results
  const computedRange = useMemo(() => {
    // Always build from the freshest Redux state for this file
    const latest = (stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null);
    // Handle both old and new file structure
    const fileData = latest?.fileData || latest;
    if (!fileData?.result) return {};
    const out = {};
    Object.entries(fileData.result).forEach(([key, res]) => {
      if (!res?.to_analyze) return;

      // Always use peak-level data when available, regardless of single or multi-peak
      let baselineX = res.x || [];
      let baselineY = res.y_baseline || [];

      // If there are peaks with their own baseline data, use the first peak's data
      if (res.peaks) {
        const peaksWithBaseline = Object.values(res.peaks).filter(peak =>
          peak && Array.isArray(peak.x_comp) && Array.isArray(peak.y_baseline) &&
          peak.x_comp.length >= 2 && peak.y_baseline.length === peak.x_comp.length
        );

        if (peaksWithBaseline.length > 0) {
          // Use the first peak's baseline data (x_comp for consistency)
          const representativePeak = peaksWithBaseline[0];
          baselineX = representativePeak.x_comp;
          baselineY = representativePeak.y_baseline;
        }
      }

      out[key] = {
        x: baselineX,
        y_baseline: baselineY,
        y_comp: {},
        // CRITICAL: Preserve the complete peaks structure with peak-level arrays
        peaks: res.peaks ? JSON.parse(JSON.stringify(res.peaks)) : {}
      };
      const peaks = res.peaks || {};
      Object.entries(peaks).forEach(([pk, pv]) => {
        // Ensure all peaks have y_comp for unified processing
        if (pv?.y_comp) {
          out[key].y_comp[pk] = [...pv.y_comp];
        } else if (pv?.y && Array.isArray(pv.y)) {
          // Initialize y_comp from y for single peaks to ensure unified handling
          out[key].y_comp[pk] = [...pv.y];
        }
      });
    });
    return out;
  }, [stdFiles, qcFiles, unkFiles, fileName]);

  // Persist last endpoints per range (per file)
  const lastEndpointsRef = useRef(new Map());
  const lastFileRef = useRef(fileName);
  const lastSelectedFileRef = useRef(selectedFileName);
  useEffect(() => {
    const currentFileName = selectedFileName || fileName;
    const previousFileName = lastSelectedFileRef.current || lastFileRef.current;

    if (previousFileName !== currentFileName) {
      const previousFile = previousFileName;

      // If we were editing when switching files, handle the previous file's state
      if (isEditingResults && previousFile) {
        // If there are unsaved changes in the previous file, we should ideally prompt user
        // For now, we'll finalize (save) the previous file's changes
        // console.log('ChartPreview: Finalizing changes for previous file before switch:', previousFile);
        dispatch(finalizeResultsEdit());
        // Clear the snapshot and changes for the previous file
        try {
          snapshotsByFileRef.current.delete(previousFile);
          clearChangesForFile(previousFile);
        } catch { }
      }

      // Reset state for new file
      lastEndpointsRef.current = new Map();
      lastFileRef.current = fileName;

      // If we were in delete mode when switching files, handle snapshots and discard button
      if (deletingRef.current) {
        // Clear the delete snapshot and concentration snapshot for the previous file if it exists (changes are kept)
        if (previousFile) {
          deletePeakSnapshotsByFileRef.current.delete(previousFile);
          concentrationSnapshotsByFileRef.current.delete(previousFile);
        }

        // Always hide discard button when switching files
        // It will be re-shown later if the new file has unsaved deletions
        try {
          window.dispatchEvent(new CustomEvent('delete-peak-history-state', {
            detail: { canDiscard: false }
          }));
        } catch { }

        // Capture a new snapshot for the new file
        const fileNameToUse = selectedFileName || fileName;
        const fileTypeToUse = selectedFileType || currentFileType;
        if (fileNameToUse) {
          const fileMap = fileTypeToUse === 'standard' ? stdFiles :
            fileTypeToUse === 'qualityControl' ? qcFiles :
              unkFiles;
          const file = fileMap[fileNameToUse];
          const fileData = file?.fileData || file;
          const hiddenPeaks = fileData?.hiddenPeaks || {};
          deletePeakSnapshotsByFileRef.current.set(fileNameToUse, { ...hiddenPeaks });

          // Also capture concentration snapshot for the new file
          const concentrationSnapshot = {};
          if (fileData?.result) {
            for (const [rangeKey, rangeData] of Object.entries(fileData.result)) {
              if (!rangeData?.peaks) continue;
              for (const peakKey of Object.keys(rangeData.peaks)) {
                const peakIndex = resolvePeakIndex(peakKey);
                if (peakIndex >= 0 && peaksObject?.[peakIndex]) {
                  const peakData = peaksObject[peakIndex];
                  let currentConc = null;

                  if (fileTypeToUse === 'standard') {
                    currentConc = peakData.stdConcentrations?.[fileNameToUse];
                  } else if (fileTypeToUse === 'qualityControl') {
                    const qcArr = peakData.qcConcentrations || [];
                    const entry = qcArr.find(o => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileNameToUse));
                    currentConc = entry?.[fileNameToUse];
                  } else if (fileTypeToUse === 'unknown') {
                    const unkArr = peakData.unkConcentrations || [];
                    const entry = unkArr.find(o => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileNameToUse));
                    currentConc = entry?.[fileNameToUse];
                  }

                  concentrationSnapshot[peakKey] = {
                    peakIndex,
                    concentration: currentConc
                  };
                }
              }
            }
          }
          concentrationSnapshotsByFileRef.current.set(fileNameToUse, concentrationSnapshot);

          // Since we just captured a snapshot with current state, there should be no changes
          // The discard button should remain hidden until the user deletes a peak
        }
      }

      // If we were in Insert Peak mode when switching files, finalize and reset
      if (insertingPeakRef.current) {
        // Clear the restored peaks list (finalizing changes for previous file)
        restoredPeaksRef.current = [];

        // Disable discard button for the new file
        try {
          window.dispatchEvent(new CustomEvent('insert-peak-history-state', {
            detail: { canDiscard: false }
          }));
        } catch { }
      }

      // If we were in Split Peak mode when switching files, finalize and reset
      if (splittingPeakRef.current) {
        // Clear the split peaks list (finalizing changes for previous file)
        splitPeaksRef.current = [];

        // Disable discard button for the new file
        try {
          window.dispatchEvent(new CustomEvent('split-peak-history-state', {
            detail: { canDiscard: false }
          }));
        } catch { }
      }

      // Reset chart navigation state to prevent issues from previous file
      try {
        const chart = chartRef.current;
        if (chart) {
          // Reset to proper navigation settings based on current edit mode
          if (editingRef.current) {
            chart.set('panX', false);
            chart.set('panY', false);
            chart.set('wheelX', 'none');
          } else {
            chart.set('panX', true);
            chart.set('panY', true);
            chart.set('wheelX', 'panX');
          }
          chart.set('wheelY', 'zoomXY');
        }
      } catch { }

      // If still in edit mode from UI perspective, start editing the new file
      if (editingRef.current) {
        // ('ChartPreview: Starting edit mode for new file after switch:', fileName);
        // Note: beginResultsEdit() will be called in useLayoutEffect for the new file
        clearChangesForFile(fileName); // Reset change tracking for new file
      } else {
        // Not in edit mode, just clear any stale change state
        clearChangesForFile(fileName);
      }

      // Update tracking refs
      lastSelectedFileRef.current = selectedFileName;
      lastFileRef.current = fileName;
    }
  }, [fileName, selectedFileName, isEditingResults, dispatch, stdFiles, qcFiles, unkFiles, selectedFileType, currentFileType]);

  // Chart refs
  const rootRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(0);
  const xAxisRef = useRef(null);
  const yAxisRef = useRef(null);
  const mainSeriesRef = useRef(null);
  const vScrollRef = useRef(null);
  const baselineByRangeRef = useRef(new Map()); // rangeKey -> { p1,p2,line,fill }
  const baselineColorByRangeRef = useRef(new Map()); // rangeKey -> am5.Color (match comp fill)
  const compColorsByRangeRef = useRef(new Map()); // rangeKey -> Map(compKey -> am5.Color)
  const peakColorsByFileRef = useRef(new Map());  // `${rangeKey}::${peakKey}` -> am5.Color
  const colorSyncTimersRef = useRef([]); // track raf/timeouts for color syncs
  const activeRangeRef = useRef(null); // which baseline is currently active for interactions
  const compFillsByRangeRef = useRef(new Map()); // rangeKey -> Map(peakKey -> fillSeries)
  const selectedPeakRef = useRef(null); // { rangeKey, peakKey }
  // Per-range debounced submit timers
  const submitDebounceMapRef = useRef(new Map());
  const SUBMIT_DEBOUNCE_MS = 500;
  const editingRef = useRef(false); // gated by Toolbox 'Edit Baseline' toggle
  const deletingRef = useRef(false); // gated by Toolbox 'Delete Peak' toggle
  const deletePeakModeRef = useRef('single'); // 'single' or 'convoluted'
  const insertingPeakRef = useRef(false); // gated by Toolbox 'Insert Peak' toggle
  const splittingPeakRef = useRef(false); // gated by Toolbox 'Split Peak' toggle
  const drawingBaselineRef = useRef(null); // Track drawing state: { rangeKey, startPoint, tempLine, tempFill }
  const drawingSplitLineRef = useRef(null); // Track split line drawing: { rangeKey, peakKey, splitX, tempLine }
  const completedInsertLinesRef = useRef([]); // Store completed insert lines for cleanup
  const completedInsertFillsRef = useRef([]); // Store completed insert fills for cleanup
  const completedSplitLinesRef = useRef([]); // Store completed split lines for cleanup
  const assignmentLabelsRef = useRef([]); // Store assignment label series for multi-peak selection cleanup
  const peakSelectionTrianglesRef = useRef([]); // Store triangle indicators for split peak selection in multi-peak ranges
  const selectedSplitPeakRef = useRef(null); // Currently selected peak for splitting: { rangeKey, peakKey, peak, xMin, xMax }
  const highlightedSplitPeakRef = useRef(null); // Currently highlighted peak during split hover: { rangeKey, peakKey }
  const restoredPeaksRef = useRef([]); // Track all restored peaks in current session: [{ fileName, fileType, peakKey }, ...]
  const splitPeaksRef = useRef([]); // Track all split peaks in current session: [{ fileName, fileType, originalPeakKey, newPeakKey }, ...]
  const insertPeakSnapshotsByFileRef = useRef(new Map()); // Per-file snapshots for insert discard
  // Per-file visual snapshots for discard: fileName -> Map(rangeKey -> { control?:{p1,p2}, perComp?: Map(peakKey->{p1,p2}) })
  const snapshotsByFileRef = useRef(new Map());
  // Per-file change tracking: fileName -> boolean
  const hasChangesByFileRef = useRef(new Map());
  // Per-file hiddenPeaks snapshots for delete discard: fileName -> { [peakKey]: true }
  const deletePeakSnapshotsByFileRef = useRef(new Map());
  // Per-file concentration snapshots for delete discard: fileName -> { peakKey: { peakIndex, concentration } }
  const concentrationSnapshotsByFileRef = useRef(new Map());
  const historyRef = useRef(new Map()); // key: `${rangeKey}::${peakKey||'range'}` -> { undo:[], redo:[] }
  // Whether a baseline is actively being dragged
  const isDraggingRef = useRef(false);
  // Stores frozen Y range (in value space) while dragging so we can restore viewport precisely
  const yFreezeRef = useRef(null);
  // Disposer for temporary Y-freeze listener during drag
  const yFreezeDisposerRef = useRef(null);
  const yFreezeRafRef = useRef(0);
  const Y_EPS = 1e-4;
  // Guard to prevent feedback loops when syncing Y scrollbar and axis
  const ySyncingRef = useRef(false);
  // Selection visuals
  const selectedMarkerRef = useRef(null);

  // Ref for the rebuild function that will be set after chart initialization
  const rebuildBaselinesFromReduxDataRef = useRef(null);

  // Track previous visibility signature to detect changes
  const prevVisibilitySignatureRef = useRef('');

  // Watch for file-specific hidden peaks changes and force chart recreation
  useEffect(() => {
    // Create a signature of hidden peaks for this file
    const fileData = currentFile?.fileData || currentFile;
    const hiddenPeaks = fileData?.hiddenPeaks || {};
    const visibilitySignature = JSON.stringify(Object.keys(hiddenPeaks).sort());

    // If visibility changed (and it's not the initial render), recreate the chart
    if (prevVisibilitySignatureRef.current !== visibilitySignature && prevVisibilitySignatureRef.current !== '') {
      // console.log('ChartPreview: Peak visibility changed for file', fileName, ', forcing chart recreation');
      setIsChartLoading(true);
      setChartRecreationCounter(prev => prev + 1);
    }

    prevVisibilitySignatureRef.current = visibilitySignature;
  }, [currentFile, fileName]);

  // Helper functions for per-file change tracking (baseline edit mode)
  const setChangesForFile = (file = fileName, hasChanges = true) => {
    hasChangesByFileRef.current.set(file, hasChanges);
    // Update discard button state based on current file
    if (file === fileName) {
      // console.log('ChartPreview: Sending baseline-history-state canDiscard:', hasChanges, 'for file:', file);
      try {
        window.dispatchEvent(new CustomEvent('baseline-history-state', { detail: { canDiscard: hasChanges } }));
      } catch { }
    }
  };
  const clearChangesForFile = (file = fileName) => {
    hasChangesByFileRef.current.delete(file);
    // Update discard button state if this is the current file
    if (file === fileName) {
      // console.log('ChartPreview: Sending baseline-history-state canDiscard: false (cleared changes for file)');
      try {
        window.dispatchEvent(new CustomEvent('baseline-history-state', { detail: { canDiscard: false } }));
      } catch { }
    }
  };

  // Helper function to check if current file has delete changes
  const hasDeleteChangesForCurrentFile = () => {
    const fileNameToUse = selectedFileName || fileName;
    const snapshot = deletePeakSnapshotsByFileRef.current.get(fileNameToUse);
    if (!snapshot) return false;

    // Get current hiddenPeaks
    const fileTypeToUse = selectedFileType || currentFileType;
    const fileMap = fileTypeToUse === 'standard' ? stdFiles :
      fileTypeToUse === 'qualityControl' ? qcFiles :
        unkFiles;
    const file = fileMap[fileNameToUse];
    const fileData = file?.fileData || file;
    const currentHidden = fileData?.hiddenPeaks || {};

    // Compare snapshot with current state
    const snapshotKeys = Object.keys(snapshot);
    const currentKeys = Object.keys(currentHidden);

    // If different number of hidden peaks, there are changes
    if (snapshotKeys.length !== currentKeys.length) return true;

    // Check if any peak differs
    for (const key of currentKeys) {
      if (!snapshot[key]) return true; // New hidden peak not in snapshot
    }

    return false;
  };

  const yOnLine = (x, p1, p2) => {
    const dx = p2.x - p1.x;
    if (Math.abs(dx) < 1e-12) return p1.y;
    const t = (x - p1.x) / dx;
    return p1.y + t * (p2.y - p1.y);
  };

  // Helpers for fills outside range closures
  const buildCompFillDataGlobal = (rangeKey, compKey, P1, P2) => {
    try {
      // Use main chromatogram data for X coordinates to show full baseline range
      const mainChromatogram = Array.isArray(props.data) ? props.data : [];
      const chromatogramX = mainChromatogram.map(d => d.x);
      const chromatogramY = mainChromatogram.map(d => d.y);

      if (chromatogramX.length === 0) {
        return [];
      }

      const interpY = (xv, xs, ys) => {
        const n = xs.length;
        if (!n) return null;
        if (xv <= xs[0]) return ys[0];
        if (xv >= xs[n - 1]) return ys[n - 1];
        let lo = 0, hi = n - 1;
        while (hi - lo > 1) {
          const mid = (lo + hi) >> 1;
          if (xs[mid] === xv) return ys[mid];
          if (xs[mid] < xv) lo = mid; else hi = mid;
        }
        const x0 = xs[lo], y0 = ys[lo];
        const x1 = xs[hi], y1 = ys[hi];
        const t = x1 === x0 ? 0 : (xv - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
      };

      // Get all chromatogram points within the baseline range
      const m1 = Math.min(P1.x, P2.x);
      const m2 = Math.max(P1.x, P2.x);
      const xs = chromatogramX.filter(x => x >= m1 && x <= m2).sort((a, b) => a - b);

      if (xs.length === 0) {
        return [];
      }

      // Get hidden peaks info to check for overlaps
      const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
      const freshFileData = freshFile?.fileData || freshFile;
      const hiddenPeaks = freshFileData?.hiddenPeaks || {};

      // Build array of visible (not hidden) peak ranges to exclude
      // Check ALL peaks in ALL ranges (not just current range)
      const visiblePeakRanges = [];
      const allRanges = freshFileData?.result || {};

      Object.keys(allRanges).forEach(rKey => {
        const rData = allRanges[rKey];
        const allPeaks = rData?.peaks || {};

        Object.keys(allPeaks).forEach(pk => {
          // Skip the current peak being edited
          if (pk === compKey && rKey === rangeKey) return;

          // Only consider visible peaks (not hidden)
          if (!hiddenPeaks[pk]) {
            const peak = allPeaks[pk];
            const peakX = peak?.originalXComp || peak?.x_comp || peak?.x || [];
            if (peakX.length > 0) {
              const peakMinX = Math.min(...peakX);
              const peakMaxX = Math.max(...peakX);
              // Check if this peak overlaps with our baseline range
              if (peakMaxX >= m1 && peakMinX <= m2) {
                visiblePeakRanges.push({ minX: peakMinX, maxX: peakMaxX });
              }
            }
          }
        });
      });

      // Build fill data, excluding areas covered by visible peaks
      return xs.map((xv, i) => {
        // Check if this X value falls within any visible peak range
        const isInVisiblePeak = visiblePeakRanges.some(range => xv >= range.minX && xv <= range.maxX);

        if (isInVisiblePeak) {
          // For visible peaks, use baseline for both y1 and y2 (no fill area)
          return { x: xv, y1: yOnLine(xv, P1, P2), y2: yOnLine(xv, P1, P2) };
        } else {
          // For areas without visible peaks (raw chromatogram or hidden peaks), show normal fill
          const y1 = interpY(xv, chromatogramX, chromatogramY);
          return { x: xv, y1: y1, y2: yOnLine(xv, P1, P2) };
        }
      });
    } catch { return []; }
  };

  const buildFillGlobal = (P1, P2) => {
    try {
      const xMin = Math.min(P1.x, P2.x);
      const xMax = Math.max(P1.x, P2.x);
      const all = Array.isArray(props.data) ? props.data : [];
      const inRange = all.filter((d) => d.x >= xMin && d.x <= xMax);
      const out = [];
      if (!inRange.length) return out;
      const addPt = (xv, yv) => out.push({ x: xv, y1: yv, y2: yOnLine(xv, P1, P2) });
      const firstX = inRange[0].x;
      const lastX = inRange[inRange.length - 1].x;
      if (firstX > xMin) addPt(xMin, inRange[0].y);
      inRange.forEach((d) => addPt(d.x, d.y));
      if (lastX < xMax) addPt(xMax, inRange[inRange.length - 1].y);
      return out;
    } catch { return []; }
  };

  // Recalculate calibration curve and standard concentrations when standard file areas change
  const recalculateStandardConcentrations = async (peakGroupIndex, updatedStandardAreas, currentQcAreas = null, currentUnkAreas = null) => {
    try {
      // console.log('=== CALIBRATION CURVE RECALCULATION START ===');
      // console.log('Peak Group Index:', peakGroupIndex);
      // console.log('Updated Standard Areas received:', updatedStandardAreas);
      // console.log('Number of standard files:', Object.keys(updatedStandardAreas || {}).length);
      // console.log('Standard files and their areas:', Object.entries(updatedStandardAreas || {}));

      // Get the current peaks object to access target concentrations and update calibration curve
      const currentPeaksObject = Array.isArray(peaksObject) ? peaksObject : Object.values(peaksObject || {});
      const peakData = currentPeaksObject[peakGroupIndex];

      if (!peakData || !Array.isArray(peakData.tc)) {
        console.warn('No target concentration data found for peak group index:', peakGroupIndex);
        return;
      }

      // Build targets object from the peak's tc array (target concentrations - manually entered)
      // IMPORTANT: tc values are TARGET CONCENTRATIONS and should NEVER be modified after initial entry
      // These are the "known" reference values for standards used to build the calibration curve
      const targetsObject = peakData.tc.reduce((acc, tcObj) => {
        const key = Object.keys(tcObj).find(k => k !== 'userEdited');
        if (key && tcObj[key] !== "" && !isNaN(tcObj[key])) {
          // Only include non-empty, valid target concentrations
          acc[key] = tcObj[key];
        }
        return acc;
      }, {});

      // console.log('Targets object for calibration (target concentrations - CONSTANT):', targetsObject);

      // Ensure we have matching standard files in both areas and targets
      const commonStandardFiles = Object.keys(updatedStandardAreas).filter(fileName =>
        fileName in targetsObject && Number.isFinite(updatedStandardAreas[fileName])
      );

      if (commonStandardFiles.length < 2) {
        console.warn('Need at least 2 standards with both area and target concentration for calibration curve');
        return;
      }

      // Filter to only include files that have both area and target concentration
      const filteredAreas = {};
      const filteredTargets = {};
      commonStandardFiles.forEach(fileName => {
        filteredAreas[fileName] = updatedStandardAreas[fileName];
        filteredTargets[fileName] = targetsObject[fileName];
      });

      // console.log('Filtered areas for calibration:', filteredAreas);
      // console.log('Filtered targets for calibration:', filteredTargets);

      // Call backend API to recalculate calibration curve with updated areas and target concentrations
      const { calibration_equation: newCalibrationEquation } =
        await targetConcentrationPredictorAPI(filteredAreas, filteredTargets);

      // console.log('New calibration equation from backend:', newCalibrationEquation);

      // Update the peaks object with the new calibration equation
      const updatedPeaksObject = [...currentPeaksObject];
      updatedPeaksObject[peakGroupIndex] = {
        ...peakData,
        equation: newCalibrationEquation
      };

      // Save updated peaks object to Redux
      dispatch(savePeaksObject(updatedPeaksObject));

      // Function to compute concentration from area using new calibration curve
      const computeConcentrationFromArea = (area, coeffs) => {
        return coeffs.reduce((acc, coeff, index) => acc + coeff * Math.pow(area, coeffs.length - 1 - index), 0);
      };

      // Batch calculate concentrations for all files using new calibration curve
      const newCoefficients = newCalibrationEquation.coefficients;
      // console.log('New calibration coefficients:', newCoefficients);

      // Prepare batch concentration updates
      const stdConcentrations = {};
      const qcConcentrations = {};
      const unkConcentrations = {};

      // For standards: Calculate detected concentrations using the new calibration curve
      // IMPORTANT: These are DETECTED concentrations (calculated), different from TARGET concentrations (tc) which remain constant
      Object.entries(updatedStandardAreas).forEach(([fileName, area]) => {
        if (Number.isFinite(area)) {
          const detectedConcentration = computeConcentrationFromArea(area, newCoefficients);
          stdConcentrations[fileName] = detectedConcentration;
        }
      });

      // Get FRESH QC and Unknown areas - use passed parameters if available, otherwise current state
      const currentQcAreasFromState = currentQcAreas || qcAreasState?.[peakGroupIndex];
      const currentUnkAreasFromState = currentUnkAreas || unkAreasState?.[peakGroupIndex];

      // Recalculate concentrations for QC files using new calibration curve
      if (currentQcAreasFromState && typeof currentQcAreasFromState === 'object') {
        // console.log('Updating QC concentrations with new calibration curve');
        Object.entries(currentQcAreasFromState).forEach(([fileName, area]) => {
          if (Number.isFinite(area)) {
            const concentration = computeConcentrationFromArea(area, newCoefficients);
            // console.log(`QC ${fileName}: area=${area} -> concentration=${concentration}`);
            qcConcentrations[fileName] = concentration;
          }
        });
      }

      // Recalculate concentrations for Unknown files using new calibration curve
      if (currentUnkAreasFromState && typeof currentUnkAreasFromState === 'object') {
        // console.log('Updating Unknown concentrations with new calibration curve');
        Object.entries(currentUnkAreasFromState).forEach(([fileName, area]) => {
          if (Number.isFinite(area)) {
            const concentration = computeConcentrationFromArea(area, newCoefficients);
            // console.log(`Unknown ${fileName}: area=${area} -> concentration=${concentration}`);
            unkConcentrations[fileName] = concentration;
          }
        });
      }

      // Batch update all concentrations in a single Redux dispatch
      if (Object.keys(stdConcentrations).length > 0 || Object.keys(qcConcentrations).length > 0 || Object.keys(unkConcentrations).length > 0) {
        dispatch(batchUpdatePeakConcentrations({
          peakIndex: peakGroupIndex,
          stdConcentrations: Object.keys(stdConcentrations).length > 0 ? stdConcentrations : undefined,
          qcConcentrations: Object.keys(qcConcentrations).length > 0 ? qcConcentrations : undefined,
          unkConcentrations: Object.keys(unkConcentrations).length > 0 ? unkConcentrations : undefined
        }));
      }

      // console.log('Calibration curve updated and all concentrations recalculated for peak group:', peakGroupIndex);

    } catch (error) {
      console.error('Error recalculating calibration curve and concentrations:', error);
    }
  };

  // Interpolation helper function for Y values
  const interpY = (xv, xs, ys) => {
    const n = xs.length;
    if (!n) return null;
    if (xv <= xs[0]) return ys[0];
    if (xv >= xs[n - 1]) return ys[n - 1];
    let lo = 0, hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (xs[mid] === xv) return ys[mid];
      if (xs[mid] < xv) lo = mid; else hi = mid;
    }
    const x0 = xs[lo], y0 = ys[lo];
    const x1 = xs[hi], y1 = ys[hi];
    const t = x1 === x0 ? 0 : (xv - x0) / (x1 - x0);
    return y0 + t * (y1 - y0);
  };

  // Submit baseline update for the active range, updating ONLY the selected peak locally (no backend call)
  const submitBaselineUpdate = async (rangeKey, selSnapshot, guard = {}) => {
    const startTime = performance.now();
    const { expectedFileName, instanceId } = guard || {};
    // console.log('ChartPreview: [OPTIMIZED] submitBaselineUpdate called for', expectedFileName, 'range:', rangeKey, 'peak:', selSnapshot?.peakKey);

    // Abort if file switched or chart instance changed
    if (expectedFileName && expectedFileName !== fileName) {
      // console.log('ChartPreview: Aborting submit - file mismatch:', expectedFileName, '!=', fileName);
      return;
    }
    if (typeof instanceId === 'number' && instanceId !== (chartInstanceRef?.current ?? instanceId)) {
      // console.log('ChartPreview: Aborting submit - instance mismatch');
      return;
    }
    if (!currentFile || !rangeKey) {
      // console.log('ChartPreview: Aborting submit - missing currentFile or rangeKey');
      return;
    }
    const endpoints = baselineByRangeRef.current.get(rangeKey);
    if (!endpoints) return;
    const sel = selSnapshot || selectedPeakRef.current;

    // Handle both old and new file structure
    const fileData = currentFile?.fileData || currentFile;
    const r = fileData?.result?.[rangeKey];
    if (!r?.to_analyze) return;

    // Always require explicit peak selection for baseline editing, or auto-select first peak
    let effectiveSel = sel;
    if (!sel || sel.rangeKey !== rangeKey) {
      const peakKeys = Object.keys(r.peaks || {});
      if (peakKeys.length > 0) {
        effectiveSel = { rangeKey, peakKey: peakKeys[0] };
      } else {
        return; // No peaks found
      }
    }

    const { p1, p2 } = endpoints;
    const xs = r.x || [];
    const yb = r.y_baseline || [];
    const peakKey = effectiveSel.peakKey;
    if (!Array.isArray(xs) || !Array.isArray(yb) || xs.length !== yb.length) return;

    // Prepare a file clone for compute based on the latest file from the store
    const latestMapStd = stdFilesRef.current || {};
    const latestMapQc = qcFilesRef.current || {};
    const latestMapUnk = unkFilesRef.current || {};
    const latestFile = latestMapStd[fileName] || latestMapQc[fileName] || latestMapUnk[fileName] || currentFileRef.current || currentFile;

    // console.log('ChartPreview: Using file for computation:', expectedFileName, 'fileType:', currentFileType);
    // console.log('ChartPreview: File sources - fromStdRef:', !!latestMapStd[fileName], 'fromQcRef:', !!latestMapQc[fileName], 'fromUnkRef:', !!latestMapUnk[fileName], 'fromCurrentRef:', !!currentFileRef.current);

    const fileClone = JSON.parse(JSON.stringify(latestFile));
    // REMOVED: Range-level baseline modification to prevent range-level area calculations
    // We now only work with peak-level data (x_comp, y_comp, y_baseline) to ensure consistency
    // const applyEndpointsToRange = (rk, ep) => { ... };
    // applyEndpointsToRange(rangeKey, { p1, p2 });
    // console.log('🚫 Skipping range-level baseline modification - using only peak-level data for consistency');

    // REMOVED: Backend API call to prevent range-level area calculations that override peak-level data
    // All area calculations are now done locally using peak-level arrays (x_comp, y_comp, y_baseline)
    let area = null;
    // console.log('🚫 Skipping backend peakProcessingAPI to ensure consistent peak-level area calculations');

    // Note: Local area calculation will happen after data update using the corrected arrays

    // 1) Persist baseline changes using unified peak-level approach for all peaks:
    //    - Always update the selected peak's x_comp, y_comp, and y_baseline arrays
    //    - Keep range-level x/y_baseline unchanged for visual consistency
    try {
      const rebasedLatest = (latestMapStd[fileName] || latestMapQc[fileName] || latestMapUnk[fileName] || currentFileRef.current || currentFile);
      // console.log('🏗️ CREATING fileForStore from:', {
      //   fileName,
      //   hasLatestStd: !!latestMapStd[fileName],
      //   hasLatestQc: !!latestMapQc[fileName], 
      //   hasLatestUnk: !!latestMapUnk[fileName],
      //   hasCurrentRef: !!currentFileRef.current,
      //   hasCurrentFile: !!currentFile,
      //   usingSource: rebasedLatest === latestMapStd[fileName] ? 'latestStd' :
      //                rebasedLatest === latestMapQc[fileName] ? 'latestQc' :
      //                rebasedLatest === latestMapUnk[fileName] ? 'latestUnk' :
      //                rebasedLatest === currentFileRef.current ? 'currentRef' : 'currentFile'
      // });
      const fileForStore = JSON.parse(JSON.stringify(rebasedLatest));

      // Ensure the file object has the correct name property for Redux matching
      if (!fileForStore.name && fileName) {
        // console.log('� FIXING missing name property in fileForStore');
        fileForStore.name = fileName;
      }

      // console.log('�📁 fileForStore created, fileName in object:', fileForStore?.name);
      // console.log('📁 fileName parameter:', fileName);
      // console.log('📁 Names match:', fileForStore?.name === fileName);
      const rr = fileForStore?.result?.[rangeKey];
      // console.log('Peak update logic - peakCount:', Object.keys(rr?.peaks || {}).length);

      // Always update peak-level data for the selected peak
      // console.log('Peak update check - rr exists:', !!rr, 'effectiveSel:', effectiveSel, 'rangeKey match:', effectiveSel?.rangeKey === rangeKey);
      // console.log('Peak exists check:', !!rr?.peaks?.[effectiveSel?.peakKey], 'Available peaks:', Object.keys(rr?.peaks || {}));

      if (rr && effectiveSel && effectiveSel.rangeKey === rangeKey && rr?.peaks?.[effectiveSel.peakKey]) {
        // Ensure unified peak-level data structure for all peaks
        const currentPeak = rr.peaks[effectiveSel.peakKey];

        // Initialize x_comp from x if it doesn't exist
        if (!Array.isArray(currentPeak.x_comp) && Array.isArray(currentPeak.x)) {
          currentPeak.x_comp = currentPeak.x.slice();
        }
        // Initialize y_comp from y if it doesn't exist 
        if (!Array.isArray(currentPeak.y_comp) && Array.isArray(currentPeak.y)) {
          currentPeak.y_comp = currentPeak.y.slice();
        }

        // Create originalYComp reference if it doesn't exist (preserve original component data)
        if (!Array.isArray(currentPeak.originalYComp) && Array.isArray(currentPeak.y_comp)) {
          // console.log('Creating originalYComp reference for peak', effectiveSel.peakKey);
          currentPeak.originalYComp = currentPeak.y_comp.slice();
          // Also store the corresponding original x_comp array for proper synchronization
          currentPeak.originalXComp = currentPeak.x_comp ? currentPeak.x_comp.slice() :
            (currentPeak.x ? currentPeak.x.slice() : []);
          // console.log('Created originalYComp with length:', currentPeak.originalYComp.length, 
          // 'and originalXComp with length:', currentPeak.originalXComp.length);
        }

        const fullX = rr.x.slice();
        const m1 = Math.min(p1.x, p2.x);
        const m2 = Math.max(p1.x, p2.x);

        // console.log('BEFORE peak update - peakKey:', effectiveSel.peakKey);
        // console.log('BEFORE peak data:', {
        //   x_length: currentPeak.x?.length,
        //   x_comp_length: currentPeak.x_comp?.length,  // Check if it uses x_comp instead
        //   y_baseline_length: currentPeak.y_baseline?.length,
        //   y_comp_length: currentPeak.y_comp?.length,
        //   first_x: currentPeak.x?.[0] || currentPeak.x_comp?.[0],
        //   last_x: currentPeak.x?.[currentPeak.x?.length - 1] || currentPeak.x_comp?.[currentPeak.x_comp?.length - 1],
        //   all_keys: Object.keys(currentPeak)
        // });

        // Get chromatogram data within the extended range
        const chromatogramData = Array.isArray(props.data) ? props.data : [];
        const chromatogramInRange = chromatogramData.filter(d => d.x >= m1 && d.x <= m2);

        // Build x array based on original chromatogram points within baseline range
        // x_comp should always match x_original points
        const originalChromatogram = Array.isArray(props.data) ? props.data : [];
        const originalXInRange = originalChromatogram
          .filter(d => d.x >= m1 && d.x <= m2)
          .map(d => d.x);

        // Use only original chromatogram points (no artificial endpoints)
        // If baseline extends beyond original data, we'll interpolate y values but keep x from original
        const newX = [...originalXInRange].sort((a, b) => a - b);
        const newBase = newX.map((xx) => yOnLine(xx, p1, p2));

        // Get the peak data to access all Y arrays
        const peakData = rr.peaks[effectiveSel.peakKey];

        // Create originalYComp reference if it doesn't exist
        if (!peakData.originalYComp && peakData.y_comp) {
          // console.log('Creating originalYComp reference for peak', effectiveSel.peakKey);
          peakData.originalYComp = [...peakData.y_comp];
        }

        // Always use x_comp for consistency (initialize from x if needed)
        const currentXArray = peakData.x_comp || peakData.x;
        const xArrayName = 'x_comp';

        // Initialize peak's x array from range if it doesn't exist
        if (!Array.isArray(currentXArray) && Array.isArray(fullX)) {
          // console.log(`Peak ${xArrayName} array missing, initializing from range x array`);
          peakData[xArrayName] = fullX.slice();
        }

        // Update the appropriate x array and y_baseline
        // console.log('Updating peak data - before:', currentXArray?.length, peakData.y_baseline?.length);
        // console.log('Using X array type:', xArrayName);
        rr.peaks[effectiveSel.peakKey][xArrayName] = newX;
        rr.peaks[effectiveSel.peakKey].y_baseline = newBase;
        // console.log('Updating peak data - after:', newX.length, newBase.length);

        // console.log('AFTER peak update - peakKey:', effectiveSel.peakKey);
        const updatedPeak = rr.peaks[effectiveSel.peakKey];
        // console.log('AFTER peak data:', {
        //   x_length: updatedPeak.x?.length,
        //   x_comp_length: updatedPeak.x_comp?.length,
        //   y_baseline_length: updatedPeak.y_baseline?.length,
        //   y_comp_length: updatedPeak.y_comp?.length,
        //   y_length: updatedPeak.y?.length,
        //   first_x: updatedPeak.x?.[0] || updatedPeak.x_comp?.[0],
        //   last_x: updatedPeak.x?.[updatedPeak.x?.length - 1] || updatedPeak.x_comp?.[updatedPeak.x_comp?.length - 1],
        //   updated_array_type: xArrayName
        // });

        // Ensure y_comp exists for all peaks (initialize from y if needed)
        if (!Array.isArray(peakData.y_comp) && Array.isArray(peakData.y)) {
          // console.log('Initializing y_comp from y array for unified peak handling');
          rr.peaks[effectiveSel.peakKey].y_comp = peakData.y.slice();
        }
        // console.log('Range-level data unchanged - x_length:', rr.x?.length, 'y_baseline_length:', rr.y_baseline?.length);

        // Always synchronize y_comp array for all peaks - ensure it matches new x array
        // Use originalYComp as the base reference for all operations
        const originalYComp = Array.isArray(peakData.originalYComp) && peakData.originalYComp.length > 0 ? peakData.originalYComp :
          Array.isArray(peakData.y_comp) && peakData.y_comp.length > 0 ? peakData.y_comp :
            Array.isArray(peakData.y) && peakData.y.length > 0 ? peakData.y : [];
        // Use the stored originalXComp that corresponds to originalYComp
        const originalXComp = Array.isArray(peakData.originalXComp) && peakData.originalXComp.length > 0 ? peakData.originalXComp :
          Array.isArray(peakData.x_comp) && peakData.x_comp.length > 0 ? peakData.x_comp :
            Array.isArray(peakData.x) && peakData.x.length > 0 ? peakData.x : [];

        if (originalYComp.length === 0 || originalXComp.length === 0) {
          console.error('❌ Cannot synchronize y_comp - no valid original data found for peak', effectiveSel.peakKey);
          console.error('Peak data:', {
            hasOriginalYComp: Array.isArray(peakData.originalYComp),
            hasXComp: Array.isArray(peakData.x_comp),
            hasX: Array.isArray(peakData.x),
            hasYComp: Array.isArray(peakData.y_comp),
            hasY: Array.isArray(peakData.y),
            originalYCompLength: peakData.originalYComp?.length,
            xCompLength: peakData.x_comp?.length,
            xLength: peakData.x?.length,
            yCompLength: peakData.y_comp?.length,
            yLength: peakData.y?.length
          });
          return; // Skip synchronization if no valid data
        }
        // console.log('Synchronizing y_comp using originalYComp as base - original length:', originalYComp.length, 'new length:', newX.length);
        // console.log('Using original X array for reference - x_comp length:', originalXComp.length);

        rr.peaks[effectiveSel.peakKey].y_comp = newX.map(xx => {
          // Use originalYComp as base - find exact x match with tolerance for floating point precision
          const originalXIndex = originalXComp.findIndex(origX => Math.abs(origX - xx) < 1e-10);

          if (originalXIndex >= 0 && originalXIndex < originalYComp.length) {
            // X point exists in originalYComp - preserve the original component value
            // console.log(`Preserving originalYComp[${originalXIndex}] = ${originalYComp[originalXIndex]} for x=${xx}`);
            return originalYComp[originalXIndex];
          }

          // X point is beyond original component range - extend using original chromatogram data
          const originalY = fileForStore?.original_y;
          const originalXArray = fileForStore?.original_x;

          if (Array.isArray(originalY) && Array.isArray(originalXArray)) {
            const chromatogramIndex = originalXArray.findIndex(origX => Math.abs(origX - xx) < 1e-10);

            if (chromatogramIndex >= 0 && chromatogramIndex < originalY.length) {
              // console.log(`Extending with original chromatogram y=${originalY[chromatogramIndex]} for x=${xx}`);
              return originalY[chromatogramIndex];
            }

            // Fallback: interpolate from original chromatogram data
            const interpolatedY = interpY(xx, originalXArray, originalY);
            // console.log(`Extending with interpolated original chromatogram y=${interpolatedY} for x=${xx}`);
            return interpolatedY;
          }

          // Final fallback: use props.data if no original chromatogram available
          const allChromatogramData = Array.isArray(props.data) ? props.data : [];
          const chromatogramPoint = allChromatogramData.find(d => Math.abs(d.x - xx) < 1e-10);

          if (chromatogramPoint) {
            // console.log(`Fallback: Using props chromatogram y=${chromatogramPoint.y} for extended x=${xx}`);
            return chromatogramPoint.y;
          }

          const interpolatedY = interpY(xx, allChromatogramData.map(d => d.x), allChromatogramData.map(d => d.y));
          // console.log(`Fallback: Using interpolated props chromatogram y=${interpolatedY} for extended x=${xx}`);
          return interpolatedY;
        });
        // console.log('y_comp synchronized to length:', rr.peaks[effectiveSel.peakKey].y_comp.length);
      }


      // 2) Recalculate area using the updated peak-level data (unified logic for all peaks)
      if (area == null) {
        const updatedRange = fileForStore?.result?.[rangeKey];

        // console.log('Recalculating area for', fileName, 'range:', rangeKey, 'peak:', peakKey);

        if (updatedRange?.peaks?.[peakKey]) {
          // Unified logic: ALL peaks use peak-level arrays (x_comp, y_comp, y_baseline)
          const updatedPeak = updatedRange.peaks[peakKey];

          // Ensure x_comp exists (initialize from x if needed)
          if (!Array.isArray(updatedPeak.x_comp) && Array.isArray(updatedPeak.x)) {
            updatedPeak.x_comp = updatedPeak.x.slice();
          }
          // Ensure y_comp exists (initialize from y if needed)
          if (!Array.isArray(updatedPeak.y_comp) && Array.isArray(updatedPeak.y)) {
            updatedPeak.y_comp = updatedPeak.y.slice();
          }

          const updatedXs = updatedPeak.x_comp || [];
          const updatedYComp = updatedPeak.y_comp || [];
          const updatedBaseline = updatedPeak.y_baseline || [];

          // console.log('Peak area calculation (unified - always peak-level):');
          // console.log('- Using X array: x_comp (length:', updatedXs.length, ')');
          // console.log('- Using Y array: y_comp (length:', updatedYComp.length, ')');
          // console.log('- Array lengths - X:', updatedXs.length, 'Y:', updatedYComp.length, 'Baseline:', updatedBaseline.length);
          // console.log('- X range:', updatedXs.length > 0 ? [updatedXs[0], updatedXs[updatedXs.length-1]] : 'empty');
          // console.log('- Y range:', updatedYComp.length > 0 ? [updatedYComp[0], updatedYComp[updatedYComp.length-1]] : 'empty');
          // console.log('- Baseline range:', updatedBaseline.length > 0 ? [updatedBaseline[0], updatedBaseline[updatedBaseline.length-1]] : 'empty');

          if (updatedXs.length === updatedYComp.length && updatedXs.length === updatedBaseline.length && updatedXs.length > 1) {
            let a = 0;
            // console.log('🔢 Starting area calculation with', updatedXs.length, 'points');

            for (let i = 1; i < updatedXs.length; i++) {
              const x0 = updatedXs[i - 1];
              const x1 = updatedXs[i];
              const yComp0 = updatedYComp[i - 1] ?? 0;
              const yComp1 = updatedYComp[i] ?? 0;
              const baseline0 = updatedBaseline[i - 1] ?? 0;
              const baseline1 = updatedBaseline[i] ?? 0;
              const y0 = Math.max(0, yComp0 - baseline0);
              const y1 = Math.max(0, yComp1 - baseline1);
              const dx = x1 - x0;

              if (Number.isFinite(dx) && dx > 0) {
                const segmentArea = 0.5 * (y0 + y1) * dx;
                a += segmentArea;

                if (i <= 3) { // Log first few calculations for debugging
                  // console.log(`Segment ${i}: x=[${x0.toFixed(4)}, ${x1.toFixed(4)}] dx=${dx.toFixed(4)} y=[${yComp0.toFixed(4)}, ${yComp1.toFixed(4)}] baseline=[${baseline0.toFixed(4)}, ${baseline1.toFixed(4)}] net_y=[${y0.toFixed(4)}, ${y1.toFixed(4)}] segment_area=${segmentArea.toFixed(6)}`);
                }
              }
            }
            area = a;
            // console.log('✅ Calculated area:', area);
          } else {
            console.error('❌ Array length mismatch or insufficient data - X:', updatedXs.length, 'Y:', updatedYComp.length, 'Baseline:', updatedBaseline.length);
          }
        } else {
          console.error('❌ Peak not found:', peakKey, 'in range:', rangeKey);
        }
      }

      // Note: area_under_peak is stored in results/areaUnderPeaksX structure, NOT at peak level

      // Recheck before dispatching writes to Redux
      if (expectedFileName && expectedFileName !== fileName) {
        // console.log('❌ DISPATCH BLOCKED: expectedFileName mismatch:', expectedFileName, '!==', fileName);
        return;
      }
      if (typeof instanceId === 'number' && instanceId !== (chartInstanceRef?.current ?? instanceId)) {
        // console.log('❌ DISPATCH BLOCKED: instanceId mismatch:', instanceId, '!==', chartInstanceRef?.current);
        return;
      }
      // console.log('✅ DISPATCH PROCEEDING for Redux, type:', currentFileType, 'file:', fileName);
      const finalPeakData = fileForStore?.result?.[rangeKey]?.peaks?.[effectiveSel?.peakKey];
      // console.log('🔍 VERIFYING fileForStore before dispatch:');
      // console.log('fileForStore exists:', !!fileForStore);
      // console.log('result exists:', !!fileForStore?.result);
      // console.log('range exists:', !!fileForStore?.result?.[rangeKey]);
      // console.log('peaks exists:', !!fileForStore?.result?.[rangeKey]?.peaks);
      // console.log('target peak exists:', !!finalPeakData);

      // console.log('🎯 FINAL peak data being dispatched:', {
      //   peakKey: effectiveSel?.peakKey,
      //   x_length: finalPeakData?.x?.length,
      //   x_comp_length: finalPeakData?.x_comp?.length,
      //   y_baseline_length: finalPeakData?.y_baseline?.length,
      //   y_comp_length: finalPeakData?.y_comp?.length,
      //   y_length: finalPeakData?.y?.length,
      //   x_first_few: finalPeakData?.x?.slice(0, 3) || finalPeakData?.x_comp?.slice(0, 3),
      //   y_baseline_first_few: finalPeakData?.y_baseline?.slice(0, 3),
      //   primary_x_array: finalPeakData?.x_comp ? 'x_comp' : (finalPeakData?.x ? 'x' : 'none'),
      //   all_arrays_match: (finalPeakData?.x?.length || finalPeakData?.x_comp?.length) === finalPeakData?.y_baseline?.length && 
      //                    (finalPeakData?.x?.length || finalPeakData?.x_comp?.length) === (finalPeakData?.y_comp?.length || finalPeakData?.y?.length)
      // });
      // console.log('Range-level data in dispatch (should be unchanged):', {
      //   range_x_length: fileForStore?.result?.[rangeKey]?.x?.length,
      //   range_y_baseline_length: fileForStore?.result?.[rangeKey]?.y_baseline?.length
      // });

      // console.log('🚀 DISPATCHING to Redux...');

      // Create the correct payload structure expected by Redux reducers
      const reduxPayload = {
        fileName: fileForStore.name,
        fileData: fileForStore
      };

      // console.log('📦 Redux payload structure:', {
      //   hasFileName: !!reduxPayload.fileName,
      //   hasFileData: !!reduxPayload.fileData,
      //   fileName: reduxPayload.fileName,
      //   fileDataHasResult: !!reduxPayload.fileData?.result,
      //   targetRangeExists: !!reduxPayload.fileData?.result?.[rangeKey],
      //   targetPeakExists: !!reduxPayload.fileData?.result?.[rangeKey]?.peaks?.[effectiveSel?.peakKey]
      // });

      if (currentFileType === 'standard') {
        // console.log('Dispatching to saveStdFilesData');
        dispatch(saveStdFilesData([reduxPayload]));
      } else if (currentFileType === 'qualityControl') {
        // console.log('Dispatching to saveQcFilesData');
        dispatch(saveQcFilesData([reduxPayload]));
      } else if (currentFileType === 'unknown') {
        // console.log('Dispatching to saveUnkFilesData with correct structure');
        dispatch(saveUnkFilesData([reduxPayload]));
      }
      // console.log('✅ DISPATCH COMPLETED');

      // Verify Redux state immediately after dispatch
      setTimeout(() => {
        // console.log('🔍 VERIFYING Redux state after dispatch...');
        const currentReduxFile = currentFileType === 'standard' ? latestMapStd[fileName] :
          currentFileType === 'qualityControl' ? latestMapQc[fileName] :
            currentFileType === 'unknown' ? latestMapUnk[fileName] : null;

        if (currentReduxFile) {
          const reduxPeakData = currentReduxFile?.result?.[rangeKey]?.peaks?.[effectiveSel?.peakKey];
          // console.log('📊 Redux peak data after dispatch:', {
          //   peakKey: effectiveSel?.peakKey,
          //   x_length: reduxPeakData?.x?.length,
          //   x_comp_length: reduxPeakData?.x_comp?.length,
          //   y_baseline_length: reduxPeakData?.y_baseline?.length,
          //   y_comp_length: reduxPeakData?.y_comp?.length,
          //   x_first_few: reduxPeakData?.x?.slice(0, 3) || reduxPeakData?.x_comp?.slice(0, 3),
          //   y_baseline_first_few: reduxPeakData?.y_baseline?.slice(0, 3),
          //   primary_x_array: reduxPeakData?.x_comp ? 'x_comp' : (reduxPeakData?.x ? 'x' : 'none')
          // });

          // Check both x and x_comp arrays for matches
          const xMatches = JSON.stringify(reduxPeakData?.x) === JSON.stringify(finalPeakData?.x);
          const xCompMatches = JSON.stringify(reduxPeakData?.x_comp) === JSON.stringify(finalPeakData?.x_comp);
          const baselineMatches = JSON.stringify(reduxPeakData?.y_baseline) === JSON.stringify(finalPeakData?.y_baseline);
          const compMatches = JSON.stringify(reduxPeakData?.y_comp) === JSON.stringify(finalPeakData?.y_comp);

          // console.log('🎯 Redux data verification:', {
          //   x_arrays_match: xMatches,
          //   x_comp_arrays_match: xCompMatches,
          //   y_baseline_arrays_match: baselineMatches,
          //   y_comp_arrays_match: compMatches,
          //   primary_x_match: (finalPeakData?.x_comp ? xCompMatches : xMatches),
          //   all_match: (xMatches || xCompMatches) && baselineMatches && compMatches
          // });

          // NOTE: Verification disabled - JSON.stringify comparison is too strict for floating-point arrays
          // and can produce false positives due to precision differences or array reconstruction.
          // The Redux state is correctly updated; this check was overly sensitive.
          // const primaryXMatch = finalPeakData?.x_comp ? xCompMatches : xMatches;
          // if (primaryXMatch && baselineMatches && compMatches) {
          //   // console.log('🎉 SUCCESS! Redux state properly updated with peak-level data!');
          // } else {
          //   // console.error('❌ Redux state update FAILED - data mismatch');
          // }
        } else {
          console.error('❌ Could not find file in Redux state after dispatch!');
        }
      }, 100);

    } catch (error) {
      console.error('Error in baseline update:', error);
    }

    // Ensure we have a valid area before updating areaUnderPeaksX structure
    if (area == null || !Number.isFinite(area)) {
      console.warn('⚠️ Area calculation resulted in invalid value:', area, 'for peak:', peakKey, 'file:', fileName);
      area = 0; // Fallback to 0 instead of null
    }

    // Ensure we have a valid area before updating areaUnderPeaksX structure
    if (area == null || !Number.isFinite(area)) {
      console.warn('⚠️ Area calculation resulted in invalid value:', area, 'for peak:', peakKey, 'file:', fileName);
      area = 0; // Fallback to 0 instead of null
    }

    // console.log('📊 Final calculated area for', fileName, 'peak', peakKey, ':', area);

    // Update only the relevant area entry for this peak's reporting group and this file
    const findGroupKeyForPeak = (peakVal) => {
      if (peakVal == null) return null;
      const pv = Number(peakVal);
      const entries = Object.entries(allReportingPeaks || {});
      for (const [rpKey, list] of entries) {
        const arr = Array.isArray(list) ? list : [];
        if (arr.some((nom) => Math.abs(Number(nom) - pv) <= 1e-3)) return rpKey;
      }
      return null;
    };
    const groupKey = findGroupKeyForPeak(Number(peakKey));
    const groupKeys = Object.keys(allReportingPeaks || {});
    const gIdx = groupKey ? groupKeys.indexOf(groupKey) : -1;
    if (gIdx === -1) return;

    // Guard again before updating areas/concentrations
    if (expectedFileName && expectedFileName !== fileName) return;
    if (typeof instanceId === 'number' && instanceId !== (chartInstanceRef?.current ?? instanceId)) return;
    if (currentFileType === 'standard') {
      // IMPORTANT: Always start with current stdAreasState to preserve all existing areas
      // stdAreasState is an object where keys are peak group indices
      const currentStdAreas = stdAreasState?.[gIdx] || {};
      const updatedStdAreas = { ...currentStdAreas, [fileName]: area };

      // Update the full areas structure for this peak group
      const next = { ...(stdAreasState || {}) };
      next[gIdx] = updatedStdAreas;

      dispatch(saveAreaUnderPeaks(next));

      // Recalculate calibration curve and concentrations for all standard files when any standard area changes
      try {
        // Pass current QC and Unknown areas to avoid stale closure issues
        const currentQcAreas = qcAreasState?.[gIdx];
        const currentUnkAreas = unkAreasState?.[gIdx];

        await recalculateStandardConcentrations(gIdx, next[gIdx], currentQcAreas, currentUnkAreas);
      } catch (error) {
        console.error('Error in standard concentration recalculation:', error);
      }
    } else if (currentFileType === 'qualityControl') {
      const next = (qcAreasState || []).map((m) => ({ ...(m || {}) }));
      while (next.length < groupKeys.length) next.push({});
      next[gIdx] = { ...(next[gIdx] || {}), [fileName]: area };
      dispatch(saveAreaUnderPeaksQc(next));

      // Batch update concentration for this specific QC file
      const concentration = (() => {
        try {
          const currentPeaksObject = Array.isArray(peaksObject) ? peaksObject : Object.values(peaksObject || {});
          const actualPeakIndex = resolvePeakIndex(peakKey);
          const peakData = currentPeaksObject[actualPeakIndex];
          if (!peakData?.equation?.coefficients) return null;
          const coefficients = peakData.equation.coefficients;
          return Number.isFinite(area) ?
            coefficients.reduce((acc, coeff, index) => acc + coeff * Math.pow(area, coefficients.length - 1 - index), 0) :
            null;
        } catch { return null; }
      })();

      if (concentration !== null) {
        dispatch(batchUpdatePeakConcentrations({
          peakIndex: gIdx,
          qcConcentrations: { [fileName]: concentration }
        }));
        // console.log(`QC concentration updated for ${fileName}: area=${area} -> concentration=${concentration}`);
      }
    } else if (currentFileType === 'unknown') {
      const next = (unkAreasState || []).map((m) => ({ ...(m || {}) }));
      while (next.length < groupKeys.length) next.push({});
      next[gIdx] = { ...(next[gIdx] || {}), [fileName]: area };
      dispatch(saveAreaUnderPeaksUnk(next));

      // Batch update concentration for this specific Unknown file  
      const concentration = (() => {
        try {
          const currentPeaksObject = Array.isArray(peaksObject) ? peaksObject : Object.values(peaksObject || {});
          const actualPeakIndex = resolvePeakIndex(peakKey);
          const peakData = currentPeaksObject[actualPeakIndex];
          if (!peakData?.equation?.coefficients) return null;
          const coefficients = peakData.equation.coefficients;
          return Number.isFinite(area) ?
            coefficients.reduce((acc, coeff, index) => acc + coeff * Math.pow(area, coefficients.length - 1 - index), 0) :
            null;
        } catch { return null; }
      })();

      if (concentration !== null) {
        dispatch(batchUpdatePeakConcentrations({
          peakIndex: gIdx,
          unkConcentrations: { [fileName]: concentration }
        }));
        // console.log(`Unknown concentration updated for ${fileName}: area=${area} -> concentration=${concentration}`);
      }
    }

    // NOTE: All concentration updates are now handled via batch operations above.
    // Standards trigger full recalibration with batch updates for all file types.
    // QC/Unknown files use optimized single-file batch updates to avoid individual dispatches.

    // Performance logging for optimization tracking
    const endTime = performance.now();
    // console.log(`ChartPreview: [PERFORMANCE] Baseline update completed in ${(endTime - startTime).toFixed(2)}ms for ${currentFileType} file: ${fileName}`);

    // Dispatch completion event for the processed peak
    const idxInPeaksObject = resolvePeakIndex(peakKey);
    if (idxInPeaksObject >= 0) {
      try {
        window.dispatchEvent(new CustomEvent('concentration-update-complete', {
          detail: { peakIndex: idxInPeaksObject, peakKey, rangeKey, fileName }
        }));
      } catch { }
    }
  };

  const scheduleBaselineSubmit = (rangeKey, selSnapshotOverride = null) => {
    const map = submitDebounceMapRef.current;
    const existing = map.get(rangeKey);
    if (existing) clearTimeout(existing);
    const fileGuard = fileName; // capture current file to avoid cross-file updates later
    const instanceGuard = chartInstanceRef.current;
    // Capture current selection snapshot so we can safely clear UI selection immediately
    const selSnapshot = selSnapshotOverride
      ? { ...selSnapshotOverride }
      : (selectedPeakRef.current && selectedPeakRef.current.rangeKey === rangeKey
        ? { ...selectedPeakRef.current }
        : null);

    // console.log('ChartPreview: Scheduling baseline submit for', fileGuard, 'range:', rangeKey, 'peak:', selSnapshot?.peakKey);

    const t = setTimeout(() => {
      // If user switched files, skip this delayed submit to avoid cross-file leakage
      if (fileGuard !== fileName) {
        // console.log('ChartPreview: Skipping delayed submit - file changed from', fileGuard, 'to', fileName);
        map.delete(rangeKey);
        return;
      }
      // console.log('ChartPreview: Executing delayed submit for', fileGuard, 'range:', rangeKey);
      submitBaselineUpdate(rangeKey, selSnapshot, { expectedFileName: fileGuard, instanceId: instanceGuard });
      map.delete(rangeKey);
    }, SUBMIT_DEBOUNCE_MS);
    map.set(rangeKey, t);
  };

  // No external rebuilds on edit toggle; chart rebuilds only when file changes.

  // Helper: capture a visual snapshot of all baselines for the active file
  const captureSnapshotForActiveFile = () => {
    try {
      // If baselines aren't ready yet, skip capturing for now
      const baselineCount = (baselineByRangeRef.current && baselineByRangeRef.current.size) || 0;
      if (baselineCount === 0) return;

      // If an existing snapshot is present but empty or smaller, allow updating; otherwise keep it
      const existing = snapshotsByFileRef.current.get(fileName);
      const existingSize = existing instanceof Map ? existing.size : 0;
      if (existing && existingSize >= baselineCount && existingSize > 0) return;

      const snap = new Map();
      baselineByRangeRef.current.forEach((v, rKey) => {
        const entry = {};
        // Control line (single-peak ranges)
        if (v?.p1 && v?.p2) entry.control = { p1: { ...v.p1 }, p2: { ...v.p2 } };
        // Per-comp lines
        if (v?.linesMap?.size) {
          const m = new Map();
          v.linesMap.forEach((ln, pk) => {
            try {
              const di1 = ln?.dataItems?.[0];
              const di2 = ln?.dataItems?.[1];
              if (di1 && di2) m.set(pk, { p1: { x: di1.get('valueX'), y: di1.get('valueY') }, p2: { x: di2.get('valueX'), y: di2.get('valueY') } });
            } catch { }
          });
          entry.perComp = m;
        }
        snap.set(rKey, entry);
      });
      snapshotsByFileRef.current.set(fileName, snap);
    } catch { }
  };

  // Chart lifecycle
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    // Show loading animation immediately
    setIsChartLoading(true);

    // Dispose any existing root for this container id
    try {
      const roots = (am5.registry && am5.registry.rootElements) ? am5.registry.rootElements : [];
      const old = roots?.find?.((r) => r.dom && r.dom.id === chartId);
      if (old) old.dispose();
    } catch { }

    // If switching files while editing, finalize the previous file's edits so discard doesn't affect it anymore
    if (isEditingResults) {
      // console.log('ChartPreview: Finalizing previous file edits during file switch');
      // console.log('ChartPreview: Previous file was:', lastFileRef.current);
      // console.log('ChartPreview: Current baseline state before switch:', baselineByRangeRef.current.size, 'ranges');

      // CRITICAL: Wait for any pending baseline submissions to complete before switching
      // This prevents state inconsistencies when switching files mid-operation
      const pendingSubmissions = submitDebounceMapRef.current.size;
      if (pendingSubmissions > 0) {
        // console.log('ChartPreview: Waiting for', pendingSubmissions, 'pending submissions to complete before file switch');
        // Clear timeouts and trigger immediate submission for pending changes
        const pendingPromises = [];
        submitDebounceMapRef.current.forEach((timeoutId, rangeKey) => {
          clearTimeout(timeoutId);
          // Trigger immediate submission for this range
          const selSnapshot = selectedPeakRef.current && selectedPeakRef.current.rangeKey === rangeKey
            ? { ...selectedPeakRef.current }
            : null;
          const promise = submitBaselineUpdate(rangeKey, selSnapshot, {
            expectedFileName: lastFileRef.current,
            instanceId: chartInstanceRef.current
          });
          pendingPromises.push(promise);
        });
        submitDebounceMapRef.current.clear();

        // Wait for all pending submissions to complete
        if (pendingPromises.length > 0) {
          Promise.all(pendingPromises).then(() => {
            // console.log('ChartPreview: All pending submissions completed before file switch');
          }).catch((error) => {
            console.error('ChartPreview: Error in pending submissions during file switch:', error);
          });
        }
      }

      dispatch(finalizeResultsEdit());
      // Clear the snapshot for the previous file
      try {
        // console.log('ChartPreview: Deleting snapshot for previous file:', lastFileRef.current);
        snapshotsByFileRef.current.delete(lastFileRef.current);
      } catch { }
    }
    // If still in edit mode from UI perspective, start editing the new file
    if (editingRef.current) {
      // console.log('ChartPreview: Starting edit mode for new file after switch');
      // console.log('ChartPreview: New file is:', fileName);
      try {
        dispatch(beginResultsEdit());
        // console.log('ChartPreview: beginResultsEdit dispatched for new file:', fileName);
      } catch (e) {
        console.error('ChartPreview: Error dispatching beginResultsEdit for new file:', e);
      }
      // Store snapshot for the new active file
      // console.log('ChartPreview: Capturing snapshot for new file:', fileName);
      captureSnapshotForActiveFile();
      clearChangesForFile(fileName); // Reset changes for new file
    }
    // Always reset change tracking and discard button state when switching files
    clearChangesForFile(fileName);

    // Fresh mapping for baseline colors per range on every render to avoid stale values
    baselineColorByRangeRef.current = new Map();

    // Also reset per-file, per-session state synchronously to avoid leaking previous file endpoints/selection
    try {
      lastEndpointsRef.current = new Map();
      lastFileRef.current = fileName;
      selectedPeakRef.current = null;
      activeRangeRef.current = null;
      // Clear restored peaks list when switching files, but NOT during chart rebuilds in Insert Peak mode
      // (chart rebuilds happen when peaks are restored/hidden during Insert Peak operations)
      if (!insertingPeakRef.current) {
        restoredPeaksRef.current = [];
      }
    } catch { }

    // Clear registries before creating a new chart for this file
    try {
      baselineByRangeRef.current = new Map();
      compFillsByRangeRef.current = new Map();
      submitDebounceMapRef.current.forEach((t) => clearTimeout(t));
      submitDebounceMapRef.current.clear();
    } catch { }
    chartInstanceRef.current = (chartInstanceRef.current || 0) + 1;
    const root = am5.Root.new(chartId);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    // Show loading indicator
    const loadingIndicator = root.container.children.push(
      am5.Container.new(root, {
        width: am5.p100,
        height: am5.p100,
        layer: 1000
      })
    );

    loadingIndicator.children.push(
      am5.Graphics.new(root, {
        width: am5.p100,
        height: am5.p100,
        fill: am5.color(0xffffff),
        fillOpacity: 0.8
      })
    );

    const loadingSpinner = loadingIndicator.children.push(
      am5.Graphics.new(root, {
        centerX: am5.p50,
        centerY: am5.p50,
        width: 40,
        height: 40
      })
    );

    // Create spinning animation
    loadingSpinner.animate({
      key: "rotation",
      to: 360,
      duration: 1000,
      loops: Infinity
    });

    // Draw the spinner (circle with border)
    loadingSpinner.setPrivate("graphics", am5.Graphics.new(root, {
      draw: (graphics) => {
        graphics.arc(0, 0, 18, 0, 360);
        graphics.arc(0, 0, 15, 0, 360);
        graphics.stroke = am5.color(0x3498db);
        graphics.strokeWidth = 3;
        graphics.fill = am5.color(0xf3f3f3);
      }
    }));

    // Add fade-in animation for initial chart appearance
    root.container.set("opacity", 0);
    root.container.animate({
      key: "opacity",
      to: 1,
      duration: 400,
      easing: am5.ease.out(am5.ease.cubic)
    });

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'zoomXY',
        pinchZoomX: false,
        paddingLeft: 0,
        layout: root.verticalLayout,
        // Enable chart-level animations
        interpolationDuration: 300,
        interpolationEasing: am5.ease.out(am5.ease.cubic),
        // Add maxZoomCount to prevent excessive auto-zooming
        maxZoomCount: 50
      })
    );
    // Helper to enable/disable panning
    const setPanEnabled = (enabled) => {
      try {
        chart.set('panX', !!enabled);
        chart.set('panY', !!enabled);
        chart.set('wheelX', enabled ? 'panX' : 'none');
        // Keep vertical zoom always enabled via wheelY=zoomXY at chart creation
        chart.set('pinchZoomX', !!enabled);
        // Don't change cursor in insert mode - keep crosshair
        if (!insertingPeakRef.current) {
          chart.plotContainer.set('cursorOverStyle', enabled ? 'grab' : 'default');
        }
      } catch (e) {
        console.error('ChartPreview: Error in setPanEnabled:', e);
      }
    };
    // Listen for global baseline edit mode toggle
    // Helper: capture a visual snapshot of all baselines for the active file
    const onEditToggle = (evt) => {
      const enabled = !!evt?.detail?.enabled;
      editingRef.current = enabled;
      // Capture initial snapshot for discard and reset history when entering edit mode
      if (enabled) {
        // console.log('ChartPreview: Entering edit mode, calling beginResultsEdit');
        try {
          dispatch(beginResultsEdit());
          // console.log('ChartPreview: beginResultsEdit dispatched successfully');
        } catch (e) {
          console.error('ChartPreview: Error dispatching beginResultsEdit:', e);
        }
        try {
          // Store snapshot for the active file only
          captureSnapshotForActiveFile();
          clearChangesForFile(fileName); // Reset changes for this file when entering edit mode
          historyRef.current = new Map();
          // Tell UI there are no actions available yet
          clearChangesForFile(fileName);
        } catch { }
      }
      // Disable panning while editing; enable when not editing
      setPanEnabled(!enabled);

      // While editing, avoid vertical zoom glitches by restricting wheel to X zoom only
      try {
        if (chartRef.current) {
          // Allow wheel zoom in edit mode too; DnD panning is still disabled separately
          chartRef.current.set('wheelY', 'zoomXY');
        }
      } catch { }
      // Keep scrollbars visible in edit mode
      try {
        const vs = vScrollRef.current;
        if (vs) vs.set('visible', true);
      } catch { }
      try {
        if (sbX) sbX.set('visible', true);
      } catch { }
      // When leaving edit mode, preserve the current Y viewport while removing any strict locks
      if (!enabled) {
        // console.log('ChartPreview: Exiting edit mode, calling finalizeResultsEdit');
        try { dispatch(finalizeResultsEdit()); } catch { }
        // Clear the snapshot for this file when finalizing
        try { snapshotsByFileRef.current.delete(fileName); } catch { }
        // Reset change tracking when exiting edit mode
        clearChangesForFile(fileName);
        try {
          const yAxis = yAxisRef.current;
          if (yAxis) {
            // Dispose any active freeze listener
            try { yFreezeDisposerRef.current?.dispose?.(); } catch { }
            yFreezeDisposerRef.current = null;
            // If axis is locked, translate min/max back to start/end and then clear locks
            const curMin = yAxis.get('min');
            const curMax = yAxis.get('max');
            if (Number.isFinite(curMin) && Number.isFinite(curMax)) {
              const sPos = yAxis.valueToPosition(Math.min(curMin, curMax));
              const ePos = yAxis.valueToPosition(Math.max(curMin, curMax));
              yAxis.set('strictMinMax', false);
              yAxis.set('min', undefined);
              yAxis.set('max', undefined);
              yAxis.set('start', sPos);
              yAxis.set('end', ePos);
            } else {
              // Otherwise, preserve current start/end
              const ys = yAxis.get('start') ?? 0;
              const ye = yAxis.get('end') ?? 1;
              const y0 = yAxis.positionToValue(Math.min(ys, ye));
              const y1 = yAxis.positionToValue(Math.max(ys, ye));
              yAxis.set('strictMinMax', false);
              yAxis.set('min', undefined);
              yAxis.set('max', undefined);
              const sPos = yAxis.valueToPosition(y0);
              const ePos = yAxis.valueToPosition(y1);
              yAxis.set('start', sPos);
              yAxis.set('end', ePos);
            }
          }
        } catch (e) {
          console.error('ChartPreview: Error during Y-axis cleanup:', e);
        }

        // CRITICAL: Wait for any pending baseline submissions to complete before chart recreation
        // This prevents visual inconsistencies when the chart recreates with stale data
        const pendingSubmissions = submitDebounceMapRef.current.size;
        if (pendingSubmissions > 0) {
          // console.log('ChartPreview: Waiting for', pendingSubmissions, 'pending submissions before chart recreation');
          // Clear timeouts and trigger immediate submission for pending changes
          const pendingPromises = [];
          submitDebounceMapRef.current.forEach((timeoutId, rangeKey) => {
            clearTimeout(timeoutId);
            // Trigger immediate submission for this range
            const selSnapshot = selectedPeakRef.current && selectedPeakRef.current.rangeKey === rangeKey
              ? { ...selectedPeakRef.current }
              : null;
            const promise = submitBaselineUpdate(rangeKey, selSnapshot, {
              expectedFileName: fileName,
              instanceId: chartInstanceRef.current
            });
            pendingPromises.push(promise);
          });
          submitDebounceMapRef.current.clear();

          // Wait for all pending submissions, then recreate chart
          Promise.all(pendingPromises).then(() => {
            // console.log('ChartPreview: All pending submissions completed, now forcing chart recreation');
            setIsChartLoading(true);
            setChartRecreationCounter(prev => prev + 1);
          }).catch(e => {
            console.error('ChartPreview: Error waiting for pending submissions:', e);
            // Recreate anyway to avoid getting stuck
            setIsChartLoading(true);
            setChartRecreationCounter(prev => prev + 1);
          });
        } else {
          // No pending submissions, recreate immediately
          // console.log('ChartPreview: No pending submissions, forcing chart recreation after edit mode exit');
          setIsChartLoading(true);
          setChartRecreationCounter(prev => prev + 1);
        }
      }
      // When disabled, clear active range and hide any visible handles
      if (!enabled) {
        const activeKey = activeRangeRef.current;
        if (activeKey) {
          const layer = baselineByRangeRef.current.get(activeKey);
          if (layer) {
            try {
              // Handles are created per baseline; ensure they are hidden via line width reset
              layer.line.strokes.template.setAll({ strokeWidth: 2 });
            } catch { }
          }
        }
        activeRangeRef.current = null;
        clearChangesForFile(fileName);
        // Clear persisted endpoints so chart recreation uses Redux data
        lastEndpointsRef.current.clear();
        try {
          baselineByRangeRef.current.forEach((layer) => {
            const { handle1, handle2 } = layer || {};
            handle1?.setAll?.({ visible: false, opacity: 0 });
            handle2?.setAll?.({ visible: false, opacity: 0 });
          });
        } catch { }
      } else {
        // Edit mode on: for single-peak ranges show endpoints; for comp ranges, show only when a comp is selected
        try {
          const sel = selectedPeakRef.current;
          if (sel) {
            try { /* align handles to selected comp immediately */
              const layer = baselineByRangeRef.current.get(sel.rangeKey);
              const lm = layer?.linesMap;
              const ln = lm && lm.get ? lm.get(sel.peakKey) : null;
              const di1 = ln?.dataItems?.[0];
              const di2 = ln?.dataItems?.[1];
              if (di1 && di2) {
                const x1 = di1.get('valueX');
                const y1 = di1.get('valueY');
                const x2 = di2.get('valueX');
                const y2 = di2.get('valueY');
                if (Number.isFinite(x1)) layer.p1.x = x1;
                if (Number.isFinite(y1)) layer.p1.y = y1;
                if (Number.isFinite(x2)) layer.p2.x = x2;
                if (Number.isFinite(y2)) layer.p2.y = y2;
              }
            } catch { }
          }
          baselineByRangeRef.current.forEach((layer, rk) => {
            const { p1, p2, handle1, handle2 } = layer || {};
            const compMap = compFillsByRangeRef.current.get(rk) || new Map();
            const show = compMap.size === 0 || (sel && sel.rangeKey === rk);
            const h1 = p1 ? dataToPixel(p1.x, p1.y) : null;
            const h2 = p2 ? dataToPixel(p2.x, p2.y) : null;
            if (handle1 && h1) handle1.setAll({ x: h1.x, y: h1.y, visible: !!show, opacity: show ? 1 : 0 });
            if (handle2 && h2) handle2.setAll({ x: h2.x, y: h2.y, visible: !!show, opacity: show ? 1 : 0 });
          });
        } catch { }
      }
    };
    window.addEventListener('baseline-edit-mode', onEditToggle);

    // Delete Peak mode toggle listener
    const onDeletePeakToggle = (evt) => {
      const enabled = evt?.detail?.enabled ?? false;
      deletingRef.current = enabled;

      if (enabled) {
        // When delete mode is enabled, disable baseline editing
        editingRef.current = false;
        // Capture snapshot of current hiddenPeaks for the file using Redux selectedFileName
        const fileNameToUse = selectedFileName || fileName;
        const fileTypeToUse = selectedFileType || currentFileType;
        if (fileNameToUse) {
          // Get the file from the correct Redux map based on file type
          const fileMap = fileTypeToUse === 'standard' ? stdFiles :
            fileTypeToUse === 'qualityControl' ? qcFiles :
              unkFiles;
          const file = fileMap[fileNameToUse];
          const fileData = file?.fileData || file;
          const hiddenPeaks = fileData?.hiddenPeaks || {};
          deletePeakSnapshotsByFileRef.current.set(fileNameToUse, { ...hiddenPeaks });

          // Also capture concentration snapshots for all peaks
          const concentrationSnapshot = {};
          if (fileData?.result) {
            for (const [rangeKey, rangeData] of Object.entries(fileData.result)) {
              if (!rangeData?.peaks) continue;
              for (const peakKey of Object.keys(rangeData.peaks)) {
                const peakIndex = resolvePeakIndex(peakKey);
                if (peakIndex >= 0 && peaksObject?.[peakIndex]) {
                  // Get current concentration for this peak and file
                  const peakData = peaksObject[peakIndex];
                  let currentConc = null;

                  if (fileTypeToUse === 'standard') {
                    currentConc = peakData.stdConcentrations?.[fileNameToUse];
                  } else if (fileTypeToUse === 'qualityControl') {
                    const qcArr = peakData.qcConcentrations || [];
                    const entry = qcArr.find(o => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileNameToUse));
                    currentConc = entry?.[fileNameToUse];
                  } else if (fileTypeToUse === 'unknown') {
                    const unkArr = peakData.unkConcentrations || [];
                    const entry = unkArr.find(o => o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, fileNameToUse));
                    currentConc = entry?.[fileNameToUse];
                  }

                  concentrationSnapshot[peakKey] = {
                    peakIndex,
                    concentration: currentConc
                  };
                }
              }
            }
          }
          concentrationSnapshotsByFileRef.current.set(fileNameToUse, concentrationSnapshot);
        }
        // Change cursor to indicate delete mode
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'pointer');
          }
        } catch { }
      } else {
        // Restore default cursor - don't clear snapshot here, it will be cleared after discard
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'default');
          }
        } catch { }
      }
    };
    window.addEventListener('delete-peak-mode', onDeletePeakToggle);

    // Delete Peak mode change listener (single vs convoluted)
    const onDeletePeakModeChange = (evt) => {
      const mode = evt?.detail?.mode ?? 'single'; // 'single' or 'convoluted'
      deletePeakModeRef.current = mode;
    };
    window.addEventListener('delete-peak-mode-change', onDeletePeakModeChange);

    // Insert Peak mode toggle listener
    const onInsertPeakToggle = (evt) => {
      const enabled = evt?.detail?.enabled ?? false;
      insertingPeakRef.current = enabled;

      if (enabled) {
        // When insert mode is enabled, disable baseline editing and delete mode
        editingRef.current = false;
        deletingRef.current = false;

        // Clear the list of restored peaks for this session
        restoredPeaksRef.current = [];

        // Disable panning initially - we'll re-enable it dynamically when not over chromatogram
        setPanEnabled(false);

        // Change cursor to indicate insert mode
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'crosshair');
          }
        } catch { }
      } else {
        // Restore default cursor, panning, and clean up any drawing state
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'default');
          }
          // Re-enable panning when exiting insert mode
          setPanEnabled(true);
          // Clean up any temporary drawing visuals
          cleanupDrawingState();
          // Clear the restored peaks list when exiting mode
          restoredPeaksRef.current = [];
        } catch { }
      }
    };
    window.addEventListener('insert-peak-mode', onInsertPeakToggle);

    // Split Peak mode toggle listener
    const onSplitPeakToggle = (evt) => {
      const enabled = evt?.detail?.enabled ?? false;
      splittingPeakRef.current = enabled;

      if (enabled) {
        // When split mode is enabled, disable other modes
        editingRef.current = false;
        deletingRef.current = false;
        insertingPeakRef.current = false;

        // Clear the list of split peaks for this session
        splitPeaksRef.current = [];

        // Disable panning to allow vertical line drawing
        setPanEnabled(false);

        // Change cursor to indicate split mode
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'crosshair');
          }
        } catch { }
      } else {
        // Restore default cursor, panning, and clean up any drawing state
        try {
          const chart = chartRef.current;
          if (chart) {
            chart.plotContainer.set('cursorOverStyle', 'default');
          }
          // Re-enable panning when exiting split mode
          setPanEnabled(true);
          // Clean up any temporary drawing visuals
          cleanupSplitDrawingState();
          // Clear the split peaks list when exiting mode
          splitPeaksRef.current = [];
        } catch { }
      }
    };
    window.addEventListener('split-peak-mode', onSplitPeakToggle);

    // Undo/Redo/Discard actions listener
    const onBaselineAction = (evt) => {
      // Ignore discard actions when not actively editing in this chart instance
      if (!editingRef.current) {
        // console.log('ChartPreview: onBaselineAction ignored - editingRef.current is false');
        return;
      }
      const action = evt?.detail?.action; // 'undo' | 'redo' | 'discard'
      if (!editingRef.current || !action) {
        // console.log('ChartPreview: onBaselineAction ignored - editingRef.current:', editingRef.current, 'action:', action);
        return;
      }
      // console.log('ChartPreview: onBaselineAction called with action:', action, 'isEditingResults:', isEditingResults, 'editingRef.current:', editingRef.current);
      // Helper to apply endpoints and refresh visuals in a scope
      const applyEndpoints = (rangeKey, peakKey, p1, p2) => {
        const v = baselineByRangeRef.current.get(rangeKey);
        if (!v) return;
        if (peakKey && v.linesMap?.has(peakKey)) {
          const ln = v.linesMap.get(peakKey);
          try { ln.data.setAll([{ ...p1 }, { ...p2 }]); } catch { }
          try {
            const cMap = compFillsByRangeRef.current.get(rangeKey);
            const fillSeries = cMap ? cMap.get(peakKey) : null;
            if (fillSeries) fillSeries.data.setAll(buildCompFillDataGlobal(rangeKey, peakKey, p1, p2));
          } catch { }
          // Sync selected comp handles to new endpoints
          try {
            const hh = v.handlesMap?.get(peakKey);
            if (hh) {
              const px1 = dataToPixel(p1.x, p1.y);
              const px2 = dataToPixel(p2.x, p2.y);
              const plotBounds = chart.plotContainer.bounds();
              const h1InBounds = px1.x >= 0 && px1.x <= plotBounds.right && px1.y >= 0 && px1.y <= plotBounds.bottom;
              const h2InBounds = px2.x >= 0 && px2.x <= plotBounds.right && px2.y >= 0 && px2.y <= plotBounds.bottom;
              hh.h1?.setAll?.({ x: px1.x, y: px1.y, visible: editingRef.current && h1InBounds, opacity: (editingRef.current && h1InBounds) ? 1 : 0 });
              hh.h2?.setAll?.({ x: px2.x, y: px2.y, visible: editingRef.current && h2InBounds, opacity: (editingRef.current && h2InBounds) ? 1 : 0 });
            }
          } catch { }
          // Keep control endpoints in sync for shared hit-testing
          try {
            if (v.getDI1 && v.getDI2) {
              const di1 = v.getDI1();
              const di2 = v.getDI2();
              di1?.set('valueXWorking', p1.x); di1?.set('valueYWorking', p1.y);
              di2?.set('valueXWorking', p2.x); di2?.set('valueYWorking', p2.y);
            }
          } catch { }
        } else if (!peakKey) {
          // Non-comp: update control line both via data.setAll and direct dataItems to guarantee sync
          try { v.line?.data?.setAll?.([{ x: p1.x, y: p1.y }, { x: p2.x, y: p2.y }]); } catch { }
          try {
            if (v.getDI1 && v.getDI2) {
              const di1 = v.getDI1();
              const di2 = v.getDI2();
              di1?.set('valueX', p1.x); di1?.set('valueY', p1.y);
              di2?.set('valueX', p2.x); di2?.set('valueY', p2.y);
            }
          } catch { }
          try { v.fill?.data?.setAll?.(buildFillGlobal(p1, p2)); } catch { }
          // Sync single-peak handles
          try {
            const h1 = v.handle1, h2 = v.handle2;
            if (h1 && h2) {
              const px1 = dataToPixel(p1.x, p1.y);
              const px2 = dataToPixel(p2.x, p2.y);
              h1.setAll({ x: px1.x, y: px1.y, visible: true, opacity: 1 });
              h2.setAll({ x: px2.x, y: px2.y, visible: true, opacity: 1 });
            }
          } catch { }
        }
        // Important: mutate existing endpoint objects in-place to keep closures/hover math in sync
        try {
          if (v.p1) {
            v.p1.x = p1.x;
            v.p1.y = p1.y;
          } else {
            v.p1 = p1;
          }
          if (v.p2) {
            v.p2.x = p2.x;
            v.p2.y = p2.y;
          } else {
            v.p2 = p2;
          }
        } catch { }
      };
      // Discard across all ranges (restore snapshot when no scope or action === discardAll)
      if (action === 'discard') {
        // console.log('ChartPreview: Processing discard action for file:', fileName);

        // Dispatch Redux discard first
        try {
          dispatch(discardResultsEdit());
        } catch (e) {
          console.error('ChartPreview: Error dispatching discardResultsEdit:', e);
        }

        // Sequence: discard, finalize, begin to reset edit state properly
        try {
          dispatch(finalizeResultsEdit());
        } catch (e) {
          console.error('ChartPreview: Error dispatching finalizeResultsEdit:', e);
        }
        try {
          dispatch(beginResultsEdit());
        } catch (e) {
          console.error('ChartPreview: Error dispatching beginResultsEdit:', e);
        }

        // Cancel pending submits immediately
        try {
          submitDebounceMapRef.current.forEach((t) => clearTimeout(t));
          submitDebounceMapRef.current.clear();
        } catch { }

        // Step 1: rebuild visuals from Redux (range-level + any stored per-peak)
        try {
          setTimeout(() => {
            rebuildBaselinesFromReduxDataRef.current?.();
          }, 50);
        } catch (e) {
          console.error('ChartPreview: Error scheduling rebuild after discard:', e);
        }

        // Step 2: if we have a snapshot for this file, layer it on top to restore per-comp visuals
        try {
          const snap = snapshotsByFileRef.current.get(fileName);
          if (snap && snap.size > 0) {
            setTimeout(() => {
              try {
                // console.log('ChartPreview: Restoring from snapshot with', snap.size, 'entries');
                snap.forEach((entry, rKey) => {
                  const v = baselineByRangeRef.current.get(rKey);
                  if (!v) return;
                  if (entry.perComp && entry.perComp.size && v.linesMap?.size) {
                    entry.perComp.forEach((pp, pk) => applyEndpoints(rKey, pk, pp.p1, pp.p2));
                    // Choose a canonical comp to drive working endpoints for hover math
                    try {
                      const sel = selectedPeakRef.current;
                      const pickKey = (sel && sel.rangeKey === rKey && v.linesMap.has(sel.peakKey))
                        ? sel.peakKey
                        : (Array.from(v.linesMap.keys())[0]);
                      const lnPick = v.linesMap.get(pickKey);
                      const di1p = lnPick?.dataItems?.[0];
                      const di2p = lnPick?.dataItems?.[1];
                      if (di1p && di2p) {
                        const P1 = { x: di1p.get('valueX'), y: di1p.get('valueY') };
                        const P2 = { x: di2p.get('valueX'), y: di2p.get('valueY') };
                        if (v.p1) { v.p1.x = P1.x; v.p1.y = P1.y; } else { v.p1 = P1; }
                        if (v.p2) { v.p2.x = P2.x; v.p2.y = P2.y; } else { v.p2 = P2; }
                      }
                    } catch { }
                  } else if (entry.control) {
                    applyEndpoints(rKey, null, entry.control.p1, entry.control.p2);
                  }
                  // Reposition handles to the restored endpoints
                  try {
                    if (v.linesMap && v.linesMap.size && v.handlesMap && v.handlesMap.size) {
                      v.linesMap.forEach((ln, pk) => {
                        const di1 = ln?.dataItems?.[0];
                        const di2 = ln?.dataItems?.[1];
                        if (!di1 || !di2) return;
                        const px1 = dataToPixel(di1.get('valueX'), di1.get('valueY'));
                        const px2 = dataToPixel(di2.get('valueX'), di2.get('valueY'));
                        const hh = v.handlesMap.get(pk);
                        hh?.h1?.setAll?.({ x: px1.x, y: px1.y });
                        hh?.h2?.setAll?.({ x: px2.x, y: px2.y });
                      });
                    } else if (v.handle1 && v.handle2 && v.p1 && v.p2) {
                      const h1 = dataToPixel(v.p1.x, v.p1.y);
                      const h2 = dataToPixel(v.p2.x, v.p2.y);
                      v.handle1.setAll({ x: h1.x, y: h1.y });
                      v.handle2.setAll({ x: h2.x, y: h2.y });
                      // Also refresh the control line and fill for non-comp ranges
                      try {
                        if (v.line?.data) {
                          v.line.data.setAll([{ x: v.p1.x, y: v.p1.y }, { x: v.p2.x, y: v.p2.y }]);
                        } else if (v.getDI1 && v.getDI2) {
                          const di1 = v.getDI1();
                          const di2 = v.getDI2();
                          di1?.set('valueX', v.p1.x); di1?.set('valueY', v.p1.y);
                          di2?.set('valueX', v.p2.x); di2?.set('valueY', v.p2.y);
                        }
                        v.fill?.data?.setAll?.(buildFillGlobal(v.p1, v.p2));
                      } catch { }
                    }
                  } catch { }
                });
                activeRangeRef.current = null;
              } catch (e) {
                console.error('ChartPreview: Error restoring from snapshot:', e);
              }
            }, 120);
          } else {
            // console.log('ChartPreview: No snapshot found for current file; Redux rebuild should suffice');
          }
        } catch { }

        // Clear discard state for current file
        clearChangesForFile(fileName);

        // CRITICAL: After discard, recalculate calibration curve if this is a standard file
        // This ensures that when we revert standard baselines, the calibration curve gets updated properly
        try {
          setTimeout(async () => {
            if (currentFileType === 'standard') {
              // console.log('ChartPreview: Recalculating calibration curve after discard for standard file:', fileName);

              // Get current standard areas from Redux state after discard
              const currentStdAreas = stdAreasState || [];

              // Trigger calibration curve recalculation for each peak group that has areas
              for (let gIdx = 0; gIdx < currentStdAreas.length; gIdx++) {
                const areasForGroup = currentStdAreas[gIdx];
                if (areasForGroup && typeof areasForGroup === 'object' && Object.keys(areasForGroup).length > 0) {
                  try {
                    await recalculateStandardConcentrations(gIdx, areasForGroup);
                  } catch (error) {
                    console.error(`Error recalculating calibration for group ${gIdx} after discard:`, error);
                  }
                }
              }
            }
          }, 200); // Wait a bit longer than the visual restoration
        } catch (error) {
          console.error('Error scheduling calibration recalculation after discard:', error);
        }

        return;
      }
      // No undo/redo behavior anymore; ignore other actions
      return;
    };

    // Delete peak action listener (for discard)
    const onDeletePeakAction = (evt) => {
      // Use selectedFileName from Redux, fallback to props fileName
      const fileNameToUse = selectedFileName || fileName;
      const fileTypeToUse = selectedFileType || currentFileType;

      if (!deletingRef.current) return;

      const action = evt?.detail?.action; // 'discard'
      if (action !== 'discard') return;

      // Use fileName from Redux or props
      if (!fileNameToUse) return;

      const snapshot = deletePeakSnapshotsByFileRef.current.get(fileNameToUse);
      if (snapshot === undefined) return;

      // Update Redux by setting hiddenPeaks back to snapshot
      dispatch(setHiddenPeaks({
        fileName: fileNameToUse,
        fileType: fileTypeToUse,
        hiddenPeaks: snapshot
      }));

      // Restore concentrations from snapshot
      const concentrationSnapshot = concentrationSnapshotsByFileRef.current.get(fileNameToUse);
      if (concentrationSnapshot) {
        Object.entries(concentrationSnapshot).forEach(([peakKey, data]) => {
          if (data && data.peakIndex >= 0) {
            dispatch(updatePeakConcentration({
              peakIndex: data.peakIndex,
              fileType: fileTypeToUse,
              fileName: fileNameToUse,
              value: data.concentration
            }));
          }
        });
      }

      // Re-capture snapshot with the restored state since we're still in delete mode
      // This allows multiple discard operations while staying in delete mode
      deletePeakSnapshotsByFileRef.current.set(fileNameToUse, { ...snapshot });
      concentrationSnapshotsByFileRef.current.set(fileNameToUse, { ...concentrationSnapshot });

      // Emit history state to disable discard button
      try {
        window.dispatchEvent(new CustomEvent('delete-peak-history-state', {
          detail: { canDiscard: false }
        }));
      } catch { }
    };

    window.addEventListener('baseline-action', onBaselineAction);
    window.addEventListener('delete-peak-action', onDeletePeakAction);

    // Insert peak action listener (for discard)
    const onInsertPeakAction = (evt) => {
      // console.log('onInsertPeakAction called, insertingPeakRef:', insertingPeakRef.current);
      if (!insertingPeakRef.current) return;

      const action = evt?.detail?.action; // 'discard'
      // console.log('Insert peak action:', action);
      if (action !== 'discard') return;

      // console.log('Discarding insert peak operation...');
      // console.log('completedInsertLinesRef:', completedInsertLinesRef.current.length);
      // console.log('completedInsertFillsRef:', completedInsertFillsRef.current.length);
      // console.log('assignmentLabelsRef:', assignmentLabelsRef.current.length);
      // console.log('drawingBaselineRef:', drawingBaselineRef.current);
      // console.log('restoredPeaksRef:', restoredPeaksRef.current);

      // Hide all restored peaks in this session
      if (restoredPeaksRef.current.length > 0) {
        // console.log('Discarding', restoredPeaksRef.current.length, 'restored/created peaks');

        // Group peaks by file for batch operations
        const peaksByFile = {};
        restoredPeaksRef.current.forEach(({ fileName: restoredFileName, fileType: restoredFileType, peakKey: restoredPeakKey, isNewPeak }) => {
          if (!peaksByFile[restoredFileName]) {
            peaksByFile[restoredFileName] = { fileType: restoredFileType, peaksToHide: [], peaksToDelete: [] };
          }

          if (isNewPeak) {
            // Newly created peaks should be deleted entirely
            peaksByFile[restoredFileName].peaksToDelete.push(restoredPeakKey);
          } else {
            // Restored hidden peaks should just be hidden again
            peaksByFile[restoredFileName].peaksToHide.push(restoredPeakKey);
          }
        });

        // Process each file
        Object.entries(peaksByFile).forEach(([restoredFileName, { fileType: restoredFileType, peaksToHide, peaksToDelete }]) => {
          // Hide peaks that were restored
          peaksToHide.forEach(peakKey => {
            // console.log('Hiding restored peak:', peakKey, 'in file:', restoredFileName);
            dispatch(togglePeakVisibility({
              fileName: restoredFileName,
              fileType: restoredFileType,
              peakKey: peakKey
            }));
          });

          // Delete newly created peaks
          if (peaksToDelete.length > 0) {
            // ('Deleting newly created peaks:', peaksToDelete, 'from file:', restoredFileName);

            // Get current file data
            const fileMap = restoredFileType === 'standard' ? stdFilesRef.current :
              restoredFileType === 'qualityControl' ? qcFilesRef.current :
                unkFilesRef.current;
            const currentFile = fileMap[restoredFileName];

            if (currentFile) {
              // Clone file data
              const updatedFileData = JSON.parse(JSON.stringify(currentFile));

              // Remove the newly created peaks from all ranges
              if (updatedFileData.result) {
                Object.keys(updatedFileData.result).forEach(rangeKey => {
                  if (updatedFileData.result[rangeKey]?.peaks) {
                    peaksToDelete.forEach(peakKey => {
                      delete updatedFileData.result[rangeKey].peaks[peakKey];
                    });
                  }
                });
              }

              // Dispatch update to Redux
              const payload = [{ fileName: restoredFileName, fileData: updatedFileData }];
              if (restoredFileType === 'standard') {
                dispatch(saveStdFilesData(payload));
              } else if (restoredFileType === 'qualityControl') {
                dispatch(saveQcFilesData(payload));
              } else if (restoredFileType === 'unknown') {
                dispatch(saveUnkFilesData(payload));
              }
            }
          }
        });

        // Clear the list after processing all peaks
        restoredPeaksRef.current = [];
      }

      // Clean up any drawing state and cancel operation
      cleanupDrawingState();

      // ('After cleanup - completedInsertLinesRef:', completedInsertLinesRef.current.length);

      // Emit history state to disable discard button
      try {
        window.dispatchEvent(new CustomEvent('insert-peak-history-state', {
          detail: { canDiscard: false }
        }));
      } catch { }
    };
    window.addEventListener('insert-peak-action', onInsertPeakAction);

    // Split peak action listener (for discard)
    const onSplitPeakAction = (evt) => {
      if (!splittingPeakRef.current) return;

      const action = evt?.detail?.action; // 'discard'
      if (action !== 'discard') return;

      // Hide all split peaks in this session and restore original peaks
      if (splitPeaksRef.current.length > 0) {
        // Group peaks by file for batch operations
        const peaksByFile = {};
        splitPeaksRef.current.forEach(({ fileName: splitFileName, fileType: splitFileType, rangeKey, originalPeakKey, newPeakKey, originalPeakData, originalArea, originalGroupIndex }) => {
          if (!peaksByFile[splitFileName]) {
            peaksByFile[splitFileName] = { fileType: splitFileType, operations: [] };
          }
          peaksByFile[splitFileName].operations.push({
            rangeKey,
            originalPeakKey,
            newPeakKey,
            originalPeakData,
            originalArea,
            originalGroupIndex
          });
        });

        // Process each file
        Object.entries(peaksByFile).forEach(([splitFileName, { fileType: splitFileType, operations }]) => {
          const fileMap = splitFileType === 'standard' ? stdFilesRef.current :
            splitFileType === 'qualityControl' ? qcFilesRef.current :
              unkFilesRef.current;

          // Get the current file data ONCE
          const currentFile = fileMap[splitFileName];
          if (!currentFile) {
            console.error('Split Discard: Could not find file:', splitFileName);
            return;
          }

          // Clone file data ONCE for all operations on this file
          const updatedFileData = JSON.parse(JSON.stringify(currentFile));

          // Track which areas we've already restored (to avoid duplicates when same peak is split multiple times)
          const areaRestoredForPeakGroup = new Set();

          // Accumulate area updates to dispatch once at the end
          const areaUpdates = {
            standard: new Map(), // groupIndex => { fileName: area }
            qualityControl: new Map(),
            unknown: new Map()
          };
          const concentrationUpdates = {
            qualityControl: [], // Array of {peakIndex, qcConcentrations}
            unknown: [] // Array of {peakIndex, unkConcentrations}
          };

          // Apply all peak data changes for this file
          operations.forEach(({ rangeKey, originalPeakKey, newPeakKey, originalPeakData, originalArea, originalGroupIndex }) => {
            // 1. Restore the original peak data
            if (originalPeakData && rangeKey) {
              // console.log('Split Discard: Processing peak', originalPeakKey, 'in range', rangeKey, 'deleting new peak', newPeakKey);

              if (updatedFileData.result?.[rangeKey]?.peaks) {
                // console.log('Split Discard: Peaks before restore:', Object.keys(updatedFileData.result[rangeKey].peaks));

                // Restore the full original peak object
                updatedFileData.result[rangeKey].peaks[originalPeakKey] = originalPeakData;

                // Delete the new split peak
                if (newPeakKey && updatedFileData.result[rangeKey].peaks[newPeakKey]) {
                  // console.log('Split Discard: Deleting new peak:', newPeakKey);
                  delete updatedFileData.result[rangeKey].peaks[newPeakKey];
                } else if (newPeakKey) {
                  console.warn('Split Discard: New peak', newPeakKey, 'not found in peaks:', Object.keys(updatedFileData.result[rangeKey].peaks));
                }

                // console.log('Split Discard: Peaks after restore:', Object.keys(updatedFileData.result[rangeKey].peaks));
              }
            }

            // 2. Accumulate area restoration (only once per peak)
            const areaKey = `${originalGroupIndex}-${originalPeakKey}-${splitFileName}`;
            if (originalGroupIndex >= 0 && originalArea !== undefined && originalArea !== null && !areaRestoredForPeakGroup.has(areaKey)) {
              areaRestoredForPeakGroup.add(areaKey);
              // console.log('Split Discard: Accumulating area', originalArea, 'for group index', originalGroupIndex);

              if (splitFileType === 'standard') {
                if (!areaUpdates.standard.has(originalGroupIndex)) {
                  areaUpdates.standard.set(originalGroupIndex, {});
                }
                areaUpdates.standard.get(originalGroupIndex)[splitFileName] = originalArea;
              } else if (splitFileType === 'qualityControl') {
                if (!areaUpdates.qualityControl.has(originalGroupIndex)) {
                  areaUpdates.qualityControl.set(originalGroupIndex, {});
                }
                areaUpdates.qualityControl.get(originalGroupIndex)[splitFileName] = originalArea;

                // Calculate concentration for QC
                const peakData = peaksObject?.[originalGroupIndex];
                if (peakData?.equation?.coefficients) {
                  const coeffs = peakData.equation.coefficients;
                  const concentration = coeffs.reduce((acc, coeff, index) =>
                    acc + coeff * Math.pow(originalArea, coeffs.length - 1 - index), 0
                  );
                  concentrationUpdates.qualityControl.push({
                    peakIndex: originalGroupIndex,
                    qcConcentrations: { [splitFileName]: concentration }
                  });
                }
              } else if (splitFileType === 'unknown') {
                if (!areaUpdates.unknown.has(originalGroupIndex)) {
                  areaUpdates.unknown.set(originalGroupIndex, {});
                }
                areaUpdates.unknown.get(originalGroupIndex)[splitFileName] = originalArea;

                // Calculate concentration for Unknown
                const peakData = peaksObject?.[originalGroupIndex];
                if (peakData?.equation?.coefficients) {
                  const coeffs = peakData.equation.coefficients;
                  const concentration = coeffs.reduce((acc, coeff, index) =>
                    acc + coeff * Math.pow(originalArea, coeffs.length - 1 - index), 0
                  );
                  concentrationUpdates.unknown.push({
                    peakIndex: originalGroupIndex,
                    unkConcentrations: { [splitFileName]: concentration }
                  });
                }
              }
            } else if (originalGroupIndex >= 0 && areaRestoredForPeakGroup.has(areaKey)) {
              // console.log('Split Discard: Skipping duplicate area for group', originalGroupIndex);
            }

            // 3. Ensure visibility is correct (show original)
            const file = fileMap[splitFileName];
            const fileData = file?.fileData || file;
            const hiddenPeaks = fileData?.hiddenPeaks || {};
            if (hiddenPeaks[originalPeakKey]) {
              dispatch(togglePeakVisibility({
                fileName: splitFileName,
                fileType: splitFileType,
                peakKey: originalPeakKey
              }));
            }
          });

          // Dispatch all accumulated area updates ONCE per file type
          if (splitFileType === 'standard' && areaUpdates.standard.size > 0) {
            const next = { ...(stdAreasState || {}) };
            areaUpdates.standard.forEach((fileAreas, groupIndex) => {
              const currentGroupAreas = stdAreasState?.[groupIndex] || {};
              next[groupIndex] = { ...currentGroupAreas, ...fileAreas };
            });
            // console.log('Split Discard: Dispatching all standard area updates:', next);
            dispatch(saveAreaUnderPeaks(next));

            // Recalculate calibration curve for all affected groups
            areaUpdates.standard.forEach(async (fileAreas, groupIndex) => {
              try {
                const currentQcAreas = qcAreasState?.[groupIndex];
                const currentUnkAreas = unkAreasState?.[groupIndex];
                await recalculateStandardConcentrations(groupIndex, next[groupIndex], currentQcAreas, currentUnkAreas);
              } catch (error) {
                console.error('Error recalculating standard concentrations after discard:', error);
              }
            });
          }

          if (splitFileType === 'qualityControl' && areaUpdates.qualityControl.size > 0) {
            const next = (qcAreasState || []).map(m => ({ ...(m || {}) }));
            areaUpdates.qualityControl.forEach((fileAreas, groupIndex) => {
              while (next.length <= groupIndex) next.push({});
              next[groupIndex] = { ...(next[groupIndex] || {}), ...fileAreas };
            });
            // console.log('Split Discard: Dispatching all QC area updates:', next);
            dispatch(saveAreaUnderPeaksQc(next));

            // Dispatch all concentration updates
            concentrationUpdates.qualityControl.forEach(update => {
              dispatch(batchUpdatePeakConcentrations(update));
            });
          }

          if (splitFileType === 'unknown' && areaUpdates.unknown.size > 0) {
            const next = (unkAreasState || []).map(m => ({ ...(m || {}) }));
            areaUpdates.unknown.forEach((fileAreas, groupIndex) => {
              while (next.length <= groupIndex) next.push({});
              next[groupIndex] = { ...(next[groupIndex] || {}), ...fileAreas };
            });
            // console.log('Split Discard: Dispatching all unknown area updates:', next);
            dispatch(saveAreaUnderPeaksUnk(next));

            // Dispatch all concentration updates
            concentrationUpdates.unknown.forEach(update => {
              dispatch(batchUpdatePeakConcentrations(update));
            });
          }

          // Dispatch file data update ONCE per file after all peak changes are applied
          const payload = [{ fileName: splitFileName, fileData: updatedFileData }];
          // console.log('Split Discard: Dispatching file update for', splitFileName, 'with type', splitFileType);
          if (splitFileType === 'standard') {
            dispatch(saveStdFilesData(payload));
          } else if (splitFileType === 'qualityControl') {
            dispatch(saveQcFilesData(payload));
          } else if (splitFileType === 'unknown') {
            dispatch(saveUnkFilesData(payload));
          }
        });

        // Clear the list after processing
        splitPeaksRef.current = [];
      }

      // Clean up any drawing state
      cleanupSplitDrawingState();

      // Wait for all dispatches to complete, then trigger chart recreation
      // This ensures the chart recreates with the fully updated Redux state
      setTimeout(() => {
        // console.log('Split Discard: Triggering chart recreation after all Redux updates');
        setIsChartLoading(true);
        setChartRecreationCounter(prev => prev + 1);
      }, 150);

      // Emit history state to disable discard button
      try {
        window.dispatchEvent(new CustomEvent('split-peak-history-state', {
          detail: { canDiscard: false }
        }));
      } catch { }
    };
    window.addEventListener('split-peak-action', onSplitPeakAction);

    chartRef.current = chart;
    chart.get('colors').set('step', 3);
    // Default state: panning enabled
    setPanEnabled(true);
    try { chart.set('wheelY', 'zoomXY'); } catch { }
    // Cursor feedback while panning; no cursor changes in edit/delete/insert/split modes
    chart.plotContainer.events.on('pointerdown', () => {
      if (!editingRef.current && !deletingRef.current && !insertingPeakRef.current && !splittingPeakRef.current) {
        chart.plotContainer.set('cursorOverStyle', 'grabbing');
      }
    });
    chart.plotContainer.events.on('globalpointerup', () => {
      if (!editingRef.current && !deletingRef.current && !insertingPeakRef.current && !splittingPeakRef.current) {
        chart.plotContainer.set('cursorOverStyle', 'grab');
      }
    });

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0.2,
        renderer: am5xy.AxisRendererX.new(root, { minorGridEnabled: false, minGridDistance: 70 }),
      })
    );
    xAxisRef.current = xAxis;
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0,
        renderer: am5xy.AxisRendererY.new(root, { minorGridEnabled: false, minGridDistance: 20 }),
        // Disable auto-zoom to fit data
        autoZoom: false,
        keepSelection: true
      })
    );
    yAxisRef.current = yAxis;

    // Helper function for calculating distance from point to line segment
    const projectToSeg = (px, py, a, b) => {
      const v = { x: b.x - a.x, y: b.y - a.y };
      const w = { x: px - a.x, y: py - a.y };
      const vv = v.x * v.x + v.y * v.y;
      const t = vv ? Math.max(0, Math.min(1, (w.x * v.x + w.y * v.y) / vv)) : 0;
      const cx = a.x + t * v.x;
      const cy = a.y + t * v.y;
      const d = Math.hypot(px - cx, py - cy);
      return { t, cx, cy, d };
    };

    // Enable smooth axis animations for better visual feedback
    try {
      chart.set('interpolationDuration', 300);
      xAxis.set('interpolationDuration', 300);
      yAxis.set('interpolationDuration', 300);
      xAxis.set('interpolationEasing', am5.ease.out(am5.ease.cubic));
      yAxis.set('interpolationEasing', am5.ease.out(am5.ease.cubic));
    } catch { }
    // Nuclear option: Completely disconnect Y-axis from all zoom mechanisms during drag
    const freezeYAxisNow = () => {
      try {
        const ya = yAxisRef.current;
        if (!ya) return;

        // Store current viewport state
        const currentState = {
          start: ya.get('start') ?? 0,
          end: ya.get('end') ?? 1,
          min: ya.get('min'),
          max: ya.get('max')
        };
        yFreezeRef.current = currentState;

        // Completely disable the Y-axis by removing all its event handlers
        ya._disposers?.forEach(disposer => {
          try { disposer.dispose(); } catch { }
        });
        ya._disposers = [];

        // Override EVERY method that could change the axis
        const originalMethods = {};
        const methodsToOverride = ['set', 'setPrivate', 'zoom', 'zoomToValues', '_updateZoom'];

        methodsToOverride.forEach(method => {
          if (ya[method]) {
            originalMethods[method] = ya[method].bind(ya);
            ya[method] = function (...args) {
              // During drag, ignore viewport-related calls completely
              if (isDraggingRef.current && args[0] &&
                (args[0] === 'start' || args[0] === 'end' || args[0] === 'min' || args[0] === 'max')) {
                return this; // Do nothing
              }
              return originalMethods[method].apply(this, args);
            };
          }
        });

        // Also disable chart-level zoom
        if (chartRef.current) {
          const chart = chartRef.current;
          chart.set('wheelable', false);
          chart.set('panY', false);

          // Store original chart methods
          if (chart._handleWheel) {
            chart._originalHandleWheel = chart._handleWheel;
            chart._handleWheel = () => { }; // Disable wheel handling
          }
        }

        yFreezeDisposerRef.current = {
          dispose: () => {
            try {
              // Immediately lock axis strictly to the frozen viewport (no visible jump)
              if (yFreezeRef.current && ya) {
                const s = Math.min(yFreezeRef.current.start, yFreezeRef.current.end);
                const e = Math.max(yFreezeRef.current.start, yFreezeRef.current.end);
                const v0 = ya.positionToValue(s);
                const v1 = ya.positionToValue(e);
                ya.set('strictMinMax', true);
                ya.set('min', v0);
                ya.set('max', v1);
              }

              // Keep overrides active for a short stabilization window, then restore
              setTimeout(() => {
                try {
                  // Restore all original methods
                  methodsToOverride.forEach(method => {
                    if (originalMethods[method]) {
                      ya[method] = originalMethods[method];
                    }
                  });

                  // Restore chart wheel handling and interactions
                  if (chartRef.current && chartRef.current._originalHandleWheel) {
                    chartRef.current._handleWheel = chartRef.current._originalHandleWheel;
                    delete chartRef.current._originalHandleWheel;
                  }
                  if (chartRef.current) {
                    chartRef.current.set('wheelable', true);
                    chartRef.current.set('panY', true);
                  }

                  // Reassert viewport once before unlocking strict bounds
                  if (yFreezeRef.current) {
                    ya.set('start', yFreezeRef.current.start);
                    ya.set('end', yFreezeRef.current.end);
                  }

                  // Release strict bounds shortly after restoring
                  setTimeout(() => {
                    try {
                      ya.set('strictMinMax', false);
                      ya.set('min', undefined);
                      ya.set('max', undefined);
                    } catch { }
                    yFreezeRef.current = null;
                  }, 120);
                } catch (e) {
                  console.error('Error finalizing Y restore:', e);
                }
              }, 300);

            } catch (e) {
              console.error('Error in dispose function:', e);
            }
          }
        };

      } catch { }
    };
    yAxis.children.moveValue(
      am5.Label.new(root, { text: 'Instrument Response', rotation: -90, y: am5.p50, centerX: am5.p50 }),
      0
    );
    xAxis.children.push(am5.Label.new(root, { text: 'Time', x: am5.p50, centerX: am5.p50 }));
    xAxis.get('renderer').grid.template.setAll({ strokeOpacity: 0.1 });
    yAxis.get('renderer').grid.template.setAll({ strokeOpacity: 0.1 });

    // Main series
    const mainSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: (props.name || '').split('.').slice(0, -1).join('.') || props.name,
        xAxis,
        yAxis,
        valueYField: 'y',
        valueXField: 'x',
        zIndex: 10,
      })
    );
    mainSeries.strokes.template.setAll({ strokeWidth: 1, interactive: false });
    mainSeries.setAll({
      interactive: false,
      interpolationDuration: 300,
      sequencedInterpolation: true,
      interpolationEasing: am5.ease.out(am5.ease.cubic)
    });
    mainSeries.data.setAll(props.data || []);
    mainSeriesRef.current = mainSeries;
    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', snapToSeries: [mainSeries] }));
    const legend = chart.children.unshift(am5.Legend.new(root, { y: am5.p0, centerY: am5.p50, paddingLeft: 70 }));
    legend.data.setAll([mainSeries]);
    // Vertical selection marker (hidden by default)
    const selSeries = chart.series.push(
      am5xy.LineSeries.new(root, { xAxis, yAxis, valueXField: 'x', valueYField: 'y', zIndex: 50 })
    );
    selSeries.strokes.template.setAll({ stroke: am5.color(0xff3b30), strokeWidth: 2, strokeDasharray: [6, 3] });
    selSeries.setAll({ interactive: false });
    selectedMarkerRef.current = selSeries;

    // Helpers
    const dataToPixel = (x, y) => {
      const px = xAxis.get('renderer').positionToCoordinate(xAxis.valueToPosition(x));
      const py = yAxis.get('renderer').positionToCoordinate(yAxis.valueToPosition(y));
      return { x: px, y: py };
    };

    // Assign the rebuild function now that dataToPixel is available
    rebuildBaselinesFromReduxDataRef.current = () => {
      try {
        // console.log('ChartPreview: rebuildBaselinesFromReduxData called');

        // Get the current file from Redux state
        const currentReduxFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName];
        if (!currentReduxFile?.result) {
          // console.log('ChartPreview: No Redux data found for file:', fileName);
          return;
        }

        // console.log('ChartPreview: Rebuilding baselines for file:', fileName);

        // Update each range's baseline from Redux data
        Object.entries(currentReduxFile.result).forEach(([rangeKey, rangeData]) => {
          if (!rangeData?.to_analyze) return;

          const layer = baselineByRangeRef.current.get(rangeKey);
          if (!layer) return;

          // Determine baseline data: always use peak-level data when available
          let baselineX = rangeData.x || [];
          let baselineY = rangeData.y_baseline || [];

          // If there are peaks with their own baseline data, use the first peak's data
          if (rangeData.peaks) {
            const peaksWithBaseline = Object.values(rangeData.peaks).filter(peak =>
              peak && Array.isArray(peak.x) && Array.isArray(peak.y_baseline) &&
              peak.x.length >= 2 && peak.y_baseline.length === peak.x.length
            );

            if (peaksWithBaseline.length > 0) {
              // Use the first peak's baseline data
              const representativePeak = peaksWithBaseline[0];
              baselineX = representativePeak.x;
              baselineY = representativePeak.y_baseline;
            }
          }

          if (baselineX.length < 2 || baselineY.length !== baselineX.length) return;

          // Update endpoints from Redux baseline data
          const p1 = { x: baselineX[0], y: baselineY[0] };
          const p2 = { x: baselineX[baselineX.length - 1], y: baselineY[baselineY.length - 1] };

          // console.log('ChartPreview: Updating range', rangeKey, 'with endpoints:', p1, p2);

          // Update the layer's endpoints
          if (layer.p1) { layer.p1.x = p1.x; layer.p1.y = p1.y; } else { layer.p1 = p1; }
          if (layer.p2) { layer.p2.x = p2.x; layer.p2.y = p2.y; } else { layer.p2 = p2; }

          // Update visual elements
          try {
            // Update control line
            if (layer.line && layer.line.data) {
              layer.line.data.setAll([p1, p2]);
            }

            // Update fill
            if (layer.fill && layer.fill.data) {
              layer.fill.data.setAll(buildFillGlobal(p1, p2));
            }

            // Update per-component lines and fills if they exist
            if (layer.linesMap && layer.linesMap.size && rangeData.peaks) {
              const interpBaselineAt = (xv) => {
                const n = baselineX.length;
                if (!n || baselineY.length !== n) return null;
                if (xv <= baselineX[0]) return baselineY[0];
                if (xv >= baselineX[n - 1]) return baselineY[n - 1];
                let lo = 0, hi = n - 1;
                while (hi - lo > 1) {
                  const mid = (lo + hi) >> 1;
                  const xm = baselineX[mid];
                  if (xm === xv) return baselineY[mid];
                  if (xm < xv) lo = mid; else hi = mid;
                }
                const x0 = baselineX[lo], y0 = baselineY[lo];
                const x1 = baselineX[hi], y1 = baselineY[hi];
                const t = x1 === x0 ? 0 : (xv - x0) / (x1 - x0);
                return y0 + t * (y1 - y0);
              };
              layer.linesMap.forEach((line, peakKey) => {
                const peakData = rangeData.peaks[peakKey];
                const hasPerPeak = peakData && Array.isArray(peakData.x_comp) && Array.isArray(peakData.y_baseline)
                  && peakData.x_comp.length >= 2 && peakData.y_baseline.length === peakData.x_comp.length;
                const hasPerPeakXOnly = peakData && Array.isArray(peakData.x_comp) && peakData.x_comp.length >= 2;
                let pp1 = null, pp2 = null;
                if (hasPerPeak) {
                  pp1 = { x: peakData.x_comp[0], y: peakData.y_baseline[0] };
                  pp2 = { x: peakData.x_comp[peakData.x_comp.length - 1], y: peakData.y_baseline[peakData.y_baseline.length - 1] };
                } else if (hasPerPeakXOnly && baselineX.length >= 2 && baselineY.length === baselineX.length) {
                  // Fallback: derive component endpoints from baseline at comp's x endpoints
                  const xStart = peakData.x_comp[0];
                  const xEnd = peakData.x_comp[peakData.x_comp.length - 1];
                  const yStart = interpBaselineAt(xStart);
                  const yEnd = interpBaselineAt(xEnd);
                  if (Number.isFinite(yStart) && Number.isFinite(yEnd)) {
                    pp1 = { x: xStart, y: yStart };
                    pp2 = { x: xEnd, y: yEnd };
                  }
                }
                if (pp1 && pp2) {

                  // Update per-component line
                  if (line && line.data) {
                    line.data.setAll([pp1, pp2]);
                  }

                  // Update per-component fill
                  const compFillMap = compFillsByRangeRef.current.get(rangeKey);
                  const fillSeries = compFillMap ? compFillMap.get(peakKey) : null;
                  if (fillSeries) {
                    fillSeries.data.setAll(buildCompFillDataGlobal(rangeKey, peakKey, pp1, pp2));
                  }
                }
              });
            }

            // Update handles to match new endpoints
            if (layer.handle1 && layer.handle2) {
              const h1 = dataToPixel(p1.x, p1.y);
              const h2 = dataToPixel(p2.x, p2.y);
              layer.handle1.setAll({ x: h1.x, y: h1.y });
              layer.handle2.setAll({ x: h2.x, y: h2.y });
            }

            // Update per-component handles
            if (layer.handlesMap && layer.handlesMap.size) {
              layer.linesMap.forEach((line, peakKey) => {
                const di1 = line?.dataItems?.[0];
                const di2 = line?.dataItems?.[1];
                if (di1 && di2) {
                  const px1 = dataToPixel(di1.get('valueX'), di1.get('valueY'));
                  const px2 = dataToPixel(di2.get('valueX'), di2.get('valueY'));
                  const handles = layer.handlesMap.get(peakKey);
                  if (handles) {
                    handles.h1?.setAll?.({ x: px1.x, y: px1.y });
                    handles.h2?.setAll?.({ x: px2.x, y: px2.y });
                  }
                }
              });
            }
          } catch (e) {
            console.error('ChartPreview: Error updating visuals for range', rangeKey, ':', e);
          }
        });

        // Clear the active range and update selection styles
        activeRangeRef.current = null;
        clearChangesForFile(fileName);

        // console.log('ChartPreview: Baseline rebuild completed');
      } catch (e) {
        console.error('ChartPreview: Error in rebuildBaselinesFromReduxData:', e);
      }
    };
    const positionFromPixel = (axis, coordPx) => {
      const renderer = axis.get('renderer');
      const c0 = renderer.positionToCoordinate(0);
      const c1 = renderer.positionToCoordinate(1);
      if (c0 === c1) return 0;
      let lo = 0, hi = 1;
      for (let i = 0; i < 22; i++) {
        const mid = (lo + hi) / 2;
        const cmid = renderer.positionToCoordinate(mid);
        const increasing = c1 >= c0;
        if ((increasing && coordPx > cmid) || (!increasing && coordPx < cmid)) lo = mid; else hi = mid;
      }
      return (lo + hi) / 2;
    };
    const pixelToData = (px, py) => {
      const posX = positionFromPixel(xAxis, px);
      const posY = positionFromPixel(yAxis, py);
      return { x: xAxis.positionToValue(posX), y: yAxis.positionToValue(posY) };
    };
    const nearestSeriesPointToPixel = (px, py) => {
      let best = null;
      let bestD2 = Infinity;
      for (let i = 0; i < mainSeries.dataItems.length; i++) {
        const di = mainSeries.dataItems[i];
        const vx = di.get('valueX');
        const vy = di.get('valueY');
        const p = dataToPixel(vx, vy);
        const dx = p.x - px;
        const dy = p.y - py;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = { x: vx, y: vy };
        }
      }
      return best;
    };
    // Interpolate main series y at a given x
    const getSeriesYAtX = (xVal) => {
      const arr = Array.isArray(props.data) ? props.data : [];
      const n = arr.length;
      if (!n) return null;
      if (xVal <= arr[0].x) return arr[0].y;
      if (xVal >= arr[n - 1].x) return arr[n - 1].y;
      let lo = 0, hi = n - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (arr[mid].x === xVal) return arr[mid].y;
        if (arr[mid].x < xVal) lo = mid; else hi = mid;
      }
      const x0 = arr[lo].x, y0 = arr[lo].y;
      const x1 = arr[hi].x, y1 = arr[hi].y;
      const t = x1 === x0 ? 0 : (xVal - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    };

    // Allocate/get a stable color for a given peak ID in current file
    const getPeakColor = (rangeKey, peakKey, fallback) => {
      try {
        const perFile = peakColorsByFileRef.current;
        const fileKey = fileName || 'default-file';
        if (!perFile.has(fileKey)) perFile.set(fileKey, new Map());
        const map = perFile.get(fileKey);
        const id = `${rangeKey}::${peakKey}`;
        if (map.has(id)) return map.get(id);
        const col = chart.get('colors').next();
        map.set(id, col);
        return col;
      } catch { return fallback; }
    };
    // Selection helpers
    const updateSelectionStyles = () => {
      const sel = selectedPeakRef.current;
      // Reset baseline lines to default (no selection-driven styling)
      baselineByRangeRef.current.forEach((layer) => {
        try { layer.line.strokes.template.setAll({ strokeWidth: 2, strokeDasharray: [] }); } catch { }
        try {
          const lm = layer.linesMap;
          if (lm && lm.forEach) lm.forEach((ln) => ln?.strokes?.template?.setAll?.({ strokeWidth: 2 }));
        } catch { }
        // Do not toggle handles based on selection; handles appear only on hover/drag
      });
      // Emphasize comp fills for selected peak; de-emphasize others
      let selHasComp = false;
      compFillsByRangeRef.current.forEach((map, rk) => {
        map.forEach((fillSeries, pk) => {
          const isSel = sel && sel.rangeKey === rk && sel.peakKey === pk;
          if (isSel) selHasComp = true;
          try { fillSeries.fills.template.set('fillOpacity', isSel ? 0.6 : 0.15); } catch { }
        });
      });
      // If no comp series exists for the selected peak, emphasize the baseline area fill for that range
      baselineByRangeRef.current.forEach((layer, rk) => {
        const isSelRange = sel && sel.rangeKey === rk;
        try {
          const targetOpacity = isSelRange && !selHasComp ? 0.6 : 0.2;
          layer.fill?.fills?.template?.setAll?.({ fillOpacity: targetOpacity });
        } catch { }
        // Also ensure single-peak (non-comp) area fill color matches its baseline line stroke
        try {
          const compMap = compFillsByRangeRef.current.get(rk);
          const hasComps = compMap && compMap.size > 0;
          if (!hasComps) {
            const strokeCol = layer?.line?.strokes?.template?.get?.('stroke');
            if (strokeCol) layer.fill?.fills?.template?.setAll?.({ fill: strokeCol });
          }
        } catch { }
      });
      // Hide vertical selection marker entirely (opacity-only cue)
      try { selectedMarkerRef.current?.data?.setAll?.([]); } catch { }
      try { selectedMarkerRef.current?.set?.('visible', false); } catch { }
    };
    // Ensure handles align to the selected comp's endpoints immediately
    const syncHandlesForSelection = () => {
      try {
        const sel = selectedPeakRef.current;
        if (!sel) return;
        const layer = baselineByRangeRef.current.get(sel.rangeKey);
        if (!layer) return;
        const { linesMap, handle1, handle2 } = layer || {};
        if (!linesMap || !linesMap.size || !linesMap.has(sel.peakKey)) return;
        const ln = linesMap.get(sel.peakKey);
        const di1 = ln?.dataItems?.[0];
        const di2 = ln?.dataItems?.[1];
        const x1 = di1?.get?.('valueX');
        const y1 = di1?.get?.('valueY');
        const x2 = di2?.get?.('valueX');
        const y2 = di2?.get?.('valueY');
        if (Number.isFinite(x1)) layer.p1.x = x1;
        if (Number.isFinite(y1)) layer.p1.y = y1;
        if (Number.isFinite(x2)) layer.p2.x = x2;
        if (Number.isFinite(y2)) layer.p2.y = y2;
        // Move handle visuals to these endpoints now
        const h1 = dataToPixel(layer.p1.x, layer.p1.y);
        const plotBounds = chart.plotContainer.bounds();
        const h1InBounds = h1.x >= 0 && h1.x <= plotBounds.right && h1.y >= 0 && h1.y <= plotBounds.bottom;
        handle1.setAll({ x: h1.x, y: h1.y, visible: editingRef.current && h1InBounds, opacity: (editingRef.current && h1InBounds) ? 1 : 0 });
        if (handle2) {
          const h2 = dataToPixel(layer.p2.x, layer.p2.y);
          const h2InBounds = h2.x >= 0 && h2.x <= plotBounds.right && h2.y >= 0 && h2.y <= plotBounds.bottom;
          handle2.setAll({ x: h2.x, y: h2.y, visible: editingRef.current && h2InBounds, opacity: (editingRef.current && h2InBounds) ? 1 : 0 });
        }
      } catch { }
    };
    const selectPeak = (rangeKey, peakKey) => {
      // Prevent peak selection changes during drag operations
      if (isDraggingRef.current) {
        // console.log('ChartPreview: selectPeak blocked during drag operation');
        return;
      }
      selectedPeakRef.current = { rangeKey, peakKey };
      updateSelectionStyles();
      // If a comp is selected, align handles immediately
      syncHandlesForSelection();
      // Re-sync colors for comps and single ranges immediately on selection
      try {
        const layer = baselineByRangeRef.current.get(rangeKey);
        if (layer) {
          const compMap = compFillsByRangeRef.current.get(rangeKey);
          if (compMap && compMap.size > 0) {
            const linesMap = layer?.linesMap || new Map();
            compMap.forEach((fs, cKey) => {
              const ln = linesMap.get(cKey);
              const strokeCol = ln?.strokes?.template?.get?.('stroke');
              if (strokeCol) fs?.fills?.template?.setAll?.({ fill: strokeCol });
            });
          } else {
            // Single-peak: keep existing color; just re-sync area fill and handles to current line stroke
            const strokeCol = layer?.line?.strokes?.template?.get?.('stroke');
            if (strokeCol) {
              try { layer?.fill?.fills?.template?.setAll?.({ fill: strokeCol }); } catch { }
              try { layer?.handle1?.setAll?.({ fill: strokeCol, stroke: strokeCol }); layer?.handle2?.setAll?.({ fill: strokeCol, stroke: strokeCol }); } catch { }
            }
          }
        }
      } catch { }
    };

    // Helper function to clean up drawing state
    const cleanupDrawingState = () => {
      const drawState = drawingBaselineRef.current;
      if (drawState) {
        // Remove temporary visual elements
        try {
          if (drawState.tempLine) {
            drawState.tempLine.dispose();
          }
        } catch (e) {
          console.error('Error disposing tempLine:', e);
        }

        try {
          if (drawState.tempFill) {
            drawState.tempFill.dispose();
          }
        } catch (e) {
          console.error('Error disposing tempFill:', e);
        }

        // Clear drawing state
        drawingBaselineRef.current = null;
      }

      // Clean up all completed insert lines
      try {
        completedInsertLinesRef.current.forEach(line => {
          if (line) {
            line.dispose();
          }
        });
        completedInsertLinesRef.current = [];
      } catch (e) {
        console.error('Error cleaning up lines:', e);
      }

      // Clean up all completed insert fills
      try {
        completedInsertFillsRef.current.forEach(fill => {
          if (fill) {
            fill.dispose();
          }
        });
        completedInsertFillsRef.current = [];
      } catch (e) {
        console.error('Error cleaning up fills:', e);
      }

      // Clean up assignment labels (multi-peak selection triangles)
      try {
        assignmentLabelsRef.current.forEach(labelSeries => {
          if (labelSeries && chartRef.current) {
            chartRef.current.series.removeValue(labelSeries);
          }
        });
        assignmentLabelsRef.current = [];
      } catch (e) {
        console.error('Error cleaning up labels:', e);
      }
    };

    // Helper function to clean up split drawing state
    const cleanupSplitDrawingState = () => {
      const drawState = drawingSplitLineRef.current;
      if (drawState) {
        // Remove temporary split line visual
        try {
          if (drawState.tempLine) {
            drawState.tempLine.dispose();
          }
        } catch (e) {
          console.error('Error disposing split tempLine:', e);
        }

        // Clear drawing state
        drawingSplitLineRef.current = null;
      }

      // Clean up all completed split lines
      try {
        completedSplitLinesRef.current.forEach(line => {
          if (line) {
            line.dispose();
          }
        });
        completedSplitLinesRef.current = [];
      } catch (e) {
        console.error('Error cleaning up split lines:', e);
      }

      // Clean up assignment labels (left/right peak selection indicators)
      try {
        assignmentLabelsRef.current.forEach(labelSeries => {
          if (labelSeries && chartRef.current) {
            chartRef.current.series.removeValue(labelSeries);
          }
        });
        assignmentLabelsRef.current = [];
      } catch (e) {
        console.error('Error cleaning up split labels:', e);
      }

      // Clean up peak selection triangles (for multi-peak ranges)
      try {
        peakSelectionTrianglesRef.current.forEach(labelSeries => {
          if (labelSeries && chartRef.current) {
            chartRef.current.series.removeValue(labelSeries);
          }
        });
        peakSelectionTrianglesRef.current = [];
      } catch (e) {
        console.error('Error cleaning up peak selection triangles:', e);
      }

      // Clear selected peak reference
      selectedSplitPeakRef.current = null;

      // Clear highlighted peak and restore normal opacity
      if (highlightedSplitPeakRef.current) {
        try {
          const rangeKey = highlightedSplitPeakRef.current.rangeKey;
          const peakKey = highlightedSplitPeakRef.current.peakKey;
          const compMap = compFillsByRangeRef.current.get(rangeKey);
          if (compMap) {
            const fillSeries = compMap.get(peakKey);
            if (fillSeries) {
              fillSeries.fills.template.setAll({ fillOpacity: 0.15 }); // Restore normal
            }
          }
        } catch { }
        highlightedSplitPeakRef.current = null;
      }
    };

    // Helper function to show triangle selection UI for multi-peak ranges
    const showPeakSelectionTriangles = (candidates, cursorX) => {
      // Clean up any existing triangles first
      try {
        peakSelectionTrianglesRef.current.forEach(series => {
          if (series && chartRef.current) {
            chartRef.current.series.removeValue(series);
          }
        });
        peakSelectionTrianglesRef.current = [];
      } catch { }

      const labelSeriesList = [];
      const colors = [0xFF6B6B, 0x4ECDC4, 0xFFD93D, 0x6BCB77]; // Red, Cyan, Yellow, Green

      candidates.forEach((candidate, index) => {
        try {
          const peakX = candidate.peak?.x_comp || candidate.peak?.x || [];
          const peakY = candidate.peak?.y_comp || candidate.peak?.y || [];

          if (peakX.length === 0 || peakY.length === 0) return;

          // Interpolate Y value at cursor X
          let yAtX = peakY[0];
          for (let i = 0; i < peakX.length - 1; i++) {
            if (peakX[i] <= cursorX && cursorX <= peakX[i + 1]) {
              const ratio = (cursorX - peakX[i]) / (peakX[i + 1] - peakX[i]);
              yAtX = peakY[i] + ratio * (peakY[i + 1] - peakY[i]);
              break;
            }
          }

          const labelSeries = chartRef.current.series.push(
            am5xy.LineSeries.new(rootRef.current, {
              valueYField: 'y',
              valueXField: 'x',
              xAxis: xAxisRef.current,
              yAxis: yAxisRef.current
            })
          );
          labelSeries.strokes.template.setAll({ visible: false });

          labelSeries.bullets.push(() => {
            const container = am5.Container.new(rootRef.current, {
              centerX: am5.p50,
              centerY: am5.p0
            });

            const triangle = container.children.push(
              am5.Triangle.new(rootRef.current, {
                width: 24,
                height: 24,
                fill: am5.color(colors[index % colors.length]),
                stroke: am5.color(0xffffff),
                strokeWidth: 2,
                rotation: 180, // Point downward
                cursorOverStyle: 'pointer',
                tooltipText: `Click to split peak ${candidate.peakKey}`,
                interactive: true
              })
            );

            triangle.states.create('hover', {
              scale: 1.2
            });

            triangle.events.on('click', (ev) => {
              // Clean up all triangles
              peakSelectionTrianglesRef.current.forEach(ls => {
                try {
                  chartRef.current.series.removeValue(ls);
                } catch { }
              });
              peakSelectionTrianglesRef.current = [];

              // Get peak data for auto-selection
              const peakData = candidate.peak;
              const peakX = peakData?.x_comp || peakData?.x || [];
              const peakY = peakData?.y_comp || peakData?.y || [];

              if (peakX.length === 0 || peakY.length === 0) {
                console.error('Invalid peak data for triangle selection');
                return;
              }

              // Split data into left and right sides of the cursorX
              const leftIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x < cursorX);
              const rightIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x >= cursorX);

              if (leftIndices.length === 0 || rightIndices.length === 0) {
                console.error('Triangle click: Split position does not properly divide the peak');
                return;
              }

              // Find max Y on each side
              const leftMax = Math.max(...leftIndices.map(p => p.y));
              const rightMax = Math.max(...rightIndices.map(p => p.y));

              // Auto-select: The side with higher max Y is the "original" peak
              const originalSide = leftMax > rightMax ? 'left' : 'right';

              // Process split immediately with auto-selected side
              processPeakSplit(candidate.rangeKey, candidate.peakKey, cursorX, originalSide);
            });

            return am5.Bullet.new(rootRef.current, { sprite: container });
          });

          labelSeries.data.setAll([{ x: cursorX, y: yAtX }]);
          labelSeriesList.push(labelSeries);

        } catch (e) {
          console.error('Error creating peak selection triangle:', e);
        }
      });

      peakSelectionTrianglesRef.current = labelSeriesList;
    };

    // Helper function to show assignment UI for split peak selection
    const showSplitAssignmentUI = (rangeKey, peakKey, splitX) => {
      const colors = [0x4ECDC4, 0xFF6B6B]; // Left (cyan), Right (red)
      const labels = ['LEFT', 'RIGHT'];
      const labelSeriesList = [];

      // Get peak data to position indicators
      const fileData = currentFile?.fileData || currentFile;
      const rangeData = fileData?.result?.[rangeKey];
      const peak = rangeData?.peaks?.[peakKey];
      const peakX = peak?.x_comp || peak?.x || [];
      const peakY = peak?.y_comp || peak?.y || [];

      if (peakX.length === 0) {
        cleanupSplitDrawingState();
        return;
      }

      // Find max Y values on each side of split for indicator placement
      const leftIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x < splitX);
      const rightIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x >= splitX);

      if (leftIndices.length === 0 || rightIndices.length === 0) {
        // Split line doesn't properly divide the peak
        cleanupSplitDrawingState();
        return;
      }

      const leftMax = leftIndices.reduce((max, p) => p.y > max.y ? p : max, leftIndices[0]);
      const rightMax = rightIndices.reduce((max, p) => p.y > max.y ? p : max, rightIndices[0]);

      // Calculate X range of peak for positioning offsets
      const peakXMin = Math.min(...peakX);
      const peakXMax = Math.max(...peakX);
      const peakXRange = peakXMax - peakXMin;

      // Calculate horizontal offset to push buttons away from center (10% of peak width)
      const horizontalOffset = peakXRange * 0.1;

      // Calculate Y range for vertical separation if needed
      const peakYMax = Math.max(...peakY);
      const peakYRange = peakYMax - Math.min(...peakY);

      // Check if buttons would be too close vertically (within 15% of peak height)
      const verticalProximityThreshold = peakYRange * 0.15;
      const buttonsAreTooClose = Math.abs(leftMax.y - rightMax.y) < verticalProximityThreshold;

      // Add vertical offset to separate buttons if they're too close
      const verticalOffset = buttonsAreTooClose ? peakYRange * 0.2 : 0;

      // Position LEFT button: slightly left and potentially higher
      const leftPosition = {
        x: leftMax.x - horizontalOffset,
        y: leftMax.y + (buttonsAreTooClose ? verticalOffset : 0)
      };

      // Position RIGHT button: slightly right and potentially lower  
      const rightPosition = {
        x: rightMax.x + horizontalOffset,
        y: rightMax.y - (buttonsAreTooClose ? verticalOffset : 0)
      };

      const positions = [leftPosition, rightPosition];

      // Create indicators for LEFT and RIGHT
      positions.forEach((position, index) => {
        try {
          const labelSeries = chartRef.current.series.push(
            am5xy.LineSeries.new(rootRef.current, {
              valueXField: 'x',
              valueYField: 'y',
              xAxis: xAxisRef.current,
              yAxis: yAxisRef.current
            })
          );

          labelSeries.bullets.push(() => {
            const container = am5.Container.new(rootRef.current, {
              centerX: am5.p50,
              centerY: am5.p100,
              interactive: true,
              cursorOverStyle: 'pointer',
              draggable: false
            });

            // Background circle
            const circle = container.children.push(
              am5.Circle.new(rootRef.current, {
                radius: 20,
                fill: am5.color(colors[index]),
                stroke: am5.color(0xffffff),
                strokeWidth: 2,
                cursorOverStyle: 'pointer',
                interactive: true
              })
            );

            // Label text
            const label = container.children.push(
              am5.Label.new(rootRef.current, {
                text: labels[index],
                fontSize: 10,
                fontWeight: 'bold',
                fill: am5.color(0xffffff),
                centerX: am5.p50,
                centerY: am5.p50,
                populateText: true,
                interactive: false
              })
            );

            // Add hover effect to the entire container for better UX
            container.states.create('hover', {
              scale: 1.3
            });

            // Add down/pressed state for visual feedback
            container.states.create('down', {
              scale: 1.1
            });

            // Handle click - user selects which side is the original peak
            container.events.on('click', () => {
              // Clean up all assignment labels
              labelSeriesList.forEach(ls => {
                try {
                  chartRef.current.series.removeValue(ls);
                } catch (e) {
                  console.warn('Error removing label series:', e);
                }
              });
              assignmentLabelsRef.current = [];

              // Process the split with user's choice
              // index 0 = LEFT side is original, index 1 = RIGHT side is original
              processPeakSplit(rangeKey, peakKey, splitX, index === 0 ? 'left' : 'right');
            });

            return am5.Bullet.new(rootRef.current, {
              sprite: container
            });
          });

          // Position the indicator above the peak maximum
          labelSeries.data.setAll([{ x: position.x, y: position.y }]);
          labelSeriesList.push(labelSeries);

        } catch (e) {
          console.error('Error creating split assignment indicator:', e);
        }
      });

      // Store labels for cleanup
      assignmentLabelsRef.current = labelSeriesList;
    };

    // Helper function to process peak split after user selects which side is original
    const processPeakSplit = async (rangeKey, originalPeakKey, splitX, originalSide) => {
      try {
        // Get current file data
        const fileMap = currentFileType === 'standard' ? stdFilesRef.current :
          currentFileType === 'qualityControl' ? qcFilesRef.current :
            unkFilesRef.current;
        const file = fileMap[fileName];
        if (!file) return;

        const fileData = file?.fileData || file;
        const rangeData = fileData?.result?.[rangeKey];
        const originalPeak = rangeData?.peaks?.[originalPeakKey];

        if (!originalPeak) return;

        // Get peak arrays (use x_comp/y_comp for consistency)
        const originalX = originalPeak.x_comp || originalPeak.x || [];
        const originalYComp = originalPeak.y_comp || originalPeak.y || [];
        const originalYBaseline = originalPeak.y_baseline || [];

        if (originalX.length === 0 || originalX.length !== originalYComp.length || originalX.length !== originalYBaseline.length) {
          console.error('Invalid peak arrays for splitting');
          return;
        }

        // Find split index - the first point at or after splitX
        const splitIndex = originalX.findIndex(x => x >= splitX);
        if (splitIndex <= 0 || splitIndex >= originalX.length) {
          console.error('Invalid split position');
          return;
        }

        // Split the arrays
        const leftX = originalX.slice(0, splitIndex);
        const leftYComp = originalYComp.slice(0, splitIndex);
        const leftYBaseline = originalYBaseline.slice(0, splitIndex);

        const rightX = originalX.slice(splitIndex);
        const rightYComp = originalYComp.slice(splitIndex);
        const rightYBaseline = originalYBaseline.slice(splitIndex);

        // Calculate areas for both peaks using trapezoidal rule
        const calculateArea = (xArr, yCompArr, yBaselineArr) => {
          let area = 0;
          for (let i = 1; i < xArr.length; i++) {
            const dx = xArr[i] - xArr[i - 1];
            const y0 = Math.max(0, yCompArr[i - 1] - yBaselineArr[i - 1]);
            const y1 = Math.max(0, yCompArr[i] - yBaselineArr[i]);
            area += 0.5 * (y0 + y1) * dx;
          }
          return area;
        };

        const leftArea = calculateArea(leftX, leftYComp, leftYBaseline);
        const rightArea = calculateArea(rightX, rightYComp, rightYBaseline);

        // Generate new peak key (similar to Insert Peak logic)
        // Use the X position at the split as the new peak identifier
        const newPeakKey = splitX.toFixed(3);

        // Determine which peak keeps the original key and which gets the new key
        let peak1Key, peak1X, peak1YComp, peak1YBaseline, peak1Area;
        let peak2Key, peak2X, peak2YComp, peak2YBaseline, peak2Area;

        if (originalSide === 'left') {
          // LEFT side is the original peak
          peak1Key = originalPeakKey;
          peak1X = leftX;
          peak1YComp = leftYComp;
          peak1YBaseline = leftYBaseline;
          peak1Area = leftArea;

          peak2Key = newPeakKey;
          peak2X = rightX;
          peak2YComp = rightYComp;
          peak2YBaseline = rightYBaseline;
          peak2Area = rightArea;
        } else {
          // RIGHT side is the original peak
          peak1Key = newPeakKey;
          peak1X = leftX;
          peak1YComp = leftYComp;
          peak1YBaseline = leftYBaseline;
          peak1Area = leftArea;

          peak2Key = originalPeakKey;
          peak2X = rightX;
          peak2YComp = rightYComp;
          peak2YBaseline = rightYBaseline;
          peak2Area = rightArea;
        }

        // Create updated file data with both peaks
        const updatedFileData = JSON.parse(JSON.stringify(fileData));
        const updatedRange = updatedFileData.result[rangeKey];

        // Helper to find peak position (x, y at max Y)
        const findPeakPosition = (xArr, yCompArr) => {
          let maxY = -Infinity;
          let maxIndex = 0;
          for (let i = 0; i < yCompArr.length; i++) {
            if (yCompArr[i] > maxY) {
              maxY = yCompArr[i];
              maxIndex = i;
            }
          }
          return { x_peak: xArr[maxIndex], y_peak: maxY };
        };

        const peak1Position = findPeakPosition(peak1X, peak1YComp);
        const peak2Position = findPeakPosition(peak2X, peak2YComp);

        // Update/create both peaks in the range
        // Original peak (whichever side was selected) keeps its to_report value
        updatedRange.peaks[peak1Key] = {
          ...originalPeak,
          x_comp: peak1X,
          y_comp: peak1YComp,
          y_baseline: peak1YBaseline,
          x: peak1X, // Keep x in sync
          y: peak1YComp, // Keep y in sync for compatibility
          x_peak: peak1Position.x_peak,
          y_peak: peak1Position.y_peak,
          to_report: originalSide === 'left' ? originalPeak.to_report : false // Original keeps to_report, new gets false
        };

        updatedRange.peaks[peak2Key] = {
          ...originalPeak,
          x_comp: peak2X,
          y_comp: peak2YComp,
          y_baseline: peak2YBaseline,
          x: peak2X, // Keep x in sync
          y: peak2YComp, // Keep y in sync for compatibility
          x_peak: peak2Position.x_peak,
          y_peak: peak2Position.y_peak,
          to_report: originalSide === 'right' ? originalPeak.to_report : false // Original keeps to_report, new gets false
        };



        // Update Redux with new file data
        const payload = [{ fileName, fileData: updatedFileData }];
        if (currentFileType === 'standard') {
          dispatch(saveStdFilesData(payload));
        } else if (currentFileType === 'qualityControl') {
          dispatch(saveQcFilesData(payload));
        } else if (currentFileType === 'unknown') {
          dispatch(saveUnkFilesData(payload));
        }

        // Update areas and concentrations in Redux
        // Find the peak group index for this peak
        const findGroupKeyForPeak = (peakVal) => {
          if (peakVal == null) return null;
          const pv = Number(peakVal);
          const entries = Object.entries(allReportingPeaks || {});
          for (const [rpKey, list] of entries) {
            const arr = Array.isArray(list) ? list : [];
            if (arr.some((nom) => Math.abs(Number(nom) - pv) <= 1e-3)) return rpKey;
          }
          return null;
        };

        const originalGroupKey = findGroupKeyForPeak(Number(originalPeakKey));
        const groupKeys = Object.keys(allReportingPeaks || {});
        const originalGroupIndex = originalGroupKey ? groupKeys.indexOf(originalGroupKey) : -1;

        // Check if this peak was already split in this session
        // Walk through the chain to find the FIRST split (true original)
        const findOriginalSplit = (peakKey) => {
          const split = splitPeaksRef.current.find(
            s => s.fileName === fileName &&
              s.fileType === currentFileType &&
              s.originalPeakKey === peakKey
          );
          if (split) {
            // Found a previous split of this peak
            // Check if that split's original was also from a split

            // Prevent infinite recursion if the key hasn't changed (since find() returns the earliest split anyway)
            if (split.originalPeakKey === peakKey) {
              return split;
            }

            const evenEarlierSplit = findOriginalSplit(split.originalPeakKey);
            return evenEarlierSplit || split;
          }
          return null;
        };

        const previousSplit = findOriginalSplit(originalPeakKey);

        // Use the original data from the first split if available, otherwise use current peak data
        const trueOriginalPeakData = previousSplit ? previousSplit.originalPeakData : JSON.parse(JSON.stringify(originalPeak));
        const trueOriginalArea = previousSplit ? previousSplit.originalArea : (
          originalGroupIndex >= 0 ? (
            currentFileType === 'standard' ? stdAreasState?.[originalGroupIndex]?.[fileName] :
              currentFileType === 'qualityControl' ? qcAreasState?.[originalGroupIndex]?.[fileName] :
                unkAreasState?.[originalGroupIndex]?.[fileName]
          ) : null
        );

        console.log('Split: Processing peak', originalPeakKey, 'previousSplit found:', !!previousSplit);

        // Store split operation for discard functionality
        splitPeaksRef.current.push({
          fileName,
          fileType: currentFileType,
          rangeKey,
          originalPeakKey: originalSide === 'left' ? peak1Key : peak2Key,
          newPeakKey: originalSide === 'left' ? peak2Key : peak1Key,
          originalPeakData: trueOriginalPeakData, // Use the true original, not the current split version
          originalArea: trueOriginalArea,
          originalGroupIndex
        });

        if (originalGroupIndex >= 0) {
          // Update area for the original peak (whichever side user chose)
          const originalArea = originalSide === 'left' ? peak1Area : peak2Area;

          if (currentFileType === 'standard') {
            const currentStdAreas = stdAreasState?.[originalGroupIndex] || {};
            const updatedStdAreas = { ...currentStdAreas, [fileName]: originalArea };
            const next = { ...(stdAreasState || {}) };
            next[originalGroupIndex] = updatedStdAreas;
            dispatch(saveAreaUnderPeaks(next));

            // Recalculate calibration curve for standards
            try {
              const currentQcAreas = qcAreasState?.[originalGroupIndex];
              const currentUnkAreas = unkAreasState?.[originalGroupIndex];
              await recalculateStandardConcentrations(originalGroupIndex, next[originalGroupIndex], currentQcAreas, currentUnkAreas);
            } catch (error) {
              console.error('Error recalculating standard concentrations:', error);
            }
          } else if (currentFileType === 'qualityControl') {
            const next = (qcAreasState || []).map((m) => ({ ...(m || {}) }));
            while (next.length < groupKeys.length) next.push({});
            next[originalGroupIndex] = { ...(next[originalGroupIndex] || {}), [fileName]: originalArea };
            dispatch(saveAreaUnderPeaksQc(next));

            // Calculate concentration for QC
            const peakData = peaksObject?.[originalGroupIndex];
            if (peakData?.equation?.coefficients) {
              const coeffs = peakData.equation.coefficients;
              const concentration = coeffs.reduce((acc, coeff, index) =>
                acc + coeff * Math.pow(originalArea, coeffs.length - 1 - index), 0
              );
              dispatch(batchUpdatePeakConcentrations({
                peakIndex: originalGroupIndex,
                qcConcentrations: { [fileName]: concentration }
              }));
            }
          } else if (currentFileType === 'unknown') {
            const next = (unkAreasState || []).map((m) => ({ ...(m || {}) }));
            while (next.length < groupKeys.length) next.push({});
            next[originalGroupIndex] = { ...(next[originalGroupIndex] || {}), [fileName]: originalArea };
            dispatch(saveAreaUnderPeaksUnk(next));

            // Calculate concentration for Unknown
            const peakData = peaksObject?.[originalGroupIndex];
            if (peakData?.equation?.coefficients) {
              const coeffs = peakData.equation.coefficients;
              const concentration = coeffs.reduce((acc, coeff, index) =>
                acc + coeff * Math.pow(originalArea, coeffs.length - 1 - index), 0
              );
              dispatch(batchUpdatePeakConcentrations({
                peakIndex: originalGroupIndex,
                unkConcentrations: { [fileName]: concentration }
              }));
            }
          }
        }

        // For the new peak, we need to add it to peaksObject and areaUnderPeaks
        // This is similar to how Insert Peak handles newly created peaks
        const newPeakArea = originalSide === 'left' ? peak2Area : peak1Area;
        const newPeakKeyValue = originalSide === 'left' ? peak2Key : peak1Key;

        // Find or create a peak group for the new peak
        // For now, we'll create a new group entry in allReportingPeaks if it doesn't exist
        const newGroupKey = findGroupKeyForPeak(Number(newPeakKeyValue));
        let newGroupIndex = newGroupKey ? groupKeys.indexOf(newGroupKey) : -1;

        if (newGroupIndex < 0) {
          // New peak doesn't have a group yet - this is expected for split peaks
          // We'll need to add it to peaksObject and create area entries
          // Full integration with peaksObject would require backend processing
        } else {
          // Update area for the new peak if it has a group
          if (currentFileType === 'standard') {
            const currentStdAreas = stdAreasState?.[newGroupIndex] || {};
            const updatedStdAreas = { ...currentStdAreas, [fileName]: newPeakArea };
            const next = { ...(stdAreasState || {}) };
            next[newGroupIndex] = updatedStdAreas;
            dispatch(saveAreaUnderPeaks(next));
          } else if (currentFileType === 'qualityControl') {
            const next = (qcAreasState || []).map((m) => ({ ...(m || {}) }));
            while (next.length < groupKeys.length) next.push({});
            next[newGroupIndex] = { ...(next[newGroupIndex] || {}), [fileName]: newPeakArea };
            dispatch(saveAreaUnderPeaksQc(next));

            const peakData = peaksObject?.[newGroupIndex];
            if (peakData?.equation?.coefficients) {
              const coeffs = peakData.equation.coefficients;
              const concentration = coeffs.reduce((acc, coeff, index) =>
                acc + coeff * Math.pow(newPeakArea, coeffs.length - 1 - index), 0
              );
              dispatch(batchUpdatePeakConcentrations({
                peakIndex: newGroupIndex,
                qcConcentrations: { [fileName]: concentration }
              }));
            }
          } else if (currentFileType === 'unknown') {
            const next = (unkAreasState || []).map((m) => ({ ...(m || {}) }));
            while (next.length < groupKeys.length) next.push({});
            next[newGroupIndex] = { ...(next[newGroupIndex] || {}), [fileName]: newPeakArea };
            dispatch(saveAreaUnderPeaksUnk(next));

            const peakData = peaksObject?.[newGroupIndex];
            if (peakData?.equation?.coefficients) {
              const coeffs = peakData.equation.coefficients;
              const concentration = coeffs.reduce((acc, coeff, index) =>
                acc + coeff * Math.pow(newPeakArea, coeffs.length - 1 - index), 0
              );
              dispatch(batchUpdatePeakConcentrations({
                peakIndex: newGroupIndex,
                unkConcentrations: { [fileName]: concentration }
              }));
            }
          }
        }


        // Enable discard button
        try {
          window.dispatchEvent(new CustomEvent('split-peak-history-state', {
            detail: { canDiscard: true }
          }));
        } catch { }

        // Clean up visual elements
        cleanupSplitDrawingState();

      } catch (error) {
        console.error('Error processing peak split:', error);
        cleanupSplitDrawingState();
      } finally {
        // Force chart recreation after Redux updates complete (or attempt to)
        // This ensures the chart recreates even if previous recreation is still in progress
        setTimeout(() => {
          try {
            console.log('Split: Triggering chart recreation after Redux updates');
            // Reset loading state first, then trigger recreation
            setIsChartLoading(false);
            setTimeout(() => {
              setIsChartLoading(true);
              setChartRecreationCounter(prev => prev + 1);
            }, 10);
          } catch (error) {
            console.error('Error triggering chart recreation after split:', error);
          }
        }, 150);
      }
    };

    const clearSelection = () => {
      // Prevent selection changes during drag operations
      if (isDraggingRef.current) {
        // console.log('ChartPreview: clearSelection blocked during drag operation');
        return;
      }
      selectedPeakRef.current = null;
      updateSelectionStyles();
    };
    const selectNearestPeakAtX = (rangeKey, xVal) => {
      try {
        const r = currentFile?.result?.[rangeKey];
        const peaks = r?.peaks || {};
        let candidates = Object.entries(peaks)
          .map(([k, v]) => ({ k, apex: v?.x_peak }))
          .filter(o => Number.isFinite(o.apex));
        // If no explicit peaks exist (e.g., non-comp case), try to infer from reporting groups near x
        if (!candidates.length) {
          const entries = Object.entries(allReportingPeaks || {});
          candidates = entries.map(([k, v]) => ({ k, apex: Number.isFinite(Number(k)) ? Number(k) : null }))
            .filter(o => Number.isFinite(o.apex));
        }
        candidates.sort((a, b) => Math.abs(a.apex - xVal) - Math.abs(b.apex - xVal));
        if (candidates.length) selectPeak(rangeKey, candidates[0].k);
      } catch { }
    };
    const selectNearestPeakAtPointer = (rangeKey, point) => {
      try {
        const pt = chart.plotContainer.toLocal(point);
        const nd = pixelToData(pt.x, pt.y);
        selectNearestPeakAtX(rangeKey, nd.x);
      } catch { }
    };
    // pixelToData is defined below using robust positionFromPixel

    // Overview scrollbars for navigation
    // Horizontal mini-chart at bottom (minimap)
    const sbX = chart.set('scrollbarX', am5xy.XYChartScrollbar.new(root, {
      orientation: 'horizontal',
      height: 60,
    }));
    const sbxAxis = sbX.chart.xAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererX.new(root, { minorGridEnabled: true, minGridDistance: 70 }),
    }));
    const sbyAxis = sbX.chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) })
    );
    const sbSeries = sbX.chart.series.push(
      am5xy.LineSeries.new(root, { valueYField: 'y', valueXField: 'x', xAxis: sbxAxis, yAxis: sbyAxis })
    );
    sbSeries.setAll({ interactive: false, interpolationDuration: 0, sequencedInterpolation: false });
    sbSeries.data.setAll(props.data || []);

    // Simple vertical scrollbar (no minimap) at right
    const vScroll = am5.Scrollbar.new(root, { orientation: 'vertical', width: 10 });
    chart.set('scrollbarY', vScroll);
    vScrollRef.current = vScroll;
    // Bi-directional sync between yAxis and vertical scrollbar
    try {
      const syncScrollFromAxis = () => {
        if (ySyncingRef.current) return;
        try {
          ySyncingRef.current = true;
          let s = yAxis.get('start') ?? 0;
          let e = yAxis.get('end') ?? 1;
          // During drag, force scrollbar to the frozen positions to avoid any thumb movement
          if (isDraggingRef.current && yFreezeRef.current) {
            s = yFreezeRef.current.start;
            e = yFreezeRef.current.end;
          }
          const cs = vScroll.get('start') ?? 0;
          const ce = vScroll.get('end') ?? 1;
          if (Math.abs(cs - s) > Y_EPS) vScroll.set('start', s);
          if (Math.abs(ce - e) > Y_EPS) vScroll.set('end', e);
        } finally { ySyncingRef.current = false; }
      };
      const syncAxisFromScroll = () => {
        if (ySyncingRef.current) return;
        // During drag, completely ignore scrollbar changes to prevent axis movement
        if (isDraggingRef.current) return;
        try {
          ySyncingRef.current = true;
          const s = vScroll.get('start') ?? 0;
          const e = vScroll.get('end') ?? 1;
          const cs = yAxis.get('start') ?? 0;
          const ce = yAxis.get('end') ?? 1;
          if (Math.abs(cs - s) > Y_EPS) yAxis.set('start', s);
          if (Math.abs(ce - e) > Y_EPS) yAxis.set('end', e);
        } finally { ySyncingRef.current = false; }
      };
      const dA1 = yAxis.events.on('startchanged', syncScrollFromAxis);
      const dA2 = yAxis.events.on('endchanged', syncScrollFromAxis);
      const dS1 = vScroll.events.on('startchanged', syncAxisFromScroll);
      const dS2 = vScroll.events.on('endchanged', syncAxisFromScroll);
      // Ensure initial alignment
      syncScrollFromAxis();
      // Cleanup on dispose
      root.events.on('exitframe', () => { }); // placeholder to keep root referenced
      const oldDispose = root.dispose.bind(root);
      root.dispose = () => {
        try { dA1?.dispose?.(); } catch { }
        try { dA2?.dispose?.(); } catch { }
        try { dS1?.dispose?.(); } catch { }
        try { dS2?.dispose?.(); } catch { }
        oldDispose();
      };
    } catch { }

    // Linear interpolation helper shared across fill builders
    const interpY = (xv, xs, ys) => {
      try {
        const n = xs?.length || 0;
        if (!n) return null;
        if (xv <= xs[0]) return ys[0];
        if (xv >= xs[n - 1]) return ys[n - 1];
        let lo = 0, hi = n - 1;
        while (hi - lo > 1) {
          const mid = (lo + hi) >> 1;
          const xm = xs[mid];
          if (xm === xv) return ys[mid];
          if (xm < xv) lo = mid; else hi = mid;
        }
        const x0 = xs[lo], y0 = ys[lo];
        const x1 = xs[hi], y1 = ys[hi];
        const t = x1 === x0 ? 0 : (xv - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
      } catch { return null; }
    };

    // Create comp fills based on y_comp data (components) with enhanced peak-level data
    const createCompFills = (rangeKey, range) => {
      const hasComp = range.y_comp && Object.keys(range.y_comp).length > 0;
      if (!hasComp) {
        // console.log('🎨 No y_comp data found for range', rangeKey, '- skipping component fills');
        return;
      }

      // console.log('🎨 Creating component fills for range', rangeKey, 'with components:', Object.keys(range.y_comp));
      const map = new Map();
      const colorMap = new Map(); // Store colors for baseline matching
      let firstCompColor = null;

      // Create component fills for each component in y_comp
      Object.entries(range.y_comp).forEach(([compKey, compVals]) => {
        // Check if this peak is marked as hidden in this specific file
        const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
        const freshFileData = freshFile?.fileData || freshFile;

        // Skip peaks that are hidden in this file
        if (freshFileData?.hiddenPeaks && freshFileData.hiddenPeaks[compKey]) {
          return; // Don't create fill for hidden peaks
        }

        const color = getPeakColor(rangeKey, compKey, chart.get('colors').next());
        colorMap.set(compKey, color); // Store the color for later use

        // Prefer per-peak x_comp/baseline if available; fallback to range
        const perPeak = freshFileData?.result?.[rangeKey]?.peaks?.[compKey];

        // Use enhanced peak-level data when available, fallback to range-level
        const px = Array.isArray(perPeak?.x_comp) ? perPeak.x_comp : range.x;
        const pbase = Array.isArray(perPeak?.y_baseline) && perPeak.y_baseline.length === px.length
          ? perPeak.y_baseline
          : (Array.isArray(perPeak?.y_baseline) ? perPeak.y_baseline : range.y_baseline);

        // console.log('🎨 Creating component fill for', compKey, 'using x:', px?.length, 'y_comp:', compVals?.length, 'baseline:', pbase?.length);

        const compFillData = px.map((xVal, i) => {
          // If compVals is at range resolution, interpolate
          const y1 = (range.x && range.x.length === compVals.length)
            ? (compVals[range.x.indexOf(xVal)] ?? interpY(xVal, range.x, compVals))
            : compVals[i];
          return { x: xVal, y1, y2: pbase[i] };
        });
        const fill = chart.series.push(
          am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'x',
            valueYField: 'y1',
            openValueYField: 'y2',
            clustered: false,
            zIndex: 6, // above baseline area fill
            name: `Comp ${compKey}`,
            // Enable smooth animations for component fills
            interpolationDuration: 400,
            sequencedInterpolation: true,
            interpolationEasing: am5.ease.out(am5.ease.cubic)
          })
        );
        fill.setAll({ interactive: true, tooltip: null });
        if (!firstCompColor) firstCompColor = color;
        // Comp area opacity and interactivity
        fill.fills.template.setAll({ visible: true, fillOpacity: 0.25, fill: color, interactive: true, cursorOverStyle: 'pointer' });
        fill.strokes.template.setAll({ strokeOpacity: 0, strokeWidth: 0, interactive: false });
        fill.data.setAll(compFillData);
        // Click to select this peak (attach to the filled area surface)
        try {
          // Toggle selection on area click: if this comp is already selected, deselect; otherwise select
          fill.fills?.template?.events?.on?.('pointerdown', (e) => {
            // Prevent peak selection changes during drag operations
            if (isDraggingRef.current) {
              e.stopPropagation?.();
              return;
            }

            // If in split mode, allow the event to bubble to the main chart handler
            if (splittingPeakRef.current) {
              // Don't intercept - let the chart's pointerdown handler process it
              return;
            }

            // If in delete mode, hide peak visibility (but only if not already hidden)
            if (deletingRef.current) {
              // Check if peak is already hidden
              const fileData = currentFile?.fileData || currentFile;
              const hiddenPeaks = fileData?.hiddenPeaks || {};

              const deleteMode = deletePeakModeRef.current; // 'single' or 'convoluted'

              if (deleteMode === 'single') {
                // Delete only the clicked peak
                if (!hiddenPeaks[compKey]) {
                  // Only hide if not already hidden
                  dispatch(togglePeakVisibility({
                    fileName: fileName,
                    fileType: currentFileType,
                    peakKey: compKey
                  }));

                  // Set concentration to "none" for this deleted peak
                  const peakIndex = resolvePeakIndex(compKey);
                  if (peakIndex >= 0) {
                    dispatch(updatePeakConcentration({
                      peakIndex,
                      fileType: currentFileType,
                      fileName: fileName,
                      value: 'none'
                    }));
                  }

                  // Emit history state to enable discard button
                  try {
                    window.dispatchEvent(new CustomEvent('delete-peak-history-state', {
                      detail: { canDiscard: true }
                    }));
                  } catch { }
                }
              } else if (deleteMode === 'convoluted') {
                // Delete all peaks in this range (convoluted peaks)
                // Get all peaks in the same range
                const rangeData = computedRange[rangeKey];
                if (rangeData?.y_comp) {
                  const peaksInRange = Object.keys(rangeData.y_comp);
                  let anyDeleted = false;

                  peaksInRange.forEach((peakKey) => {
                    if (!hiddenPeaks[peakKey]) {
                      // Hide this peak
                      dispatch(togglePeakVisibility({
                        fileName: fileName,
                        fileType: currentFileType,
                        peakKey: peakKey
                      }));

                      // Set concentration to "none" for this deleted peak
                      const peakIndex = resolvePeakIndex(peakKey);
                      if (peakIndex >= 0) {
                        dispatch(updatePeakConcentration({
                          peakIndex,
                          fileType: currentFileType,
                          fileName: fileName,
                          value: 'none'
                        }));
                      }

                      anyDeleted = true;
                    }
                  });

                  // Emit history state to enable discard button if any peaks were deleted
                  if (anyDeleted) {
                    try {
                      window.dispatchEvent(new CustomEvent('delete-peak-history-state', {
                        detail: { canDiscard: true }
                      }));
                    } catch { }
                  }
                }
              }

              e.stopPropagation?.();
              return;
            }

            // Check if we're close to a baseline - if so, prioritize baseline dragging over peak selection
            const pt = chart.plotContainer.toLocal(e.point);
            let nearBaseline = false;

            // Check proximity to any baseline in edit mode
            if (editingRef.current) {
              baselineByRangeRef.current.forEach((layer, rk) => {
                const { p1, p2 } = layer;
                if (!p1 || !p2) return;
                const p1px = dataToPixel(p1.x, p1.y);
                const p2px = dataToPixel(p2.x, p2.y);
                const proj = projectToSeg(pt.x, pt.y, p1px, p2px);
                // Use same LINE_HIT_PX threshold as baseline drag detection
                if (proj.d <= 8) { // LINE_HIT_PX = 8
                  nearBaseline = true;
                }
              });
            }

            // If we're near a baseline in edit mode, don't select the peak - let baseline drag happen
            if (nearBaseline) {
              // Don't stop propagation - let the baseline drag handler take over
              return;
            }

            const sel = selectedPeakRef.current;
            if (sel && sel.rangeKey === rangeKey && sel.peakKey === compKey) {
              clearSelection();
            } else {
              selectPeak(rangeKey, compKey);
            }
            e.stopPropagation?.();
          });
          // Fallback: only select if not already selected to avoid re-select after a deselect on pointerdown
          fill.events.on('pointerup', (e) => {
            // Prevent peak selection changes during drag operations
            if (isDraggingRef.current) {
              e.stopPropagation?.();
              return;
            }

            // Check if we're close to a baseline - if so, prioritize baseline operations over peak selection
            const pt = chart.plotContainer.toLocal(e.point);
            let nearBaseline = false;

            if (editingRef.current) {
              baselineByRangeRef.current.forEach((layer, rk) => {
                const { p1, p2 } = layer;
                if (!p1 || !p2) return;
                const p1px = dataToPixel(p1.x, p1.y);
                const p2px = dataToPixel(p2.x, p2.y);
                const proj = projectToSeg(pt.x, pt.y, p1px, p2px);
                if (proj.d <= 8) { // LINE_HIT_PX = 8
                  nearBaseline = true;
                }
              });
            }

            // If we're near a baseline in edit mode, don't select the peak
            if (nearBaseline) {
              return;
            }

            const sel = selectedPeakRef.current;
            if (!(sel && sel.rangeKey === rangeKey && sel.peakKey === compKey)) {
              selectPeak(rangeKey, compKey);
            }
            e.stopPropagation?.();
          });
        } catch { }
        map.set(compKey, fill);
      });
      if (firstCompColor) baselineColorByRangeRef.current.set(rangeKey, firstCompColor);
      compFillsByRangeRef.current.set(rangeKey, map);
      // Store color mapping for baseline/handle color matching
      compColorsByRangeRef.current.set(rangeKey, colorMap);
    };

    // Click in the series-baseline band to select the nearest peak (non-comp ranges only)
    chart.plotContainer.events.on('pointerdown', (e) => {
      try {
        // Prevent peak selection changes during drag operations
        if (isDraggingRef.current) {
          e.stopPropagation?.();
          return;
        }

        // Handle Insert Peak mode - start drawing baseline only if clicking on chromatogram
        if (insertingPeakRef.current) {
          const pt = chart.plotContainer.toLocal(e.point);
          const startData = pixelToData(pt.x, pt.y);
          if (!startData) return;

          // Check if click is near the chromatogram line using pixel distance
          const seriesY = getSeriesYAtX(startData.x);
          if (seriesY == null) return;

          // Use pixel-based threshold for consistent behavior with pointermove
          const seriesPixel = dataToPixel(startData.x, seriesY);
          const pixelDistance = Math.abs(pt.y - seriesPixel.y);
          const threshold = 10; // 10 pixels - same as in pointermove

          // Check if clicked position is close to the series
          if (pixelDistance > threshold) {
            // Click is not on the chromatogram line, allow normal panning
            return; // Let the chart handle panning
          }

          // Check if clicking in an area with a visible peak - don't allow inserting there
          const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
          const freshFileData = freshFile?.fileData || freshFile;
          const hiddenPeaks = freshFileData?.hiddenPeaks || {};
          const allRanges = freshFileData?.result || {};

          // Check if this X position overlaps with any visible peak
          let isInVisiblePeak = false;
          for (const rangeKey of Object.keys(allRanges)) {
            const rangeData = allRanges[rangeKey];
            const allPeaks = rangeData?.peaks || {};

            for (const pk of Object.keys(allPeaks)) {
              if (!hiddenPeaks[pk]) {
                const peak = allPeaks[pk];
                const peakX = peak?.originalXComp || peak?.x_comp || peak?.x || [];
                if (peakX.length > 0) {
                  const peakMinX = Math.min(...peakX);
                  const peakMaxX = Math.max(...peakX);
                  if (startData.x >= peakMinX && startData.x <= peakMaxX) {
                    isInVisiblePeak = true;
                    break;
                  }
                }
              }
            }
            if (isInVisiblePeak) break;
          }

          // Don't allow drawing baseline in areas with visible peaks
          if (isInVisiblePeak) {
            return;
          }

          // Click is on/near the chromatogram - start drawing baseline
          // User can click anywhere along X, but Y is constrained to be at or below chromatogram
          // Keep the clicked X position, but clamp Y to not exceed chromatogram
          if (startData.y > seriesY) {
            startData.y = seriesY;
          }

          e.stopPropagation?.();

          // For Insert Peak mode, allow drawing anywhere on the chromatogram
          // Find which range this x belongs to, or use the first available range
          let targetRangeKey = null;
          const fileData = currentFile?.fileData || currentFile;
          const result = fileData?.result || {};

          // First, try to find a range that contains this X position
          for (const [rKey, rData] of Object.entries(result)) {
            if (!rData?.x || !rData.x.length) continue;
            const xMin = Math.min(...rData.x);
            const xMax = Math.max(...rData.x);
            if (startData.x >= xMin && startData.x <= xMax) {
              targetRangeKey = rKey;
              break;
            }
          }

          // If no range contains this X, find the closest range or use the first available
          if (!targetRangeKey) {
            const ranges = Object.entries(result).filter(([_, rData]) => rData?.x && rData.x.length);
            if (ranges.length > 0) {
              // Find the closest range by checking which range's X bounds are nearest
              let closestRange = ranges[0][0];
              let minDistance = Infinity;

              for (const [rKey, rData] of ranges) {
                const xMin = Math.min(...rData.x);
                const xMax = Math.max(...rData.x);
                const distance = startData.x < xMin ? xMin - startData.x :
                  startData.x > xMax ? startData.x - xMax : 0;

                if (distance < minDistance) {
                  minDistance = distance;
                  closestRange = rKey;
                }
              }
              targetRangeKey = closestRange;
            }
          }

          if (!targetRangeKey) return; // No ranges available at all

          // Create temporary visual elements for drawing feedback
          const tempLine = chart.series.push(
            am5xy.LineSeries.new(root, {
              valueXField: 'x',
              valueYField: 'y',
              xAxis,
              yAxis,
              stroke: am5.color(0x00ff00),
              strokeWidth: 2,
              strokeDasharray: [5, 5]
            })
          );
          tempLine.data.setAll([{ x: startData.x, y: startData.y }]);

          // Store drawing state
          drawingBaselineRef.current = {
            rangeKey: targetRangeKey,
            startPoint: startData,
            tempLine,
            currentPoint: startData
          };

          return;
        }

        // Handle Split Peak mode - clicking finalizes split at current hover position
        if (splittingPeakRef.current && drawingSplitLineRef.current) {
          // Don't process click if assignment UI or triangles are already active
          if (assignmentLabelsRef.current && assignmentLabelsRef.current.length > 0) {
            return;
          }
          if (peakSelectionTrianglesRef.current && peakSelectionTrianglesRef.current.length > 0) {
            return;
          }

          const drawState = drawingSplitLineRef.current;
          const splitX = drawState.splitX;
          const rangeKey = drawState.rangeKey;
          const peakKey = drawState.peakKey;

          e.stopPropagation?.();

          // Check if this is a multi-peak scenario (ambiguous case)
          const multiPeakData = selectedSplitPeakRef.current;
          if (multiPeakData && multiPeakData.peaksAtX && multiPeakData.peaksAtX.length > 1) {
            // AMBIGUOUS: Multiple peaks overlap at this position
            // Show triangle selection UI

            // Make the preview line solid and store it
            try {
              drawState.tempLine.set('strokeDasharray', []);
              drawState.tempLine.set('strokeWidth', 3);
              completedSplitLinesRef.current.push(drawState.tempLine);
            } catch { }

            // Clear drawingSplitLineRef to prevent hover updates
            drawingSplitLineRef.current = null;

            // Show triangles for peak selection
            showPeakSelectionTriangles(multiPeakData.peaksAtX, splitX);

            return;
          }

          // UNAMBIGUOUS CASE: Single peak or user already selected via triangle
          // Auto-select based on which side has higher max Y

          // Make the preview line solid and store it
          try {
            drawState.tempLine.set('strokeDasharray', []);
            drawState.tempLine.set('strokeWidth', 3);
            completedSplitLinesRef.current.push(drawState.tempLine);
          } catch { }

          // Clear drawingSplitLineRef to prevent hover updates
          drawingSplitLineRef.current = null;

          // Get peak data to determine which side has higher max
          const fileData = currentFile?.fileData || currentFile;
          const rangeData = fileData?.result?.[rangeKey];
          const peak = rangeData?.peaks?.[peakKey];
          const peakX = peak?.x_comp || peak?.x || [];
          const peakY = peak?.y_comp || peak?.y || [];

          if (peakX.length === 0 || peakY.length === 0) {
            console.error('Invalid peak data for auto-selection');
            return;
          }

          // Split data into left and right sides
          const leftIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x < splitX);
          const rightIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x >= splitX);

          if (leftIndices.length === 0 || rightIndices.length === 0) {
            console.error('Split line does not properly divide the peak');
            cleanupSplitDrawingState();
            return;
          }

          // Find max Y on each side
          const leftMax = Math.max(...leftIndices.map(p => p.y));
          const rightMax = Math.max(...rightIndices.map(p => p.y));

          // Auto-select: The side with higher max Y is the "original" peak
          const originalSide = leftMax > rightMax ? 'left' : 'right';

          // Process split immediately with auto-selected side
          processPeakSplit(rangeKey, peakKey, splitX, originalSide);

          return;
        }

        // If in split mode but didn't find a valid peak to split, just return
        if (splittingPeakRef.current) {
          return;
        }

        const pt = chart.plotContainer.toLocal(e.point);
        const nd = pixelToData(pt.x, pt.y);
        let best = null; // { rangeKey, delta }
        baselineByRangeRef.current.forEach((layer, rk) => {
          // If comps exist in this range, do not allow selection via baseline area
          const cm = compFillsByRangeRef.current.get(rk);
          if (cm && cm.size > 0) return;
          const p1 = layer?.p1, p2 = layer?.p2;
          if (!p1 || !p2) return;
          const x = nd.x;
          const xMin = Math.min(p1.x, p2.x);
          const xMax = Math.max(p1.x, p2.x);
          if (x < xMin || x > xMax) return;
          const yS = getSeriesYAtX(x);
          if (yS == null) return;
          const yB = yOnLine(x, p1, p2);
          // Consider only regions where the series is above baseline (true peak area)
          if (yS <= yB) return;
          const y = nd.y;
          // Check if clicked y lies inside the area band between baseline and series
          if (y >= Math.min(yB, yS) && y <= Math.max(yB, yS)) {
            const delta = Math.abs(yS - yB);
            if (!best || delta > best.delta) best = { rangeKey: rk, delta };
          }
        });
        if (best) {
          // Determine which peak would be selected at this x so we can toggle if already selected
          const getNearestPeakKeyAtX = (rk, xVal) => {
            try {
              const r = currentFile?.result?.[rk];
              const peaks = r?.peaks || {};
              let candidates = Object.entries(peaks)
                .map(([k, v]) => ({ k, apex: v?.x_peak }))
                .filter(o => Number.isFinite(o.apex));
              if (!candidates.length) {
                const entries = Object.entries(allReportingPeaks || {});
                candidates = entries
                  .map(([k]) => ({ k, apex: Number.isFinite(Number(k)) ? Number(k) : null }))
                  .filter(o => Number.isFinite(o.apex));
              }
              candidates.sort((a, b) => Math.abs(a.apex - xVal) - Math.abs(b.apex - xVal));
              return candidates.length ? candidates[0].k : null;
            } catch { return null; }
          };
          const nearestKey = getNearestPeakKeyAtX(best.rangeKey, nd.x);

          // If in delete mode, hide peak visibility (but only if not already hidden)
          if (deletingRef.current && nearestKey) {
            // Check if peak is already hidden
            const fileData = currentFile?.fileData || currentFile;
            const hiddenPeaks = fileData?.hiddenPeaks || {};

            if (!hiddenPeaks[nearestKey]) {
              // Only hide if not already hidden
              dispatch(togglePeakVisibility({
                fileName: fileName,
                fileType: currentFileType,
                peakKey: nearestKey
              }));

              // Set concentration to "none" for this deleted peak
              const peakIndex = resolvePeakIndex(nearestKey);
              if (peakIndex >= 0) {
                dispatch(updatePeakConcentration({
                  peakIndex,
                  fileType: currentFileType,
                  fileName: fileName,
                  value: 'none'
                }));
              }

              // Emit history state to enable discard button
              // We know there are changes since we just hid a peak
              try {
                window.dispatchEvent(new CustomEvent('delete-peak-history-state', {
                  detail: { canDiscard: true }
                }));
              } catch { }
            }
            e.stopPropagation?.();
            return;
          }

          const sel = selectedPeakRef.current;
          const isSame = sel && sel.rangeKey === best.rangeKey && sel.peakKey === nearestKey && !!nearestKey;
          if (isSame) {
            clearSelection();
          } else {
            if (nearestKey) selectPeak(best.rangeKey, nearestKey); else selectNearestPeakAtX(best.rangeKey, nd.x);
            if (editingRef.current) {
              // When editing and we actually selected, consume the event to avoid starting a pan
              activeRangeRef.current = best.rangeKey;
              updateSelectionStyles();
              e.stopPropagation?.();
            }
          }
        }
      } catch { }
    });

    // Add one interactive baseline per range
    const addBaselineForRange = (key, range) => {
      const persisted = lastEndpointsRef.current.get(key);
      const p1 = persisted ? { ...persisted.p1 } : { x: range.x[0], y: range.y_baseline[0] };
      const p2 = persisted ? { ...persisted.p2 } : { x: range.x[range.x.length - 1], y: range.y_baseline[range.y_baseline.length - 1] };

      const presetColor = baselineColorByRangeRef.current.get(key);
      // Derive a single-peak id: try selectedPeak or nearest reporting peak; fallback to rangeKey itself
      let singlePeakId = null;
      try {
        const r = currentFile?.result?.[key];
        const peaks = r?.peaks || {};
        const firstKey = Object.keys(peaks)[0];
        singlePeakId = firstKey || 'range';
      } catch { }

      // Check if ALL peaks in this range are hidden in this file
      // If all peaks are hidden, don't render anything
      const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
      const freshFileData = freshFile?.fileData || freshFile;
      const hiddenPeaks = freshFileData?.hiddenPeaks || {};

      const hasComp = range.y_comp && Object.keys(range.y_comp).length > 0;
      if (hasComp) {
        // For ranges with y_comp data, check if all peaks are hidden in this file
        const allPeaksHidden = Object.keys(range.y_comp).every(compKey => {
          return hiddenPeaks[compKey] === true;
        });

        if (allPeaksHidden) {
          // Create placeholder layer in baselineByRangeRef to maintain structure
          baselineByRangeRef.current.set(key, { p1, p2 });
          return; // Don't create visual elements if all peaks are hidden
        }
      } else if (singlePeakId) {
        // For non-comp ranges, check if the single peak is hidden in this file
        if (hiddenPeaks[singlePeakId]) {
          // Create placeholder layer in baselineByRangeRef to maintain structure
          baselineByRangeRef.current.set(key, { p1, p2 });
          return; // Don't create visual elements for hidden peaks
        }
      }

      const color = presetColor || getPeakColor(key, singlePeakId || 'range', chart.get('colors').next());
      // Fill between series and baseline
      const buildFill = (P1, P2) => {
        const xMin = Math.min(P1.x, P2.x);
        const xMax = Math.max(P1.x, P2.x);
        const all = Array.isArray(props.data) ? props.data : [];
        const inRange = all.filter((d) => d.x >= xMin && d.x <= xMax);
        const ep1 = { x: P1.x, y1: nearestSeriesPointToPixel(dataToPixel(P1.x, P1.y).x, dataToPixel(P1.x, P1.y).y)?.y || P1.y, y2: yOnLine(P1.x, P1, P2) };
        const ep2 = { x: P2.x, y1: nearestSeriesPointToPixel(dataToPixel(P2.x, P2.y).x, dataToPixel(P2.x, P2.y).y)?.y || P2.y, y2: yOnLine(P2.x, P1, P2) };
        const arr = [...inRange.map((d) => ({ x: d.x, y1: d.y, y2: yOnLine(d.x, P1, P2) })), ep1, ep2];
        const byX = new Map();
        arr.forEach((d) => byX.set(d.x, d));
        return Array.from(byX.values()).sort((a, b) => a.x - b.x);
      };
      const fill = chart.series.push(
        am5xy.LineSeries.new(root, {
          xAxis,
          yAxis,
          valueXField: 'x',
          valueYField: 'y1',
          openValueYField: 'y2',
          zIndex: 2,
          // Enable smooth animations for baseline fills
          interpolationDuration: 400,
          sequencedInterpolation: true,
          interpolationEasing: am5.ease.out(am5.ease.cubic)
        })
      );
      // Disable interactivity so comp fill above receives pointer events
      fill.setAll({ interactive: false, tooltip: null });
      // Match comp area opacity so perceived color is identical; but don't intercept events
      fill.fills.template.setAll({ visible: true, fillOpacity: 0.25, fill: color, interactive: false, forceInactive: true, hitTest: false });
      fill.strokes.template.setAll({ strokeOpacity: 0, strokeWidth: 0, interactive: false, forceInactive: true, hitTest: false });
      fill.data.setAll(buildFill(p1, p2));
      // Range baseline fills are now primarily for fallback when no component fills exist
      // The visibility will be determined after component fills are created
      // console.log('🎨 Setting up range-level baseline fill for range', key, '- visibility will be determined after component fill creation');

      // Create per-peak baseline lines for comps: identical P1/P2, colored to match each comp
      const perPeakLines = new Map();
      const compMap = compFillsByRangeRef.current.get(key) || new Map();
      const colorMap = compColorsByRangeRef.current.get(key) || new Map();
      let controlLine = null;
      if (compMap.size > 0) {
        compMap.forEach((fillSeries, compKey) => {
          // Try multiple methods to get the correct color
          let compColor = colorMap.get(compKey) || getPeakColor(key, compKey, color);
          if (!compColor) {
            // Fallback: try to get color from the fill series
            try {
              compColor = fillSeries.fills.template.get('fill');
            } catch { }
          }
          if (!compColor) {
            // Fallback: try to get from stroke
            try {
              compColor = fillSeries.strokes.template.get('stroke');
            } catch { }
          }
          if (!compColor) {
            // Final fallback: use default color
            compColor = color;
          }
          const ln = chart.series.push(
            am5xy.LineSeries.new(root, {
              xAxis,
              yAxis,
              valueXField: 'x',
              valueYField: 'y',
              zIndex: 15,
              // Enable smooth animations for baseline lines
              interpolationDuration: 350,
              sequencedInterpolation: true,
              interpolationEasing: am5.ease.out(am5.ease.cubic)
            })
          );
          ln.setAll({ interactive: false });
          ln.strokes.template.setAll({ stroke: compColor, strokeWidth: 2, strokeOpacity: 1, interactive: false });
          // Initialize endpoints from this comp's own x_comp/y_baseline if present; otherwise fallback
          const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
          const freshFileData = freshFile?.fileData || freshFile;
          const perPeak = freshFileData?.result?.[key]?.peaks?.[compKey];
          const px = Array.isArray(perPeak?.x_comp) && perPeak.x_comp.length >= 2 ? perPeak.x_comp : null;
          const pbase = Array.isArray(perPeak?.y_baseline) ? perPeak.y_baseline : null;
          if (px && pbase && pbase.length === px.length) {
            const pp1 = { x: px[0], y: pbase[0] };
            const pp2 = { x: px[px.length - 1], y: pbase[pbase.length - 1] };
            ln.data.setAll([pp1, pp2]);
          } else if (Array.isArray(pbase) && pbase.length === range.x.length && range.x.length >= 2) {
            // Legacy fallback: y_baseline aligned to range.x
            const pp1 = { x: range.x[0], y: pbase[0] };
            const pp2 = { x: range.x[range.x.length - 1], y: pbase[pbase.length - 1] };
            ln.data.setAll([pp1, pp2]);
          } else {
            ln.data.setAll([p1, p2]);
          }
          perPeakLines.set(compKey, ln);
          if (!controlLine) controlLine = ln;
        });
        // Ensure each comp area fill color matches its baseline line stroke color
        try {
          perPeakLines.forEach((ln, cKey) => {
            const strokeCol = ln?.strokes?.template?.get?.('stroke') || colorMap.get(cKey) || color;
            const fs = compMap.get(cKey);
            try { fs?.fills?.template?.setAll?.({ fill: strokeCol }); } catch { }
          });
        } catch { }
      } else {
        // Fallback: single baseline line if no comps defined
        controlLine = chart.series.push(
          am5xy.LineSeries.new(root, {
            xAxis,
            yAxis,
            valueXField: 'x',
            valueYField: 'y',
            zIndex: 15,
            // Enable smooth animations for control line
            interpolationDuration: 350,
            sequencedInterpolation: true,
            interpolationEasing: am5.ease.out(am5.ease.cubic)
          })
        );
        controlLine.setAll({ interactive: false });
        controlLine.strokes.template.setAll({ stroke: color, strokeWidth: 2, strokeOpacity: 1, interactive: false });
        controlLine.data.setAll([p1, p2]);
        // Ensure single-peak area fill uses the same color as the baseline line
        try {
          const lineStroke = controlLine?.strokes?.template?.get?.('stroke') || color;
          fill?.fills?.template?.setAll?.({ fill: lineStroke });
        } catch { }
        // Re-sync once after initial data set
        try { if (compMap.size === 0) { const s = controlLine?.strokes?.template?.get?.('stroke') || color; fill?.fills?.template?.setAll?.({ fill: s }); } } catch { }
      }
      const line = controlLine;
      // Helper: keep single-peak (non-comp) area fill color in sync with its baseline line stroke
      const syncSingleFillToLine = () => {
        try {
          if (compMap.size === 0) {
            const strokeCol = line?.strokes?.template?.get?.('stroke') || color;
            fill?.fills?.template?.setAll?.({ fill: strokeCol });
          }
        } catch { }
      };
      const getDI1 = () => line?.dataItems?.[0];
      const getDI2 = () => line?.dataItems?.[1];

      // Helpers for comp fill rebuild
      const trimXBetween = (srcX, P1, P2) => {
        const m1 = Math.min(P1.x, P2.x);
        const m2 = Math.max(P1.x, P2.x);
        const arr = (Array.isArray(srcX) ? srcX : []).filter((xx) => xx >= m1 && xx <= m2);

        // Only add baseline endpoints if they're within the original data range
        // This prevents drawing fills beyond the chromatogram data
        if (srcX.length > 0) {
          const dataMin = Math.min(...srcX);
          const dataMax = Math.max(...srcX);

          // Only add m1 if it's within or at the data range boundary
          if ((arr.length === 0 || arr[0] > m1) && m1 >= dataMin && m1 <= dataMax) {
            arr.unshift(m1);
          }

          // Only add m2 if it's within or at the data range boundary
          if (arr.length > 0 && arr[arr.length - 1] < m2 && m2 >= dataMin && m2 <= dataMax) {
            arr.push(m2);
          }
        }

        return Array.from(new Set(arr)).sort((a, b) => a - b);
      };
      const interpY = (xv, xs, ys) => {
        const n = xs.length;
        if (!n) return null;
        if (xv <= xs[0]) return ys[0];
        if (xv >= xs[n - 1]) return ys[n - 1];
        let lo = 0, hi = n - 1;
        while (hi - lo > 1) {
          const mid = (lo + hi) >> 1;
          const xm = xs[mid];
          if (xm === xv) return ys[mid];
          if (xm < xv) lo = mid; else hi = mid;
        }
        const x0 = xs[lo], y0 = ys[lo];
        const x1 = xs[hi], y1 = ys[hi];
        const t = x1 === x0 ? 0 : (xv - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
      };
      // Rebuild the selected comp's fill using current endpoints and trimmed x
      const buildCompFillData = (compKey, P1, P2) => {
        try {
          // Use main chromatogram data for X coordinates to show full baseline range
          const mainChromatogram = Array.isArray(props.data) ? props.data : [];
          const chromatogramX = mainChromatogram.map(d => d.x);
          const chromatogramY = mainChromatogram.map(d => d.y);

          if (chromatogramX.length === 0) {
            return [];
          }

          // Get all chromatogram points within the baseline range
          const m1 = Math.min(P1.x, P2.x);
          const m2 = Math.max(P1.x, P2.x);
          const xs = chromatogramX.filter(x => x >= m1 && x <= m2).sort((a, b) => a - b);

          if (xs.length === 0) {
            return [];
          }

          // Get hidden peaks info to check for overlaps
          const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
          const freshFileData = freshFile?.fileData || freshFile;
          const hiddenPeaks = freshFileData?.hiddenPeaks || {};

          // Build array of visible (not hidden) peak ranges to exclude
          // Check ALL peaks in ALL ranges (not just current range)
          const visiblePeakRanges = [];
          const allRanges = freshFileData?.result || {};

          Object.keys(allRanges).forEach(rangeKey => {
            const rangeData = allRanges[rangeKey];
            const allPeaks = rangeData?.peaks || {};

            Object.keys(allPeaks).forEach(pk => {
              // Skip the current peak being edited
              if (pk === compKey && rangeKey === key) return;

              // Only consider visible peaks (not hidden)
              if (!hiddenPeaks[pk]) {
                const peak = allPeaks[pk];
                const peakX = peak?.originalXComp || peak?.x_comp || peak?.x || [];
                if (peakX.length > 0) {
                  const peakMinX = Math.min(...peakX);
                  const peakMaxX = Math.max(...peakX);
                  // Check if this peak overlaps with our baseline range
                  if (peakMaxX >= m1 && peakMinX <= m2) {
                    visiblePeakRanges.push({ minX: peakMinX, maxX: peakMaxX });
                  }
                }
              }
            });
          });

          // Build fill data, excluding areas covered by visible peaks
          const out = [];
          for (let i = 0; i < xs.length; i++) {
            const xv = xs[i];

            // Check if this X value falls within any visible peak range
            const isInVisiblePeak = visiblePeakRanges.some(range => xv >= range.minX && xv <= range.maxX);

            if (isInVisiblePeak) {
              // For visible peaks, use baseline for both y1 and y2 (no fill area)
              out.push({ x: xv, y1: yOnLine(xv, P1, P2), y2: yOnLine(xv, P1, P2) });
            } else {
              // For areas without visible peaks (raw chromatogram or hidden peaks), show normal fill
              const y1 = interpY(xv, chromatogramX, chromatogramY);
              out.push({ x: xv, y1: y1, y2: yOnLine(xv, P1, P2) });
            }
          }
          return out;
        } catch { return []; }
      };

      // Determine the Y limit (max allowed baseline y) at a given x
      // Always use the main chromatogram data for Y limit
      const getYLimitAtX = (xv) => {
        try {
          // Use main chromatogram data
          const mainChromatogram = Array.isArray(props.data) ? props.data : [];
          const chromatogramX = mainChromatogram.map(d => d.x);
          const chromatogramY = mainChromatogram.map(d => d.y);

          if (chromatogramX.length > 0 && chromatogramY.length > 0) {
            const yc = interpY(xv, chromatogramX, chromatogramY);
            if (Number.isFinite(yc)) return yc;
          }

          return getSeriesYAtX(xv);
        } catch { return getSeriesYAtX(xv); }
      };

      // Get the apex x (x_peak) for the current selected peak in this range
      const getApexX = () => {
        try {
          const sel = selectedPeakRef.current;
          if (!sel || sel.rangeKey !== key) return null;
          const r = currentFile?.result?.[key];
          const pk = sel.peakKey;
          const xp = r?.peaks?.[pk]?.x_peak;
          if (Number.isFinite(Number(xp))) return Number(xp);
          // Fallback: try numeric interpretation of peak key (e.g., reporting group key)
          const fv = Number(pk);
          return Number.isFinite(fv) ? fv : null;
        } catch { return null; }
      };

      // Get X boundaries imposed by visible (not hidden) peaks
      // Returns { minX, maxX } indicating the allowed range for baseline endpoints
      const getVisiblePeakBoundaries = (currentPeakKey) => {
        try {
          const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
          const freshFileData = freshFile?.fileData || freshFile;
          const hiddenPeaks = freshFileData?.hiddenPeaks || {};
          const allRanges = freshFileData?.result || {};

          // Collect all visible peak X ranges
          const visiblePeakRanges = [];
          Object.keys(allRanges).forEach(rangeKey => {
            const rangeData = allRanges[rangeKey];
            const allPeaks = rangeData?.peaks || {};

            Object.keys(allPeaks).forEach(pk => {
              // Skip the current peak being edited
              if (pk === currentPeakKey && rangeKey === key) return;

              // Only consider visible peaks (not hidden)
              if (!hiddenPeaks[pk]) {
                const peak = allPeaks[pk];
                const peakX = peak?.originalXComp || peak?.x_comp || peak?.x || [];
                if (peakX.length > 0) {
                  const peakMinX = Math.min(...peakX);
                  const peakMaxX = Math.max(...peakX);
                  visiblePeakRanges.push({ minX: peakMinX, maxX: peakMaxX });
                }
              }
            });
          });

          return visiblePeakRanges;
        } catch {
          return [];
        }
      };

      // Ensure p1/p2 reflect the selected comp's line when comps exist
      const syncEndpointsToSelectedComp = () => {
        try {
          if (perPeakLines.size === 0) return;
          const sel = selectedPeakRef.current;
          if (!sel || sel.rangeKey !== key) return;
          const ln = perPeakLines.get(sel.peakKey);
          if (!ln) return;
          const di1 = ln.dataItems?.[0];
          const di2 = ln.dataItems?.[1];
          if (di1 && di2) {
            const nx1 = di1.get('valueX');
            const ny1 = di1.get('valueY');
            const nx2 = di2.get('valueX');
            const ny2 = di2.get('valueY');
            if (Number.isFinite(nx1)) p1.x = nx1;
            if (Number.isFinite(ny1)) p1.y = ny1;
            if (Number.isFinite(nx2)) p2.x = nx2;
            if (Number.isFinite(ny2)) p2.y = ny2;
            if (typeof updateHandlesFn === 'function') updateHandlesFn();
          }
        } catch { }
      };

      // Endpoint handles
      let updateHandlesFn = null;
      let handle1 = null,
        handle2 = null;
      const perPeakHandles = new Map(); // compKey -> { h1, h2 }
      if (compMap.size > 0) {
        // One pair per comp
        perPeakLines.forEach((ln, compKey) => {
          // Get color using same logic as baseline creation
          let compColor = colorMap.get(compKey);
          if (!compColor) {
            const fillSeries = compMap.get(compKey);
            try {
              compColor = fillSeries.fills.template.get('fill');
            } catch { }
          }
          if (!compColor) {
            try {
              compColor = ln?.strokes?.template?.get?.('stroke');
            } catch { }
          }
          if (!compColor) {
            compColor = color;
          }
          const strokeCol = compColor;
          const h1 = chart.plotContainer.children.push(
            am5.Circle.new(root, {
              radius: 8,
              fill: strokeCol,
              stroke: strokeCol,
              strokeWidth: 2,
              fillOpacity: 1,
              strokeOpacity: 1,
              isMeasured: true,
              visible: false,
              opacity: 0,
              interactive: true,
              zIndex: 1000,
              cursorOverStyle: 'pointer',
            })
          );

          // Add hover effects to component handle h1
          h1.states.create('hover', {
            scale: 1.3,
            strokeWidth: 3,
          });

          const h2 = chart.plotContainer.children.push(
            am5.Circle.new(root, {
              radius: 8,
              fill: strokeCol,
              stroke: strokeCol,
              strokeWidth: 2,
              fillOpacity: 1,
              strokeOpacity: 1,
              isMeasured: true,
              visible: false,
              opacity: 0,
              interactive: true,
              zIndex: 1000,
              cursorOverStyle: 'pointer',
            })
          );

          // Add hover effects to component handle h2
          h2.states.create('hover', {
            scale: 1.3,
            strokeWidth: 3,
          });
          // Initial placement from line endpoints
          try {
            const di1 = ln.dataItems?.[0];
            const di2 = ln.dataItems?.[1];
            if (di1 && di2) {
              const px1 = dataToPixel(di1.get('valueX'), di1.get('valueY'));
              const px2 = dataToPixel(di2.get('valueX'), di2.get('valueY'));
              h1.setAll({ x: px1.x, y: px1.y });
              h2.setAll({ x: px2.x, y: px2.y });
            }
          } catch { }
          // Store
          perPeakHandles.set(compKey, { h1, h2 });

          // Function to sync handle colors with the baseline line
          const syncHandleColors = () => {
            try {
              const actualStrokeColor = ln?.strokes?.template?.get?.('stroke') || colorMap.get(compKey) || color;
              h1.setAll({ fill: actualStrokeColor, stroke: actualStrokeColor });
              h2.setAll({ fill: actualStrokeColor, stroke: actualStrokeColor });
            } catch (e) {
              // Fallback to stored color
              const fallbackColor = colorMap.get(compKey) || color;
              h1.setAll({ fill: fallbackColor, stroke: fallbackColor });
              h2.setAll({ fill: fallbackColor, stroke: fallbackColor });
            }
          };

          // Sync colors immediately after creation
          syncHandleColors();

          // Drag handlers: only active when this comp is selected and editing
          h1.events.on('pointerdown', (e) => {
            if (!editingRef.current) return;
            const sel = selectedPeakRef.current;
            if (!(sel && sel.rangeKey === key && sel.peakKey === compKey)) return;
            // Align and start drag for p1
            syncEndpointsToSelectedComp();
            // Begin editing; enable discard
            setChangesForFile(fileName, true);
            freezeYAxisNow();
            isDraggingRef.current = true;
            try {
              const chart = chartRef.current;
              chart?.set('wheelX', 'none');
              chart?.set('wheelY', 'none');
              chart?.set('panX', false);
              chart?.set('panY', false);
            } catch { }
            const pt = chart.plotContainer.toLocal(e.point);
            activeRangeRef.current = key;
            drag = { type: 'p1', startPx: pt, origP1: { ...p1 }, origP2: { ...p2 }, compKey };

            // Dispatch loading start event for component drag
            if (Array.isArray(peaksObject)) {
              const idxInPeaksObject = resolvePeakIndex(compKey);
              if (idxInPeaksObject >= 0) {
                try {
                  window.dispatchEvent(new CustomEvent('concentration-update-start', {
                    detail: { peakIndex: idxInPeaksObject, peakKey: compKey, rangeKey: key, fileName }
                  }));
                } catch { }
              }
            }
            e.stopPropagation?.();
          });
          h2.events.on('pointerdown', (e) => {
            if (!editingRef.current) return;
            const sel = selectedPeakRef.current;
            if (!(sel && sel.rangeKey === key && sel.peakKey === compKey)) return;
            // Align and start drag for p2
            syncEndpointsToSelectedComp();
            // Begin editing; enable discard
            setChangesForFile(fileName, true);
            freezeYAxisNow();
            isDraggingRef.current = true;
            try {
              const chart = chartRef.current;
              chart?.set('wheelX', 'none');
              chart?.set('wheelY', 'none');
              chart?.set('panX', false);
              chart?.set('panY', false);
            } catch { }
            const pt = chart.plotContainer.toLocal(e.point);
            activeRangeRef.current = key;
            drag = { type: 'p2', startPx: pt, origP1: { ...p1 }, origP2: { ...p2 }, compKey };

            // Dispatch loading start event for component drag  
            if (Array.isArray(peaksObject)) {
              const idxInPeaksObject = resolvePeakIndex(compKey);
              if (idxInPeaksObject >= 0) {
                try {
                  window.dispatchEvent(new CustomEvent('concentration-update-start', {
                    detail: { peakIndex: idxInPeaksObject, peakKey: compKey, rangeKey: key, fileName }
                  }));
                } catch { }
              }
            }
            e.stopPropagation?.();
          });
        });
      } else {
        // Single-peak/simple: one pair for the range
        handle1 = chart.plotContainer.children.push(
          am5.Circle.new(root, {
            radius: 8,
            fill: color,
            stroke: color,
            strokeWidth: 2,
            fillOpacity: 1,
            strokeOpacity: 1,
            isMeasured: true,
            visible: false,
            opacity: 0,
            interactive: true,
            zIndex: 1000,
            cursorOverStyle: 'pointer',
          })
        );

        // Add simple hover effects to handle1
        handle1.states.create('hover', {
          scale: 1.3,
          strokeWidth: 3,
        });

        handle2 = chart.plotContainer.children.push(
          am5.Circle.new(root, {
            radius: 8,
            fill: color,
            stroke: color,
            strokeWidth: 2,
            fillOpacity: 1,
            strokeOpacity: 1,
            isMeasured: true,
            visible: false,
            opacity: 0,
            interactive: true,
            zIndex: 1000,
            cursorOverStyle: 'pointer',
          })
        );

        // Add simple hover effects to handle2
        handle2.states.create('hover', {
          scale: 1.3,
          strokeWidth: 3,
        });

        const syncHandleColors = () => {
          const strokeCol = line?.strokes?.template?.get?.('stroke') || color;
          handle1.setAll({ fill: strokeCol, stroke: strokeCol });
          handle2.setAll({ fill: strokeCol, stroke: strokeCol });
        };
        const updateHandles = () => {
          // For non-comp ranges, prefer the control line's current data items if available
          let P1 = p1, P2 = p2;
          try {
            if (compMap.size === 0 && getDI1 && getDI2) {
              const di1m = getDI1();
              const di2m = getDI2();
              if (di1m && di2m) {
                P1 = { x: di1m.get('valueX'), y: di1m.get('valueY') };
                P2 = { x: di2m.get('valueX'), y: di2m.get('valueY') };
              }
            }
          } catch { }
          const h1 = dataToPixel(P1.x, P1.y);
          const h2 = dataToPixel(P2.x, P2.y);

          // Check if handles are within visible chart bounds
          const plotBounds = chart.plotContainer.bounds();
          const h1InBounds = h1.x >= 0 && h1.x <= plotBounds.right && h1.y >= 0 && h1.y <= plotBounds.bottom;
          const h2InBounds = h2.x >= 0 && h2.x <= plotBounds.right && h2.y >= 0 && h2.y <= plotBounds.bottom;

          handle1.setAll({
            x: h1.x,
            y: h1.y,
            visible: editingRef.current && h1InBounds,
            opacity: (editingRef.current && h1InBounds) ? 1 : 0
          });
          handle2.setAll({
            x: h2.x,
            y: h2.y,
            visible: editingRef.current && h2InBounds,
            opacity: (editingRef.current && h2InBounds) ? 1 : 0
          });
          syncHandleColors();
          // Keep single-peak area fill color synced with baseline line
          syncSingleFillToLine();
        };
        updateHandles();
        updateHandlesFn = updateHandles;
      }
      // Attach handle listeners only for single-peak/simple mode
      if (compMap.size === 0 && handle1 && handle2) {
        handle1.events.on('pointerdown', (e) => {
          if (!editingRef.current) {
            e.stopPropagation?.();
            return;
          }
          // If comps exist in this range, require an explicit selection (from comp area) before editing
          // Single-peak/simple: auto-select nearest peak at pointer x
          selectNearestPeakAtPointer(key, e.point);
          setChangesForFile(fileName, true);
          // Prevent auto zoom-out by freezing current Y view on drag start
          freezeYAxisNow();
          isDraggingRef.current = true;
          // Disable wheel zoom and panning during drag to prevent interference
          try {
            const chart = chartRef.current;
            chart?.set('wheelX', 'none');
            chart?.set('wheelY', 'none');
            chart?.set('panX', false);
            chart?.set('panY', false);
          } catch { }
          const pt = chart.plotContainer.toLocal(e.point);
          // Start dragging p1
          activeRangeRef.current = key;
          drag = { type: 'p1', startPx: pt, origP1: { ...p1 }, origP2: { ...p2 } };

          // Dispatch loading start event when baseline endpoint drag begins (only for selected peak)
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key && Array.isArray(peaksObject)) {
            const idxInPeaksObject = resolvePeakIndex(sel.peakKey);
            if (idxInPeaksObject >= 0) {
              try {
                window.dispatchEvent(new CustomEvent('concentration-update-start', {
                  detail: { peakIndex: idxInPeaksObject, peakKey: sel.peakKey, rangeKey: key, fileName }
                }));
              } catch { }
            }
          }
          e.stopPropagation?.();
        });
        handle2.events.on('pointerdown', (e) => {
          if (!editingRef.current) {
            e.stopPropagation?.();
            return;
          }
          // Single-peak/simple: auto-select nearest peak at pointer x
          selectNearestPeakAtPointer(key, e.point);
          setChangesForFile(fileName, true);
          // Prevent auto zoom-out by freezing current Y view on drag start
          freezeYAxisNow();
          isDraggingRef.current = true;
          // Disable wheel zoom and panning temporarily during drag
          try {
            const chart = chartRef.current;
            chart?.set('wheelX', 'none');
            chart?.set('wheelY', 'none');
            chart?.set('panX', false);
            chart?.set('panY', false);
          } catch { }
          const pt = chart.plotContainer.toLocal(e.point);
          // Start dragging p2
          activeRangeRef.current = key;
          drag = { type: 'p2', startPx: pt, origP1: { ...p1 }, origP2: { ...p2 } };

          // Dispatch loading start event when baseline endpoint drag begins (only for selected peak)
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key && Array.isArray(peaksObject)) {
            const idxInPeaksObject = resolvePeakIndex(sel.peakKey);
            if (idxInPeaksObject >= 0) {
              try {
                window.dispatchEvent(new CustomEvent('concentration-update-start', {
                  detail: { peakIndex: idxInPeaksObject, peakKey: sel.peakKey, rangeKey: key, fileName }
                }));
              } catch { }
            }
          }
          e.stopPropagation?.();
        });
      }

      // Store
      baselineByRangeRef.current.set(key, { p1, p2, line, linesMap: perPeakLines, fill, color, getDI1, getDI2, handle1, handle2, handlesMap: perPeakHandles });

      // Simple drag interactions: endpoints + vertical shift
      const HANDLE_RADIUS_PX = 10;
      const HOVER_RADIUS_PX = 12;
      const LINE_HIT_PX = 8;
      const valueDeltaPerPixelY = (atY) => {
        const y0 = atY;
        const y1 = y0 + 10;
        const p0 = yAxis.get('renderer').positionToCoordinate(yAxis.valueToPosition(y0));
        const p1c = yAxis.get('renderer').positionToCoordinate(yAxis.valueToPosition(y1));
        const pxPerUnit = (p1c - p0) / 10;
        return pxPerUnit === 0 ? 0 : 1 / pxPerUnit;
      };

      let drag = { type: null, startPx: null, origP1: null, origP2: null };
      // Click on the baseline to drag (middle = vertical-only)
      chart.plotContainer.events.on('pointerdown', (e) => {
        if (!editingRef.current) {
          // When not in edit mode, completely ignore this event to avoid interference
          return;
        }
        // console.log('ChartPreview: Baseline drag pointerdown handler triggered, editingRef.current:', editingRef.current);
        // If a comp is selected for this range, align working endpoints to that line before hit-testing
        if (compMap.size > 0) {
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key) syncEndpointsToSelectedComp();
        }
        const pt = chart.plotContainer.toLocal(e.point);
        // Use actual line endpoints for non-comp ranges to avoid stale p1/p2 closure values
        let P1m = p1, P2m = p2;
        try {
          if (compMap.size === 0 && getDI1 && getDI2) {
            const di1m = getDI1();
            const di2m = getDI2();
            if (di1m && di2m) {
              P1m = { x: di1m.get('valueX'), y: di1m.get('valueY') };
              P2m = { x: di2m.get('valueX'), y: di2m.get('valueY') };
            }
          }
        } catch { }
        const p1px = dataToPixel(P1m.x, P1m.y);
        const p2px = dataToPixel(P2m.x, P2m.y);
        const dEnd1 = Math.hypot(pt.x - p1px.x, pt.y - p1px.y);
        const dEnd2 = Math.hypot(pt.x - p2px.x, pt.y - p2px.y);
        // Don't start line drag if near endpoints; endpoints are handled by bullets
        if (dEnd1 <= HANDLE_RADIUS_PX || dEnd2 <= HANDLE_RADIUS_PX) return;
        const proj = projectToSeg(pt.x, pt.y, p1px, p2px);
        if (proj.d <= LINE_HIT_PX) {
          if (compMap.size > 0) {
            // Comps: require pre-selection; do not select from the baseline
            const sel = selectedPeakRef.current;
            if (!(sel && sel.rangeKey === key)) return;
            // Align active endpoints to selected comp's baseline
            syncEndpointsToSelectedComp();
            // Begin editing; enable discard
            setChangesForFile(fileName, true);
          } else {
            // Single-peak/simple: always select nearest peak at this pointer x when clicking the baseline
            selectNearestPeakAtPointer(key, e.point);
            setChangesForFile(fileName, true);
          }
          // Treat any point along the baseline (excluding endpoints) as center drag (vertical shift)
          freezeYAxisNow();
          isDraggingRef.current = true;
          // Disable wheel zoom and panning during drag to prevent interference
          try {
            const chart = chartRef.current;
            chart?.set('wheelX', 'none');
            chart?.set('wheelY', 'none');
            chart?.set('panX', false);
            chart?.set('panY', false);
          } catch { }
          activeRangeRef.current = key;
          drag = { type: 'line_v', startPx: pt, origP1: { ...p1 }, origP2: { ...p2 } };

          // Dispatch loading start event when baseline drag begins (only for selected peak)
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key && Array.isArray(peaksObject)) {
            const idxInPeaksObject = resolvePeakIndex(sel.peakKey);
            if (idxInPeaksObject >= 0) {
              try {
                window.dispatchEvent(new CustomEvent('concentration-update-start', {
                  detail: { peakIndex: idxInPeaksObject, peakKey: sel.peakKey, rangeKey: key, fileName }
                }));
              } catch { }
            }
          }
          if (compMap.size > 0) {
            try { perPeakLines.forEach((ln) => ln?.strokes?.template?.setAll?.({ strokeWidth: 2 })); } catch { }
            const sel = selectedPeakRef.current;
            if (sel && sel.rangeKey === key && perPeakLines.has(sel.peakKey)) {
              perPeakLines.get(sel.peakKey)?.strokes?.template?.setAll?.({ strokeWidth: 5 });
              // Also sync handle colors on selection change
              const { h1, h2 } = perPeakHandles.get(sel.peakKey) || {};
              if (h1 && h2) {
                const ln = perPeakLines.get(sel.peakKey);
                const actualStrokeColor = ln?.strokes?.template?.get?.('stroke') || colorMap.get(sel.peakKey) || color;
                h1.setAll({ fill: actualStrokeColor, stroke: actualStrokeColor });
                h2.setAll({ fill: actualStrokeColor, stroke: actualStrokeColor });
              }
              // Sync comp area fill to the selected baseline stroke as well
              try {
                const fs = compMap.get(sel.peakKey);
                const ln = perPeakLines.get(sel.peakKey);
                const strokeCol = ln?.strokes?.template?.get?.('stroke') || colorMap.get(sel.peakKey) || color;
                fs?.fills?.template?.setAll?.({ fill: strokeCol });
              } catch { }
            }
            // Show only the selected comp's handles
            showSelectedCompHandles();
          } else {
            line.strokes.template.setAll({ strokeWidth: 5 });
            if (handle1 && editingRef.current) {
              const h1px = dataToPixel(p1.x, p1.y);
              const plotBounds = chart.plotContainer.bounds();
              const h1InBounds = h1px.x >= 0 && h1px.x <= plotBounds.right && h1px.y >= 0 && h1px.y <= plotBounds.bottom;
              handle1.setAll({ visible: h1InBounds, opacity: h1InBounds ? 1 : 0 });
            }
            if (handle2 && editingRef.current) {
              const h2px = dataToPixel(p2.x, p2.y);
              const plotBounds = chart.plotContainer.bounds();
              const h2InBounds = h2px.x >= 0 && h2px.x <= plotBounds.right && h2px.y >= 0 && h2px.y <= plotBounds.bottom;
              handle2.setAll({ visible: h2InBounds, opacity: h2InBounds ? 1 : 0 });
            }
            // Keep single-peak area fill color in sync with baseline line on drag start
            syncSingleFillToLine();
          }
        }
      });

      // Helpers for comp handles visibility/positioning
      const showSelectedCompHandles = () => {
        try {
          if (compMap.size === 0) return;
          const sel = selectedPeakRef.current;
          if (!(sel && sel.rangeKey === key)) return;
          const hh = perPeakHandles.get(sel.peakKey);
          if (!hh) return;
          const h1px = dataToPixel(p1.x, p1.y);
          const h2px = dataToPixel(p2.x, p2.y);
          // Sync color from the selected comp baseline line
          const lnSel = perPeakLines.get(sel.peakKey);
          const selStroke = lnSel?.strokes?.template?.get?.('stroke') || colorMap.get(sel.peakKey) || color;
          // Check if handles are within visible chart bounds
          const plotBounds = chart.plotContainer.bounds();
          const h1InBounds = h1px.x >= 0 && h1px.x <= plotBounds.right && h1px.y >= 0 && h1px.y <= plotBounds.bottom;
          const h2InBounds = h2px.x >= 0 && h2px.x <= plotBounds.right && h2px.y >= 0 && h2px.y <= plotBounds.bottom;
          // Only show handles when in edit mode and within bounds
          const shouldShow1 = editingRef.current && h1InBounds;
          const shouldShow2 = editingRef.current && h2InBounds;
          hh.h1?.setAll?.({ x: h1px.x, y: h1px.y, visible: shouldShow1, opacity: shouldShow1 ? 1 : 0, fill: selStroke, stroke: selStroke });
          hh.h2?.setAll?.({ x: h2px.x, y: h2px.y, visible: shouldShow2, opacity: shouldShow2 ? 1 : 0, fill: selStroke, stroke: selStroke });
        } catch { }
      };
      const hideAllCompHandles = () => {
        try { perPeakHandles.forEach(({ h1, h2 }) => { h1?.setAll?.({ visible: false, opacity: 0 }); h2?.setAll?.({ visible: false, opacity: 0 }); }); } catch { }
      };
      const showAllCompHandles = () => {
        try {
          if (compMap.size === 0) return;
          const plotBounds = chart.plotContainer.bounds();
          perPeakLines.forEach((ln, cKey) => {
            const hh = perPeakHandles.get(cKey);
            if (!hh) return;
            const di1 = ln?.dataItems?.[0];
            const di2 = ln?.dataItems?.[1];
            if (!di1 || !di2) return;
            const px1 = dataToPixel(di1.get('valueX'), di1.get('valueY'));
            const px2 = dataToPixel(di2.get('valueX'), di2.get('valueY'));
            // Sync color from each comp baseline line
            const strokeCol = ln?.strokes?.template?.get?.('stroke') || colorMap.get(cKey) || color;
            // Check if handles are within visible chart bounds
            const h1InBounds = px1.x >= 0 && px1.x <= plotBounds.right && px1.y >= 0 && px1.y <= plotBounds.bottom;
            const h2InBounds = px2.x >= 0 && px2.x <= plotBounds.right && px2.y >= 0 && px2.y <= plotBounds.bottom;
            // Only show handles when in edit mode and within bounds
            const shouldShow1 = editingRef.current && h1InBounds;
            const shouldShow2 = editingRef.current && h2InBounds;
            hh.h1?.setAll?.({ x: px1.x, y: px1.y, visible: shouldShow1, opacity: shouldShow1 ? 1 : 0, fill: strokeCol, stroke: strokeCol });
            hh.h2?.setAll?.({ x: px2.x, y: px2.y, visible: shouldShow2, opacity: shouldShow2 ? 1 : 0, fill: strokeCol, stroke: strokeCol });
            // Also sync comp area fill color
            try { const fs = compMap.get(cKey); fs?.fills?.template?.setAll?.({ fill: strokeCol }); } catch { }
          });
        } catch { }
      };

      chart.plotContainer.events.on('globalpointermove', (e) => {
        const pt = chart.plotContainer.toLocal(e.point);

        // Dynamic panning control for Insert Peak mode based on proximity to chromatogram
        if (insertingPeakRef.current && !drawingBaselineRef.current) {
          try {
            const data = pixelToData(pt.x, pt.y);
            if (data) {
              // Check if pointer is near the chromatogram line
              const seriesY = getSeriesYAtX(data.x);

              if (seriesY != null) {
                // Use pixel-based threshold for more precise control
                // Convert both points to pixels and measure distance
                const seriesPixel = dataToPixel(data.x, seriesY);
                const pointerPixel = { x: pt.x, y: pt.y };
                const pixelDistance = Math.abs(pointerPixel.y - seriesPixel.y);
                const threshold = 10; // 10 pixels - adjust this value as needed

                // Enable/disable panning based on proximity
                if (pixelDistance > threshold) {
                  setPanEnabled(true); // Far from chromatogram - allow panning
                } else {
                  setPanEnabled(false); // Near chromatogram - disable panning to allow drawing
                }
              } else {
                setPanEnabled(true); // Allow panning when not over valid chromatogram
              }
            }
          } catch (err) {
            // If any error occurs, default to allowing panning
            setPanEnabled(true);
          }
        }

        // Handle Insert Peak mode - update drawing baseline
        if (insertingPeakRef.current && drawingBaselineRef.current) {
          const currentData = pixelToData(pt.x, pt.y);
          if (!currentData) return;

          const drawState = drawingBaselineRef.current;
          const startPoint = drawState.startPoint;

          // Get visible peak boundaries to constrain drawing
          const freshFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName] || null;
          const freshFileData = freshFile?.fileData || freshFile;
          const hiddenPeaks = freshFileData?.hiddenPeaks || {};
          const allRanges = freshFileData?.result || {};

          // Collect visible peak ranges
          const visiblePeakRanges = [];
          for (const rangeKey of Object.keys(allRanges)) {
            const rangeData = allRanges[rangeKey];
            const allPeaks = rangeData?.peaks || {};

            for (const pk of Object.keys(allPeaks)) {
              if (!hiddenPeaks[pk]) {
                const peak = allPeaks[pk];
                const peakX = peak?.originalXComp || peak?.x_comp || peak?.x || [];
                if (peakX.length > 0) {
                  const peakMinX = Math.min(...peakX);
                  const peakMaxX = Math.max(...peakX);
                  visiblePeakRanges.push({ minX: peakMinX, maxX: peakMaxX });
                }
              }
            }
          }

          // Constrain currentData.x based on visible peak boundaries
          if (currentData.x > startPoint.x) {
            // Drawing to the right - find nearest visible peak to the right of startPoint
            let rightBoundary = Infinity;
            for (const peakRange of visiblePeakRanges) {
              if (peakRange.minX > startPoint.x && peakRange.minX < rightBoundary) {
                rightBoundary = peakRange.minX;
              }
            }
            if (currentData.x > rightBoundary) {
              currentData.x = rightBoundary;
            }
          } else if (currentData.x < startPoint.x) {
            // Drawing to the left - find nearest visible peak to the left of startPoint
            let leftBoundary = -Infinity;
            for (const peakRange of visiblePeakRanges) {
              if (peakRange.maxX < startPoint.x && peakRange.maxX > leftBoundary) {
                leftBoundary = peakRange.maxX;
              }
            }
            if (currentData.x < leftBoundary) {
              currentData.x = leftBoundary;
            }
          }

          // Apply Y constraint: baseline cannot be above chromatogram
          const yLimitAtCurrent = getSeriesYAtX(currentData.x);
          if (yLimitAtCurrent != null && currentData.y > yLimitAtCurrent) {
            currentData.y = yLimitAtCurrent;
          }

          drawState.currentPoint = currentData;

          // Update temporary line to show current drawing
          try {
            drawState.tempLine.data.setAll([
              { x: drawState.startPoint.x, y: drawState.startPoint.y },
              { x: currentData.x, y: currentData.y }
            ]);
          } catch { }

          return;
        }

        // Handle Split Peak mode - show/update preview line on hover over single peaks
        if (splittingPeakRef.current) {
          // Don't show hover preview if assignment UI is active
          if (assignmentLabelsRef.current && assignmentLabelsRef.current.length > 0) {
            return;
          }

          const currentData = pixelToData(pt.x, pt.y);
          if (!currentData) {
            // Not over valid chart area - hide preview
            if (drawingSplitLineRef.current) {
              try {
                drawingSplitLineRef.current.tempLine.set('visible', false);
              } catch { }
            }
            return;
          }

          // Find ALL peaks at the current cursor position (multi-peak support)
          const fileData = currentFile?.fileData || currentFile;
          const result = fileData?.result || {};
          const hiddenPeaks = fileData?.hiddenPeaks || {};

          // Collect all peaks that contain the cursor X position
          const peaksAtX = [];

          for (const [rKey, rData] of Object.entries(result)) {
            const peaks = rData?.peaks || {};

            Object.entries(peaks).forEach(([peakKey, peak]) => {
              // Skip hidden (deleted) peaks
              if (hiddenPeaks[peakKey]) return;

              const peakX = peak?.x_comp || peak?.x || [];
              if (peakX.length === 0) return;

              const xMin = Math.min(...peakX);
              const xMax = Math.max(...peakX);

              // Check if cursor is within this peak's boundaries
              if (currentData.x >= xMin && currentData.x <= xMax) {
                peaksAtX.push({
                  rangeKey: rKey,
                  peakKey,
                  peak,
                  xMin,
                  xMax
                });
              }
            });
          }

          // SMART SELECTION LOGIC
          let targetPeak = null;
          let targetRangeKey = null;

          if (peaksAtX.length === 0) {
            // No peaks at cursor - hide preview
            if (drawingSplitLineRef.current) {
              try {
                drawingSplitLineRef.current.tempLine.set('visible', false);
              } catch { }
            }
            // Clean up any existing triangles (if they were shown for some reason)
            if (peakSelectionTrianglesRef.current.length > 0) {
              peakSelectionTrianglesRef.current.forEach(series => {
                try {
                  if (series && chartRef.current) {
                    chartRef.current.series.removeValue(series);
                  }
                } catch { }
              });
              peakSelectionTrianglesRef.current = [];
            }
            return;
          } else if (peaksAtX.length === 1) {
            // Single peak - use it
            targetPeak = { peakKey: peaksAtX[0].peakKey, xMin: peaksAtX[0].xMin, xMax: peaksAtX[0].xMax };
            targetRangeKey = peaksAtX[0].rangeKey;

            // Clean up triangles if any
            if (peakSelectionTrianglesRef.current.length > 0) {
              peakSelectionTrianglesRef.current.forEach(series => {
                try {
                  if (series && chartRef.current) {
                    chartRef.current.series.removeValue(series);
                  }
                } catch { }
              });
              peakSelectionTrianglesRef.current = [];
            }
          } else {
            // Multiple peaks - show split line on the HIGHEST peak at this X as default
            // Triangles will appear AFTER clicking, not on hover

            // Find highest peak at cursor X
            let highestPeak = peaksAtX[0];
            let highestY = -Infinity;

            for (const candidate of peaksAtX) {
              const peakX = candidate.peak?.x_comp || candidate.peak?.x || [];
              const peakY = candidate.peak?.y_comp || candidate.peak?.y || [];

              // Interpolate Y at cursor X
              let yAtX = peakY[0];
              for (let i = 0; i < peakX.length - 1; i++) {
                if (peakX[i] <= currentData.x && currentData.x <= peakX[i + 1]) {
                  const ratio = (currentData.x - peakX[i]) / (peakX[i + 1] - peakX[i]);
                  yAtX = peakY[i] + ratio * (peakY[i + 1] - peakY[i]);
                  break;
                }
              }

              if (yAtX > highestY) {
                highestY = yAtX;
                highestPeak = candidate;
              }
            }

            targetPeak = { peakKey: highestPeak.peakKey, xMin: highestPeak.xMin, xMax: highestPeak.xMax };
            targetRangeKey = highestPeak.rangeKey;

            // Store all candidates for the click handler to use
            // Only update if the X position has changed or it's the first time
            if (!drawingSplitLineRef.current || drawingSplitLineRef.current.splitX !== currentData.x) {
              selectedSplitPeakRef.current = { peaksAtX, defaultPeak: highestPeak };
            }

            // Hide triangles if they were previously shown
            if (peakSelectionTrianglesRef.current.length > 0) {
              peakSelectionTrianglesRef.current.forEach(series => {
                try {
                  if (series && chartRef.current) {
                    chartRef.current.series.removeValue(series);
                  }
                } catch { }
              });
              peakSelectionTrianglesRef.current = [];
            }
          }

          if (!targetPeak) {
            // Shouldn't happen, but handle edge case
            if (drawingSplitLineRef.current) {
              try {
                drawingSplitLineRef.current.tempLine.set('visible', false);
              } catch { }
            }
            // Clear any highlighted peak
            if (highlightedSplitPeakRef.current) {
              try {
                const prevRangeKey = highlightedSplitPeakRef.current.rangeKey;
                const prevPeakKey = highlightedSplitPeakRef.current.peakKey;
                const compMap = compFillsByRangeRef.current.get(prevRangeKey);
                if (compMap) {
                  const fillSeries = compMap.get(prevPeakKey);
                  if (fillSeries) {
                    fillSeries.fills.template.setAll({ fillOpacity: 0.15 }); // Restore normal opacity
                  }
                }
              } catch { }
              highlightedSplitPeakRef.current = null;
            }
            return;
          }

          // HIGHLIGHT THE TARGET PEAK for visual feedback
          try {
            // First, restore previous highlighted peak to normal
            if (highlightedSplitPeakRef.current &&
              (highlightedSplitPeakRef.current.rangeKey !== targetRangeKey ||
                highlightedSplitPeakRef.current.peakKey !== targetPeak.peakKey)) {
              const prevRangeKey = highlightedSplitPeakRef.current.rangeKey;
              const prevPeakKey = highlightedSplitPeakRef.current.peakKey;
              const prevCompMap = compFillsByRangeRef.current.get(prevRangeKey);
              if (prevCompMap) {
                const prevFillSeries = prevCompMap.get(prevPeakKey);
                if (prevFillSeries) {
                  prevFillSeries.fills.template.setAll({ fillOpacity: 0.15 }); // Restore normal
                }
              }
            }

            // Now highlight the current target peak
            const compMap = compFillsByRangeRef.current.get(targetRangeKey);
            if (compMap) {
              const fillSeries = compMap.get(targetPeak.peakKey);
              if (fillSeries) {
                fillSeries.fills.template.setAll({ fillOpacity: 0.5 }); // Brighter/richer
                highlightedSplitPeakRef.current = { rangeKey: targetRangeKey, peakKey: targetPeak.peakKey };
              }
            }
          } catch (err) {
            console.error('Error highlighting target peak:', err);
          }

          // Constrain X to peak boundaries
          let constrainedX = currentData.x;
          if (constrainedX < targetPeak.xMin) constrainedX = targetPeak.xMin;
          if (constrainedX > targetPeak.xMax) constrainedX = targetPeak.xMax;

          // Get the peak data to calculate Y boundaries (baseline to chromatogram)
          const rangeData = result[targetRangeKey];
          const peak = rangeData?.peaks?.[targetPeak.peakKey];
          const peakX = peak?.x_comp || peak?.x || [];
          const peakY = peak?.y_comp || peak?.y || [];
          const peakBaseline = peak?.y_baseline || [];

          // Find the Y values at the constrained X position by interpolation
          let yBottom = 0; // baseline Y
          let yTop = 0; // chromatogram Y

          // Find the two points that bracket constrainedX
          for (let i = 0; i < peakX.length - 1; i++) {
            if (peakX[i] <= constrainedX && constrainedX <= peakX[i + 1]) {
              // Interpolate Y values
              const ratio = (constrainedX - peakX[i]) / (peakX[i + 1] - peakX[i]);
              yTop = peakY[i] + ratio * (peakY[i + 1] - peakY[i]);
              yBottom = peakBaseline[i] + ratio * (peakBaseline[i + 1] - peakBaseline[i]);
              break;
            }
          }

          // If we're exactly at the boundaries, use the boundary values
          if (constrainedX === peakX[0]) {
            yTop = peakY[0];
            yBottom = peakBaseline[0];
          } else if (constrainedX === peakX[peakX.length - 1]) {
            yTop = peakY[peakY.length - 1];
            yBottom = peakBaseline[peakBaseline.length - 1];
          }

          // Create or update preview line constrained between baseline and chromatogram
          if (!drawingSplitLineRef.current) {
            // Create preview line
            const tempLine = chart.series.push(
              am5xy.LineSeries.new(root, {
                valueXField: 'x',
                valueYField: 'y',
                xAxis,
                yAxis,
                stroke: am5.color(0xff0000),
                strokeWidth: 2,
                strokeDasharray: [5, 5],
                tooltip: am5.Tooltip.new(root, {
                  labelText: 'Click to split peak here',
                  pointerOrientation: 'down',
                  dy: -15 // Position tooltip above the cursor
                })
              })
            );

            tempLine.data.setAll([
              { x: constrainedX, y: yBottom },
              { x: constrainedX, y: yTop }
            ]);

            drawingSplitLineRef.current = {
              rangeKey: targetRangeKey,
              peakKey: targetPeak.peakKey,
              splitX: constrainedX,
              tempLine,
              xMin: targetPeak.xMin,
              xMax: targetPeak.xMax
            };
          } else {
            // Update existing preview line
            const drawState = drawingSplitLineRef.current;

            // Update position and metadata
            drawState.rangeKey = targetRangeKey;
            drawState.peakKey = targetPeak.peakKey;
            drawState.splitX = constrainedX;
            drawState.xMin = targetPeak.xMin;
            drawState.xMax = targetPeak.xMax;

            try {
              drawState.tempLine.set('visible', true);
              drawState.tempLine.data.setAll([
                { x: constrainedX, y: yBottom },
                { x: constrainedX, y: yTop }
              ]);
            } catch { }
          }

          return;
        }

        if (!editingRef.current) {
          // When not editing, ensure handles are hidden and baseline stroke width normal
          if (handle1 && handle2) {
            handle1.setAll({ visible: false, opacity: 0 });
            handle2.setAll({ visible: false, opacity: 0 });
          }
          hideAllCompHandles();
          if (line) line.strokes.template.setAll({ strokeWidth: 2 });
          if (perPeakLines?.size) { try { perPeakLines.forEach((ln) => ln?.strokes?.template?.setAll?.({ strokeWidth: 2 })); } catch { } }
          return;
        }
        // While hovering (not dragging), if a comp is selected for this range, keep endpoints synced
        if (!drag.type && compMap.size > 0) {
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key) {
            syncEndpointsToSelectedComp();
          }
        }
        if (drag.type) {
          // Dragging logic
          if (drag.type === 'p1' || drag.type === 'p2') {
            const nd = pixelToData(pt.x, pt.y);
            const sel = selectedPeakRef.current;
            const currentPeakKey = sel?.peakKey;

            // Get visible peak boundaries to constrain movement
            const visiblePeakRanges = getVisiblePeakBoundaries(currentPeakKey);

            if (drag.type === 'p1') {
              p1.x = nd.x;
              p1.y = nd.y;

              // Find the nearest visible peak to the LEFT of p2
              // P1 cannot extend past this peak
              let leftBoundary = -Infinity;
              for (const peakRange of visiblePeakRanges) {
                // Only consider peaks to the left of p2
                if (peakRange.maxX < p2.x) {
                  // This peak is to the left, check if it's the nearest
                  if (peakRange.maxX > leftBoundary) {
                    leftBoundary = peakRange.maxX;
                  }
                }
              }

              // Constrain p1 to not go past the left boundary
              if (p1.x < leftBoundary) {
                p1.x = leftBoundary;
              }

              const yLim = getYLimitAtX(p1.x);
              if (yLim != null && p1.y > yLim) p1.y = yLim;
              const apex = getApexX();
              if (apex != null && Number.isFinite(apex) && p1.x > apex) {
                p1.x = apex;
                const yLim2 = getYLimitAtX(p1.x);
                if (yLim2 != null && p1.y > yLim2) p1.y = yLim2;
              }
            } else {
              p2.x = nd.x;
              p2.y = nd.y;

              // Find the nearest visible peak to the RIGHT of p1
              // P2 cannot extend past this peak
              let rightBoundary = Infinity;
              for (const peakRange of visiblePeakRanges) {
                // Only consider peaks to the right of p1
                if (peakRange.minX > p1.x) {
                  // This peak is to the right, check if it's the nearest
                  if (peakRange.minX < rightBoundary) {
                    rightBoundary = peakRange.minX;
                  }
                }
              }

              // Constrain p2 to not go past the right boundary
              if (p2.x > rightBoundary) {
                p2.x = rightBoundary;
              }

              const yLim = getYLimitAtX(p2.x);
              if (yLim != null && p2.y > yLim) p2.y = yLim;
              const apex = getApexX();
              if (apex != null && Number.isFinite(apex) && p2.x < apex) {
                p2.x = apex;
                const yLim2 = getYLimitAtX(p2.x);
                if (yLim2 != null && p2.y > yLim2) p2.y = yLim2;
              }
            }
          } else if (drag.type === 'line_v') {
            // Vertical-only shift when dragging the middle
            const dyPx = pt.y - drag.startPx.y;
            const dYVal = -dyPx * Math.abs(valueDeltaPerPixelY(drag.origP1.y));
            p1.y = drag.origP1.y + dYVal;
            p2.y = drag.origP2.y + dYVal;
            const yL1 = getYLimitAtX(p1.x);
            const yL2 = getYLimitAtX(p2.x);
            if (yL1 != null && p1.y > yL1) p1.y = yL1;
            if (yL2 != null && p2.y > yL2) p2.y = yL2;
          }
          // Update visuals
          // Update baseline endpoints via dataItem.set to avoid flicker
          try {
            const bl = baselineByRangeRef.current.get(key);
            const linesMap = bl?.linesMap;
            const sel = selectedPeakRef.current;
            // Update only the selected comp's baseline line if a comp is selected
            if (sel && sel.rangeKey === key && linesMap && linesMap.size && linesMap.has(sel.peakKey)) {
              const ln = linesMap.get(sel.peakKey);
              try { ln?.data?.setAll?.([p1, p2]); } catch { }
              // Also update its area fill live
              const cMap = compFillsByRangeRef.current.get(key);
              const fillSeries = cMap ? cMap.get(sel.peakKey) : null;
              if (fillSeries) {
                try { fillSeries.data.setAll(buildCompFillData(sel.peakKey, p1, p2)); } catch { }
              }
            } else {
              // Fallback: update control line
              if (bl && bl.getDI1 && bl.getDI2) {
                const di1 = bl.getDI1();
                const di2 = bl.getDI2();
                if (di1) {
                  di1.set('valueX', p1.x);
                  di1.set('valueXWorking', p1.x);
                  di1.set('valueY', p1.y);
                  di1.set('valueYWorking', p1.y);
                }
                if (di2) {
                  di2.set('valueX', p2.x);
                  di2.set('valueXWorking', p2.x);
                  di2.set('valueY', p2.y);
                  di2.set('valueYWorking', p2.y);
                }
              } else if (line) {
                line.data.setAll([p1, p2]);
              }
              // Single-peak/simple case: live update the generic fill too
              if (compMap.size === 0) {
                try { fill.data.setAll(buildFill(p1, p2)); } catch { }
              }
            }
          } catch {
            try { line?.data?.setAll?.([p1, p2]); } catch { }
          }
          // Previously we avoided fill updates during drag to prevent autoscale.
          // We now freeze Y during drag, so generic fill updates are safe and handled above for single-peak.
          if (compMap.size === 0) {
            // Single-peak: update and show simple handles
            if (typeof updateHandlesFn === 'function') updateHandlesFn();
            if (handle1 && handle2 && editingRef.current) {
              const h1px = dataToPixel(p1.x, p1.y);
              const h2px = dataToPixel(p2.x, p2.y);
              const plotBounds = chart.plotContainer.bounds();

              const h1InBounds = h1px.x >= 0 && h1px.x <= plotBounds.right && h1px.y >= 0 && h1px.y <= plotBounds.bottom;
              handle1.setAll({ visible: h1InBounds, opacity: h1InBounds ? 1 : 0 });

              const h2InBounds = h2px.x >= 0 && h2px.x <= plotBounds.right && h2px.y >= 0 && h2px.y <= plotBounds.bottom;
              handle2.setAll({ visible: h2InBounds, opacity: h2InBounds ? 1 : 0 });
            }
          } else {
            // Comps: keep selected comp handles visible
            hideAllCompHandles();
            showSelectedCompHandles();
          }
          // Ensure single-peak area fill color remains in sync during drag
          if (compMap.size === 0) {
            syncSingleFillToLine();
          }
        } else {
          // Hover logic (show handles when near endpoints; thicken when near middle)
          // Use actual line endpoints for non-comp ranges when computing hover distances
          let P1h = p1, P2h = p2;
          try {
            if (compMap.size === 0 && getDI1 && getDI2) {
              const di1h = getDI1();
              const di2h = getDI2();
              if (di1h && di2h) {
                P1h = { x: di1h.get('valueX'), y: di1h.get('valueY') };
                P2h = { x: di2h.get('valueX'), y: di2h.get('valueY') };
              }
            }
          } catch { }
          const p1px = dataToPixel(P1h.x, P1h.y);
          const p2px = dataToPixel(P2h.x, P2h.y);
          const near1 = Math.hypot(pt.x - p1px.x, pt.y - p1px.y) <= HOVER_RADIUS_PX;
          const near2 = Math.hypot(pt.x - p2px.x, pt.y - p2px.y) <= HOVER_RADIUS_PX;
          if (compMap.size === 0) {
            if (typeof updateHandlesFn === 'function') updateHandlesFn();
          } else {
            // Keep handles visible: if selected in this range, show only selected comp; else show all comps
            const sel = selectedPeakRef.current;
            if (sel && sel.rangeKey === key) showSelectedCompHandles(); else showAllCompHandles();
          }
          // Determine proximity to this baseline
          const proj = projectToSeg(pt.x, pt.y, p1px, p2px);
          const isOnBaseline = proj.d <= LINE_HIT_PX;
          // In edit mode, keep endpoints visible even for comp ranges without selection
          // Decide which baseline should be active based on closest distance
          if (near1 || near2 || isOnBaseline) {
            const thisDist = Math.min(
              Math.hypot(pt.x - p1px.x, pt.y - p1px.y),
              Math.hypot(pt.x - p2px.x, pt.y - p2px.y),
              proj.d
            );
            const activeKey = activeRangeRef.current;
            if (!activeKey || activeKey === key) {
              activeRangeRef.current = key;
            } else {
              const active = baselineByRangeRef.current.get(activeKey);
              if (active) {
                const ap1px = dataToPixel(active.p1.x, active.p1.y);
                const ap2px = dataToPixel(active.p2.x, active.p2.y);
                const aproj = projectToSeg(pt.x, pt.y, ap1px, ap2px);
                const activeDist = Math.min(
                  Math.hypot(pt.x - ap1px.x, pt.y - ap1px.y),
                  Math.hypot(pt.x - ap2px.x, pt.y - ap2px.y),
                  aproj.d
                );
                // Only switch if this baseline is strictly closer by >1px to avoid flicker on ties
                if (thisDist + 1 < activeDist) {
                  activeRangeRef.current = key;
                }
              } else {
                activeRangeRef.current = key;
              }
            }
          }
          const isActive = activeRangeRef.current === key;
          if (compMap.size === 0) {
            if (editingRef.current) {
              // Always show endpoints during edit mode (single-peak) but only if in bounds
              if (handle1 && handle2) {
                const h1px = dataToPixel(p1.x, p1.y);
                const h2px = dataToPixel(p2.x, p2.y);
                const plotBounds = chart.plotContainer.bounds();

                const h1InBounds = h1px.x >= 0 && h1px.x <= plotBounds.right && h1px.y >= 0 && h1px.y <= plotBounds.bottom;
                handle1.setAll({ visible: h1InBounds, opacity: h1InBounds ? 1 : 0 });

                const h2InBounds = h2px.x >= 0 && h2px.x <= plotBounds.right && h2px.y >= 0 && h2px.y <= plotBounds.bottom;
                handle2.setAll({ visible: h2InBounds, opacity: h2InBounds ? 1 : 0 });
              }
            } else if (handle1 && handle2) {
              // In non-edit mode, apply both bounds checking and proximity/active checks
              const h1px = dataToPixel(p1.x, p1.y);
              const h2px = dataToPixel(p2.x, p2.y);
              const plotBounds = chart.plotContainer.bounds();

              const h1InBounds = h1px.x >= 0 && h1px.x <= plotBounds.right && h1px.y >= 0 && h1px.y <= plotBounds.bottom;
              handle1.setAll({ visible: isActive && near1 && h1InBounds, opacity: isActive && near1 && h1InBounds ? 1 : 0 });

              const h2InBounds = h2px.x >= 0 && h2px.x <= plotBounds.right && h2px.y >= 0 && h2px.y <= plotBounds.bottom;
              handle2.setAll({ visible: isActive && near2 && h2InBounds, opacity: isActive && near2 && h2InBounds ? 1 : 0 });
            }
          } else {
            // For comps: in edit mode, show selected comp's handles; if no selection, show all
            hideAllCompHandles();
            if (editingRef.current) {
              const sel = selectedPeakRef.current;
              if (sel && sel.rangeKey === key) showSelectedCompHandles(); else showAllCompHandles();
            }
          }
          // Thicken the correct line for hover
          if (compMap.size > 0) {
            // Reset all per-peak lines to normal, then thicken the selected comp’s line only when hovering baseline
            try { perPeakLines.forEach((ln) => ln?.strokes?.template?.setAll?.({ strokeWidth: 2 })); } catch { }
            const sel = selectedPeakRef.current;
            if (isActive && isOnBaseline && sel && sel.rangeKey === key && perPeakLines.has(sel.peakKey)) {
              const ln = perPeakLines.get(sel.peakKey);
              ln?.strokes?.template?.setAll?.({ strokeWidth: 5 });
            }
          } else {
            line.strokes.template.setAll({ strokeWidth: isActive && isOnBaseline ? 5 : 2 });
            // Keep single-peak area fill color synced while hovering
            syncSingleFillToLine();
          }
        }
      });
      chart.plotContainer.events.on('pointerout', () => {
        if (drag.type) return;
        if (!editingRef.current) {
          if (handle1 && handle2) {
            handle1.setAll({ visible: false, opacity: 0 });
            handle2.setAll({ visible: false, opacity: 0 });
          }
          hideAllCompHandles();
        } else if (compMap.size > 0) {
          // In edit mode, keep handles visible on pointer out: show selected or all if none selected
          hideAllCompHandles();
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key) showSelectedCompHandles(); else showAllCompHandles();
        }
        // Clear active baseline when leaving plot to avoid sticky state
        if (activeRangeRef.current === key) activeRangeRef.current = null;
        if (compMap.size > 0) {
          try { perPeakLines.forEach((ln) => ln?.strokes?.template?.setAll?.({ strokeWidth: 2 })); } catch { }
        } else {
          line.strokes.template.setAll({ strokeWidth: 2 });
        }
        // On pointer out, re-sync single-peak area fill color
        syncSingleFillToLine();
      });
      // (drag handled in unified globalpointermove above)
      chart.plotContainer.events.on('globalpointerup', async () => {
        // Handle Split Peak mode - complete drawing split line with auto-selection
        if (splittingPeakRef.current && drawingSplitLineRef.current) {
          const drawState = drawingSplitLineRef.current;
          const splitX = drawState.splitX;
          const rangeKey = drawState.rangeKey;
          const peakKey = drawState.peakKey;

          // Make the line solid to indicate it's placed
          try {
            drawState.tempLine.set('strokeDasharray', []);
            // Store in completed list for cleanup
            completedSplitLinesRef.current.push(drawState.tempLine);
          } catch { }

          // Clear drawingSplitLineRef
          drawingSplitLineRef.current = null;

          // Get peak data to determine which side has higher max
          const fileData = currentFile?.fileData || currentFile;
          const rangeData = fileData?.result?.[rangeKey];
          const peak = rangeData?.peaks?.[peakKey];
          const peakX = peak?.x_comp || peak?.x || [];
          const peakY = peak?.y_comp || peak?.y || [];

          if (peakX.length === 0 || peakY.length === 0) {
            console.error('Invalid peak data for auto-selection in globalpointerup');
            return;
          }

          // Split data into left and right sides
          const leftIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x < splitX);
          const rightIndices = peakX.map((x, i) => ({ x, y: peakY[i], i })).filter(p => p.x >= splitX);

          if (leftIndices.length === 0 || rightIndices.length === 0) {
            console.error('Split line does not properly divide the peak in globalpointerup');
            cleanupSplitDrawingState();
            return;
          }

          // Find max Y on each side
          const leftMax = Math.max(...leftIndices.map(p => p.y));
          const rightMax = Math.max(...rightIndices.map(p => p.y));

          // Auto-select: The side with higher max Y is the "original" peak
          const originalSide = leftMax > rightMax ? 'left' : 'right';

          // Process split immediately with auto-selected side
          processPeakSplit(rangeKey, peakKey, splitX, originalSide);

          return;
        }

        // Handle Insert Peak mode - complete drawing baseline
        if (insertingPeakRef.current && drawingBaselineRef.current) {
          const drawState = drawingBaselineRef.current;
          const p1 = drawState.startPoint;
          const p2 = drawState.currentPoint;
          const rangeKey = drawState.rangeKey;

          // Only proceed if user drew a meaningful baseline (not just a click)
          const distance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
          if (distance < 0.01) {
            // Too small, ignore and clean up
            cleanupDrawingState();
            return;
          }

          // Keep the line visible (don't clean up yet - it will be cleaned on discard or mode exit)
          // Make the line solid to indicate it's complete
          try {
            drawState.tempLine.set('strokeDasharray', []);
            // Track this line so we can clean it up later
            completedInsertLinesRef.current.push(drawState.tempLine);
            // Also track the fill
            if (drawState.tempFill) {
              completedInsertFillsRef.current.push(drawState.tempFill);
            }
          } catch { }

          // Clear the drawing state but keep the visual
          drawingBaselineRef.current = null;

          // Analyze the drawn baseline to check if it matches a hidden (deleted) peak
          const analyzeDrawnBaseline = () => {
            const fileData = currentFile?.fileData || currentFile;
            const hiddenPeaks = fileData?.hiddenPeaks || {};
            const rangeData = fileData?.result?.[rangeKey];
            const peaks = rangeData?.peaks || {};

            // Calculate the baseline X range with tolerance
            const tolerance = Math.abs(p2.x - p1.x) * 0.5; // 50% of baseline width
            const minX = Math.min(p1.x, p2.x) - tolerance;
            const maxX = Math.max(p1.x, p2.x) + tolerance;

            // Find ALL hidden peaks within the baseline range
            const candidatePeaks = [];
            Object.keys(hiddenPeaks).forEach(peakKey => {
              const peak = peaks[peakKey];
              if (peak && Number.isFinite(peak.x_peak)) {
                const peakX = peak.x_peak;
                // Check if peak apex is within the drawn baseline X range
                if (peakX >= minX && peakX <= maxX) {
                  candidatePeaks.push({
                    peakKey: peakKey,
                    x_peak: peakX,
                    peak: peak
                  });
                }
              }
            });

            // Process single matched peak
            if (candidatePeaks.length === 1) {
              const matchedPeakKey = candidatePeaks[0].peakKey;
              const peak = candidatePeaks[0].peak;
              if (peak) {
                // Get the X range from the drawn baseline
                const minX = Math.min(p1.x, p2.x);
                const maxX = Math.max(p1.x, p2.x);

                // Get the original chromatogram data from props.data
                const chromatogramData = props.data || [];

                // Find all chromatogram points within the new baseline X range
                const pointsInRange = chromatogramData.filter(point =>
                  point.x >= minX && point.x <= maxX
                );

                // Extract x and y arrays for the new peak region
                const newXComp = pointsInRange.map(p => p.x);
                const newYComp = pointsInRange.map(p => p.y);

                // Create baseline Y values matching the new X coordinates
                const newYBaseline = newXComp.map(x => {
                  // Interpolate Y on the drawn baseline
                  const t = (x - p1.x) / (p2.x - p1.x);
                  return p1.y + t * (p2.y - p1.y);
                });

                // Get the complete current file data from Redux
                const currentReduxFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName];
                if (!currentReduxFile) {
                  console.error('Could not find current file in Redux:', fileName);
                  return;
                }

                // Deep clone the entire file to avoid mutation
                const updatedFileData = JSON.parse(JSON.stringify(currentReduxFile));

                // Update the specific peak's baseline, x_comp, and y_comp
                if (!updatedFileData.result) updatedFileData.result = {};
                if (!updatedFileData.result[rangeKey]) updatedFileData.result[rangeKey] = {};
                if (!updatedFileData.result[rangeKey].peaks) updatedFileData.result[rangeKey].peaks = {};

                // IMPORTANT: Always use raw chromatogram data for originalXComp/originalYComp
                // This ensures that if the peak was previously deconvoluted, we "forget" the deconvolution
                // and use the actual raw chromatogram data
                const originalXComp = newXComp;
                const originalYComp = newYComp;

                // Exclude x and y from the spread
                const { x, y, ...peakWithoutXY } = peak;

                updatedFileData.result[rangeKey].peaks[matchedPeakKey] = {
                  ...peakWithoutXY,
                  x_comp: newXComp,
                  y_comp: newYComp,
                  y_baseline: newYBaseline,
                  originalXComp: originalXComp,  // Always use raw chromatogram data
                  originalYComp: originalYComp   // Always use raw chromatogram data
                };

                // Prepare the payload structure - must be an array of objects with fileName and fileData
                const payloadItem = {
                  fileName: fileName,
                  fileData: updatedFileData
                };

                const arrayPayload = [payloadItem];

                // Dispatch update to Redux with complete file data
                if (currentFileType === 'standard') {
                  dispatch(saveStdFilesData(arrayPayload));
                } else if (currentFileType === 'qualityControl') {
                  dispatch(saveQcFilesData(arrayPayload));
                } else if (currentFileType === 'unknown') {
                  dispatch(saveUnkFilesData(arrayPayload));
                }

                // Track this restoration for discard functionality
                restoredPeaksRef.current.push({
                  fileName: fileName,
                  fileType: currentFileType,
                  peakKey: matchedPeakKey
                });

                // Calculate area under the peak using trapezoidal rule
                let calculatedArea = 0;
                for (let i = 0; i < newXComp.length - 1; i++) {
                  const x0 = newXComp[i];
                  const x1 = newXComp[i + 1];
                  const dx = x1 - x0;

                  const y0 = newYComp[i];
                  const y1 = newYComp[i + 1];
                  const baseline0 = newYBaseline[i];
                  const baseline1 = newYBaseline[i + 1];

                  // Height above baseline (clamped to 0 if below baseline)
                  const h0 = Math.max(0, y0 - baseline0);
                  const h1 = Math.max(0, y1 - baseline1);

                  // Trapezoidal area: 0.5 * (h0 + h1) * dx
                  calculatedArea += 0.5 * (h0 + h1) * dx;
                }

                // Find the peak group index from peaksObject using the peak key
                const currentPeaksObject = Array.isArray(peaksObject) ? peaksObject : Object.values(peaksObject || {});
                const peakGroupIndex = resolvePeakIndex(matchedPeakKey);

                if (peakGroupIndex === -1) {
                  // This is expected immediately after restoration - the peak will be properly tracked
                  // after the chart rebuilds and the reporting peaks structure is updated
                  // console.log('Peak group index not found yet for restored peak:', matchedPeakKey, '- area will be calculated on next baseline edit');
                } else {
                  const peakData = currentPeaksObject[peakGroupIndex];

                  // Update area in Redux based on file type
                  if (currentFileType === 'standard') {
                    // Update standard areas
                    const currentStdAreas = stdAreasState?.[peakGroupIndex] || {};
                    const updatedStdAreas = { ...currentStdAreas, [fileName]: calculatedArea };

                    // Update the full areas structure for this peak group
                    const next = { ...(stdAreasState || {}) };
                    next[peakGroupIndex] = updatedStdAreas;

                    dispatch(saveAreaUnderPeaks(next));

                    // Recalculate calibration curve and all concentrations
                    // Use setTimeout to ensure this runs after the peak is fully restored and unhidden
                    setTimeout(() => {
                      recalculateStandardConcentrations(peakGroupIndex, updatedStdAreas);
                    }, 50);

                  } else if (currentFileType === 'qualityControl') {
                    // Update QC areas
                    const currentQcAreas = qcAreasState?.[peakGroupIndex] || {};
                    const updatedQcAreas = { ...currentQcAreas, [fileName]: calculatedArea };

                    // Update the full areas structure for this peak group
                    const groupKeys = currentPeaksObject.map((_, idx) => idx);
                    const next = (qcAreasState || []).map((m) => ({ ...(m || {}) }));
                    while (next.length < groupKeys.length) next.push({});
                    next[peakGroupIndex] = updatedQcAreas;

                    dispatch(saveAreaUnderPeaksQc(next));

                    // Recalculate QC concentrations using current calibration curve
                    if (peakData?.equation?.coefficients) {
                      const coeffs = peakData.equation.coefficients;
                      const concentration = coeffs.reduce((acc, coeff, index) =>
                        acc + coeff * Math.pow(calculatedArea, coeffs.length - 1 - index), 0
                      );

                      dispatch(batchUpdatePeakConcentrations({
                        peakIndex: peakGroupIndex,
                        qcConcentrations: { [fileName]: concentration }
                      }));
                    }

                  } else if (currentFileType === 'unknown') {
                    // Update Unknown areas
                    const currentUnkAreas = unkAreasState?.[peakGroupIndex] || {};
                    const updatedUnkAreas = { ...currentUnkAreas, [fileName]: calculatedArea };

                    // Update the full areas structure for this peak group
                    const groupKeys = currentPeaksObject.map((_, idx) => idx);
                    const next = (unkAreasState || []).map((m) => ({ ...(m || {}) }));
                    while (next.length < groupKeys.length) next.push({});
                    next[peakGroupIndex] = updatedUnkAreas;

                    dispatch(saveAreaUnderPeaksUnk(next));

                    // Recalculate Unknown concentrations using current calibration curve
                    if (peakData?.equation?.coefficients) {
                      const coeffs = peakData.equation.coefficients;
                      const concentration = coeffs.reduce((acc, coeff, index) =>
                        acc + coeff * Math.pow(calculatedArea, coeffs.length - 1 - index), 0
                      );

                      dispatch(batchUpdatePeakConcentrations({
                        peakIndex: peakGroupIndex,
                        unkConcentrations: { [fileName]: concentration }
                      }));
                    }
                  }
                }

                // Unhide the peak AFTER all area/concentration updates to avoid race conditions
                // where chart rebuild might reset concentrations before they're calculated
                setTimeout(() => {
                  dispatch(togglePeakVisibility({
                    fileName: fileName,
                    fileType: currentFileType,
                    peakKey: matchedPeakKey
                  }));
                }, 100);

                // Clean up the temporary drawing line since the real baseline will show
                setTimeout(() => {
                  cleanupDrawingState();
                }, 150);
              }
            } else if (candidatePeaks.length > 1) {
              // Multiple matches - show assignment UI with clickable triangle indicators

              // Rainbow colors for different peaks
              const colors = [0xFF6B6B, 0x4ECDC4, 0x45B7D1, 0xFFA07A, 0x98D8C8, 0xF7DC6F];

              // Create a temporary label series to hold all assignment indicators
              const labelSeriesList = [];

              candidatePeaks.forEach((candidate, index) => {
                try {
                  const xValue = candidate.x_peak;
                  // Position triangle above the peak apex
                  const chromatogramY = getSeriesYAtX(xValue) || 0;
                  const yValue = chromatogramY * 1.15; // 15% above peak

                  // Create a series for this triangle indicator
                  const labelSeries = chart.series.push(
                    am5xy.LineSeries.new(root, {
                      xAxis: xAxis,
                      yAxis: yAxis,
                      valueYField: 'y',
                      valueXField: 'x'
                    })
                  );
                  labelSeries.strokes.template.setAll({ visible: false });

                  // Add bullet with triangle pointing down
                  labelSeries.bullets.push(() => {
                    const container = am5.Container.new(root, {
                      centerX: am5.p50,
                      centerY: am5.p0
                    });

                    // Create triangle shape pointing downward
                    const triangle = container.children.push(
                      am5.Triangle.new(root, {
                        width: 24,
                        height: 24,
                        fill: am5.color(colors[index % colors.length]),
                        stroke: am5.color(0xffffff),
                        strokeWidth: 2,
                        rotation: 180, // Point downward
                        cursorOverStyle: 'pointer',
                        tooltipText: `Click to assign baseline to peak ${candidate.peakKey}`,
                        interactive: true,
                        // Add hover animation
                        scale: 1
                      })
                    );

                    // Add hover effect
                    triangle.states.create('hover', {
                      scale: 1.2
                    });

                    // Handle click - process selected peak
                    triangle.events.on('click', () => {
                      // Clean up all assignment labels
                      labelSeriesList.forEach(ls => {
                        try {
                          chart.series.removeValue(ls);
                        } catch (e) {
                          console.warn('Error removing label series:', e);
                        }
                      });

                      // Clear the ref after cleanup
                      assignmentLabelsRef.current = [];

                      // Process the selected peak using the same restoration logic
                      const selectedPeak = candidate.peak;
                      const selectedPeakKey = candidate.peakKey;

                      // Get the X range from the drawn baseline
                      const restoreMinX = Math.min(p1.x, p2.x);
                      const restoreMaxX = Math.max(p1.x, p2.x);

                      // Get the original chromatogram data from props.data
                      const chromatogramData = props.data || [];

                      // Find all chromatogram points within the new baseline X range
                      const pointsInRange = chromatogramData.filter(point =>
                        point.x >= restoreMinX && point.x <= restoreMaxX
                      );

                      // Extract x and y arrays for the new peak region
                      const newXComp = pointsInRange.map(p => p.x);
                      const newYComp = pointsInRange.map(p => p.y);

                      // Create baseline Y values matching the new X coordinates
                      const newYBaseline = newXComp.map(x => {
                        // Interpolate Y on the drawn baseline
                        const t = (x - p1.x) / (p2.x - p1.x);
                        return p1.y + t * (p2.y - p1.y);
                      });

                      // Get the complete current file data from Redux
                      const currentReduxFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName];
                      if (!currentReduxFile) {
                        console.error('Could not find current file in Redux:', fileName);
                        return;
                      }

                      // Deep clone the entire file to avoid mutation
                      const updatedFileData = JSON.parse(JSON.stringify(currentReduxFile));

                      // Update the specific peak's baseline, x_comp, and y_comp
                      if (!updatedFileData.result) updatedFileData.result = {};
                      if (!updatedFileData.result[rangeKey]) updatedFileData.result[rangeKey] = {};
                      if (!updatedFileData.result[rangeKey].peaks) updatedFileData.result[rangeKey].peaks = {};

                      // IMPORTANT: Always use raw chromatogram data for originalXComp/originalYComp
                      // This ensures that if the peak was previously deconvoluted, we "forget" the deconvolution
                      // and use the actual raw chromatogram data
                      const originalXComp = newXComp;
                      const originalYComp = newYComp;

                      // Exclude x and y from the spread
                      const { x, y, ...peakWithoutXY } = selectedPeak;

                      updatedFileData.result[rangeKey].peaks[selectedPeakKey] = {
                        ...peakWithoutXY,
                        x_comp: newXComp,
                        y_comp: newYComp,
                        y_baseline: newYBaseline,
                        originalXComp: originalXComp,  // Always use raw chromatogram data
                        originalYComp: originalYComp   // Always use raw chromatogram data
                      };

                      // Prepare the payload structure
                      const payloadItem = {
                        fileName: fileName,
                        fileData: updatedFileData
                      };
                      const arrayPayload = [payloadItem];

                      // Dispatch update to Redux
                      if (currentFileType === 'standard') {
                        dispatch(saveStdFilesData(arrayPayload));
                      } else if (currentFileType === 'qualityControl') {
                        dispatch(saveQcFilesData(arrayPayload));
                      } else if (currentFileType === 'unknown') {
                        dispatch(saveUnkFilesData(arrayPayload));
                      }

                      // Unhide the peak
                      dispatch(togglePeakVisibility({
                        fileName: fileName,
                        fileType: currentFileType,
                        peakKey: selectedPeakKey
                      }));

                      // Track this restoration for discard functionality
                      restoredPeaksRef.current.push({
                        fileName: fileName,
                        fileType: currentFileType,
                        peakKey: selectedPeakKey
                      });

                      // Calculate area under the peak using trapezoidal rule
                      let calculatedArea = 0;
                      for (let i = 0; i < newXComp.length - 1; i++) {
                        const x0 = newXComp[i];
                        const x1 = newXComp[i + 1];
                        const dx = x1 - x0;

                        const y0 = newYComp[i];
                        const y1 = newYComp[i + 1];
                        const baseline0 = newYBaseline[i];
                        const baseline1 = newYBaseline[i + 1];

                        const h0 = Math.max(0, y0 - baseline0);
                        const h1 = Math.max(0, y1 - baseline1);

                        calculatedArea += 0.5 * (h0 + h1) * dx;
                      }

                      // Find the peak group index and update areas/concentrations
                      const peakGroupIndex = resolvePeakIndex(selectedPeakKey);
                      if (peakGroupIndex !== -1) {
                        const currentPeaksObject = Array.isArray(peaksObject) ? peaksObject : Object.values(peaksObject || {});
                        const peakData = currentPeaksObject[peakGroupIndex];

                        if (currentFileType === 'standard') {
                          const currentStdAreas = stdAreasState?.[peakGroupIndex] || {};
                          const updatedStdAreas = { ...currentStdAreas, [fileName]: calculatedArea };
                          const next = { ...(stdAreasState || {}) };
                          next[peakGroupIndex] = updatedStdAreas;
                          dispatch(saveAreaUnderPeaks(next));
                          recalculateStandardConcentrations(peakGroupIndex, updatedStdAreas);
                        } else if (currentFileType === 'qualityControl') {
                          const currentQcAreas = qcAreasState?.[peakGroupIndex] || {};
                          const updatedQcAreas = { ...currentQcAreas, [fileName]: calculatedArea };
                          const groupKeys = currentPeaksObject.map((_, idx) => idx);
                          const next = (qcAreasState || []).map((m) => ({ ...(m || {}) }));
                          while (next.length < groupKeys.length) next.push({});
                          next[peakGroupIndex] = updatedQcAreas;
                          dispatch(saveAreaUnderPeaksQc(next));

                          if (peakData?.equation?.coefficients) {
                            const coeffs = peakData.equation.coefficients;
                            const concentration = coeffs.reduce((acc, coeff, index) =>
                              acc + coeff * Math.pow(calculatedArea, coeffs.length - 1 - index), 0
                            );
                            dispatch(batchUpdatePeakConcentrations({
                              peakIndex: peakGroupIndex,
                              qcConcentrations: { [fileName]: concentration }
                            }));
                          }
                        } else if (currentFileType === 'unknown') {
                          const currentUnkAreas = unkAreasState?.[peakGroupIndex] || {};
                          const updatedUnkAreas = { ...currentUnkAreas, [fileName]: calculatedArea };
                          const groupKeys = currentPeaksObject.map((_, idx) => idx);
                          const next = (unkAreasState || []).map((m) => ({ ...(m || {}) }));
                          while (next.length < groupKeys.length) next.push({});
                          next[peakGroupIndex] = updatedUnkAreas;
                          dispatch(saveAreaUnderPeaksUnk(next));

                          if (peakData?.equation?.coefficients) {
                            const coeffs = peakData.equation.coefficients;
                            const concentration = coeffs.reduce((acc, coeff, index) =>
                              acc + coeff * Math.pow(calculatedArea, coeffs.length - 1 - index), 0
                            );
                            dispatch(batchUpdatePeakConcentrations({
                              peakIndex: peakGroupIndex,
                              unkConcentrations: { [fileName]: concentration }
                            }));
                          }
                        }
                      }

                      // Clean up the temporary drawing line
                      setTimeout(() => {
                        cleanupDrawingState();
                      }, 100);
                    });

                    return am5.Bullet.new(root, {
                      sprite: container
                    });
                  });

                  // Set data for the label position
                  labelSeries.data.setAll([{ x: xValue, y: yValue }]);
                  labelSeriesList.push(labelSeries);

                } catch (e) {
                  console.error('Error creating assignment label:', e);
                }
              });

              // Store label series in ref so discard button can clean them up
              assignmentLabelsRef.current = labelSeriesList;

            } else {
              // No matching hidden peak - create a new peak

              const minX = Math.min(p1.x, p2.x);
              const maxX = Math.max(p1.x, p2.x);

              // Get the original chromatogram data from props.data
              const chromatogramData = props.data || [];

              // Find all chromatogram points within the new baseline X range
              const pointsInRange = chromatogramData.filter(point =>
                point.x >= minX && point.x <= maxX
              );

              if (pointsInRange.length === 0) {
                console.warn('No chromatogram data points in the drawn baseline range');
                return;
              }

              // Extract x and y arrays for the new peak region
              const newXComp = pointsInRange.map(p => p.x);
              const newYComp = pointsInRange.map(p => p.y);

              // Create baseline Y values matching the new X coordinates
              const newYBaseline = newXComp.map(x => {
                const t = (x - p1.x) / (p2.x - p1.x);
                return p1.y + t * (p2.y - p1.y);
              });

              // Find the peak apex (highest point above baseline)
              let maxHeightAboveBaseline = -Infinity;
              let peakApexX = newXComp[0];
              let peakApexY = newYComp[0];
              newXComp.forEach((x, i) => {
                const heightAboveBaseline = newYComp[i] - newYBaseline[i];
                if (heightAboveBaseline > maxHeightAboveBaseline) {
                  maxHeightAboveBaseline = heightAboveBaseline;
                  peakApexX = x;
                  peakApexY = newYComp[i];
                }
              });

              // Generate a unique peak key using the apex X coordinate (rounded to 6 decimals)
              const newPeakKey = peakApexX.toFixed(6);

              // Get the complete current file data from Redux
              const currentReduxFile = stdFiles[fileName] || qcFiles[fileName] || unkFiles[fileName];
              if (!currentReduxFile) {
                console.error('Could not find current file in Redux:', fileName);
                return;
              }

              // Deep clone the entire file to avoid mutation
              const updatedFileData = JSON.parse(JSON.stringify(currentReduxFile));

              // Ensure the result structure exists
              if (!updatedFileData.result) updatedFileData.result = {};
              if (!updatedFileData.result[rangeKey]) updatedFileData.result[rangeKey] = {};
              if (!updatedFileData.result[rangeKey].peaks) updatedFileData.result[rangeKey].peaks = {};

              // Create the new peak data structure
              updatedFileData.result[rangeKey].peaks[newPeakKey] = {
                x_peak: peakApexX,
                y_peak: peakApexY,
                x_comp: newXComp,
                y_comp: newYComp,
                y_baseline: newYBaseline,
                originalXComp: newXComp.slice(),  // Preserve original data
                originalYComp: newYComp.slice(),  // Preserve original data
                to_analyze: true // Mark as a peak to analyze
              };

              // IMPORTANT: Also add to range-level y_comp so the peak gets visualized
              if (!updatedFileData.result[rangeKey].y_comp) {
                updatedFileData.result[rangeKey].y_comp = {};
              }
              updatedFileData.result[rangeKey].y_comp[newPeakKey] = newYComp;

              // Ensure range-level x and y_baseline exist and match the peak data
              if (!updatedFileData.result[rangeKey].x || updatedFileData.result[rangeKey].x.length === 0) {
                updatedFileData.result[rangeKey].x = newXComp;
                updatedFileData.result[rangeKey].y_baseline = newYBaseline;
              }

              // Prepare the payload structure
              const payloadItem = {
                fileName: fileName,
                fileData: updatedFileData
              };
              const arrayPayload = [payloadItem];

              // Dispatch update to Redux
              if (currentFileType === 'standard') {
                dispatch(saveStdFilesData(arrayPayload));
              } else if (currentFileType === 'qualityControl') {
                dispatch(saveQcFilesData(arrayPayload));
              } else if (currentFileType === 'unknown') {
                dispatch(saveUnkFilesData(arrayPayload));
              }

              // Force chart recreation to show the new peak immediately
              // We need to increment the recreation counter to trigger useLayoutEffect
              setChartRecreationCounter(prev => prev + 1);

              // The new peak is visible by default (not in hiddenPeaks)
              // No need to toggle visibility

              // Track this new peak for discard functionality
              restoredPeaksRef.current.push({
                fileName: fileName,
                fileType: currentFileType,
                peakKey: newPeakKey,
                isNewPeak: true // Flag to indicate this is a newly created peak
              });

              // Clean up the temporary drawing line
              setTimeout(() => {
                cleanupDrawingState();
              }, 100);
            }
          };

          // Analyze and process the drawn baseline
          analyzeDrawnBaseline();

          // Enable discard button - it will stay enabled as long as there are restored peaks
          // The button state is now managed by whether restoredPeaksRef has content
          try {
            window.dispatchEvent(new CustomEvent('insert-peak-history-state', {
              detail: { canDiscard: true }
            }));
          } catch { }

          return;
        }

        if (!drag.type) return;
        drag = { type: null, startPx: null, origP1: null, origP2: null };
        line.strokes?.template?.setAll?.({ strokeWidth: 2 });
        // Persist
        lastEndpointsRef.current.set(key, { p1: { ...p1 }, p2: { ...p2 } });
        const existing = baselineByRangeRef.current.get(key) || {};
        const linesMap = existing.linesMap || new Map();
        // Keep endpoints record general; visualization lines updated only for selected comp above
        // Preserve the same p1/p2 object identities when updating the registry to keep all closures in sync
        if (existing && existing.p1 && existing.p2) {
          existing.p1.x = p1.x; existing.p1.y = p1.y;
          existing.p2.x = p2.x; existing.p2.y = p2.y;
          baselineByRangeRef.current.set(key, { ...existing, line, linesMap, fill, color });
        } else {
          baselineByRangeRef.current.set(key, { p1, p2, line, linesMap, fill, color });
        }
        // Check if baselines changed from snapshot
        const snap = snapshotsByFileRef.current.get(fileName);
        let changed = false;
        if (snap) {
          snap.forEach((entry, rKey) => {
            const v = baselineByRangeRef.current.get(rKey);
            if (!v) return;
            // Check control line
            if (entry.control && v.p1 && v.p2) {
              if (Math.abs(entry.control.p1.x - v.p1.x) > 1e-6 || Math.abs(entry.control.p1.y - v.p1.y) > 1e-6 ||
                Math.abs(entry.control.p2.x - v.p2.x) > 1e-6 || Math.abs(entry.control.p2.y - v.p2.y) > 1e-6) {
                changed = true;
              }
            }
            // Check per-comp lines
            if (entry.perComp && entry.perComp.size && v.linesMap?.size) {
              entry.perComp.forEach((pp, pk) => {
                const ln = v.linesMap.get(pk);
                if (ln) {
                  try {
                    const di1 = ln?.dataItems?.[0];
                    const di2 = ln?.dataItems?.[1];
                    if (di1 && di2) {
                      const cx1 = di1.get('valueX');
                      const cy1 = di1.get('valueY');
                      const cx2 = di2.get('valueX');
                      const cy2 = di2.get('valueY');
                      if (Math.abs(pp.p1.x - cx1) > 1e-6 || Math.abs(pp.p1.y - cy1) > 1e-6 ||
                        Math.abs(pp.p2.x - cx2) > 1e-6 || Math.abs(pp.p2.y - cy2) > 1e-6) {
                        changed = true;
                      }
                    }
                  } catch { }
                }
              });
            }
          });
        }
        if (changed) {
          setChangesForFile(fileName, true);
        }
        // Store Y viewport before data updates, then restore to prevent zoom flicker
        let currentYStart, currentYEnd;
        try {
          const yAxis = yAxisRef.current;
          if (yAxis) {
            currentYStart = yAxis.get('start');
            currentYEnd = yAxis.get('end');
          }
        } catch { }
        try {
          const yAxisLock = yAxisRef.current;
          const freeze = yFreezeRef.current;
          if (yAxisLock && freeze) {
            const v0 = yAxisLock.positionToValue(Math.min(freeze.start, freeze.end));
            const v1 = yAxisLock.positionToValue(Math.max(freeze.start, freeze.end));
            yAxisLock.set('strictMinMax', true);
            yAxisLock.set('min', v0);
            yAxisLock.set('max', v1);
          }
        } catch { }
        // Now refresh the selected comp’s fill once (if any) to reflect final baseline
        try {
          const sel = selectedPeakRef.current;
          if (sel && sel.rangeKey === key) {
            // Update line series endpoints using individual dataItems (like amCharts demo)
            const bl = baselineByRangeRef.current.get(key);
            const linesMap = bl?.linesMap;
            const ln = linesMap && linesMap.get ? linesMap.get(sel.peakKey) : null;
            try {
              const di1 = ln?.dataItems?.[0];
              const di2 = ln?.dataItems?.[1];
              if (di1 && di2) {
                di1.setAll(p1);
                di2.setAll(p2);
              }
            } catch { }
            // Update area fill once
            const cMap = compFillsByRangeRef.current.get(key);
            const fillSeries = cMap ? cMap.get(sel.peakKey) : null;
            if (fillSeries) {
              fillSeries.data.setAll(buildCompFillData(sel.peakKey, p1, p2));
            }
          } else {
            // Non-comp case: update the generic fill
            fill.data.setAll(buildFill(p1, p2));
          }
        } catch { }

        // Restore Y viewport immediately to prevent zoom flicker
        try {
          const yAxis = yAxisRef.current;
          if (yAxis && typeof currentYStart === 'number' && typeof currentYEnd === 'number') {
            yAxis.set('start', currentYStart);
            yAxis.set('end', currentYEnd);
          }
        } catch { }

        // Debounced submit to throttle API calls
        scheduleBaselineSubmit(key);
        // Handle visibility after drag ends
        if (compMap.size === 0) {
          // Single-peak: hide handles until next hover
          if (handle1 && handle2) {
            handle1.setAll({ visible: false, opacity: 0 });
            handle2.setAll({ visible: false, opacity: 0 });
          }
        } else {
          // Comps: keep selected comp handles visible
          hideAllCompHandles();
          showSelectedCompHandles();
        }
        // After drag ends, ensure single-peak area fill color matches baseline line
        if (compMap.size === 0) {
          syncSingleFillToLine();
        }
        // Clean up freeze state
        try {
          const yAxis = yAxisRef.current;
          if (yAxis) {
            // Dispose freeze listeners and RAF
            try { yFreezeDisposerRef.current?.dispose?.(); } catch { }
            yFreezeDisposerRef.current = null;
            try { if (yFreezeRafRef.current) cancelAnimationFrame(yFreezeRafRef.current); } catch { }
            yFreezeRafRef.current = 0;
            // Let the disposer handle lock release; keep dragging state true until it completes
            // to suppress any late viewport changes from internal pointerup processing.
            setTimeout(() => {
              // Now it's safe to mark drag inactive and clear any leftover freeze refs
              isDraggingRef.current = false;
              yFreezeRef.current = null;
              // Reset wheel and pan settings after drag completion
              const chart = chartRef.current;
              if (chart) {
                // Don't restore saved settings - instead set correct values based on current mode
                if (editingRef.current) {
                  // Still in edit mode - keep navigation disabled
                  chart.set('wheelX', 'none');
                  chart.set('wheelY', 'zoomXY');
                  chart.set('panX', false);
                  chart.set('panY', false);
                  chart.plotContainer.set('cursorOverStyle', 'default');
                } else {
                  // Exited edit mode - enable full navigation
                  chart.set('wheelX', 'panX');
                  chart.set('wheelY', 'zoomXY');
                  chart.set('panX', true);
                  chart.set('panY', true);
                  chart.set('pinchZoomX', true);
                  chart.plotContainer.set('cursorOverStyle', 'grab');
                }
              }
            }, 360);
          }
          const xAxis = xAxisRef.current;
          if (xAxis) {
            xAxis.set('strictMinMax', false);
            xAxis.set('min', undefined);
            xAxis.set('max', undefined);
          }
        } catch { }
        // Deselect peak after drag ends
        // Keep selection after drag ends per request
      });
    };

    // Render comps and baselines
    Object.entries(computedRange || {}).forEach(([key, range]) => {
      createCompFills(key, range);
      if (Array.isArray(range.x) && range.x.length > 1 && range.x.length === range.y_baseline.length) {
        addBaselineForRange(key, range);
      }
    });

    // After all ranges are created, sync all colors so they match even before editing/hover
    const syncAllColorsNow = () => {
      try {
        baselineByRangeRef.current.forEach((layer, rk) => {
          const compMap = compFillsByRangeRef.current.get(rk);
          if (compMap && compMap.size > 0) {
            // For comps: set each area fill to its baseline line stroke color
            try {
              const linesMap = layer?.linesMap || new Map();
              compMap.forEach((fs, cKey) => {
                try {
                  const ln = linesMap.get(cKey);
                  const strokeCol = ln?.strokes?.template?.get?.('stroke');
                  if (strokeCol) fs?.fills?.template?.setAll?.({ fill: strokeCol });
                } catch { }
              });
            } catch { }

            // Hide range-level baseline fill when component fills exist
            // console.log('🎨 Hiding range-level fill for range', rk, '- using', compMap.size, 'component fills');
            try { layer?.fill?.set?.('visible', false); } catch { }
            try { layer?.fill?.fills?.template?.setAll?.({ visible: false, fillOpacity: 0 }); } catch { }
            try { layer?.fill?.strokes?.template?.setAll?.({ strokeOpacity: 0 }); } catch { }
          } else {
            // Single-peak: sync generic area fill from the baseline line stroke and ensure it's visible
            // console.log('🎨 Ensuring range-level fill is visible for range', rk, '- no component fills available');
            try {
              const strokeCol = layer?.line?.strokes?.template?.get?.('stroke');
              if (strokeCol) layer?.fill?.fills?.template?.setAll?.({ fill: strokeCol });
              // Ensure range fill is visible as primary visualization
              layer?.fill?.set?.('visible', true);
              layer?.fill?.fills?.template?.setAll?.({ visible: true, fillOpacity: 0.25 });
            } catch { }
          }
        });
      } catch { }
    };
    const scheduleColorSyncs = () => {
      // Run now
      syncAllColorsNow();
      // Next two animation frames
      try {
        const r1 = requestAnimationFrame(() => {
          syncAllColorsNow();
          const r2 = requestAnimationFrame(() => syncAllColorsNow());
          colorSyncTimersRef.current.push({ type: 'raf', id: r2 });
        });
        colorSyncTimersRef.current.push({ type: 'raf', id: r1 });
      } catch { }
      // Small timeout in case themes apply slightly later
      try {
        const t = setTimeout(syncAllColorsNow, 60);
        colorSyncTimersRef.current.push({ type: 'timeout', id: t });
      } catch { }
    };
    scheduleColorSyncs();

    // If we are already in edit mode (user switched files while editing), capture a snapshot now that baselines exist
    if (editingRef.current) {
      try { captureSnapshotForActiveFile(); } catch { }
    }

    // Chart setup complete - hide loading animations
    try {
      loadingIndicator.dispose();
    } catch { }
    setIsChartLoading(false);

    return () => {
      try { root.dispose(); } catch { }
      try { window.removeEventListener('baseline-edit-mode', onEditToggle); } catch { }
      try { window.removeEventListener('delete-peak-mode', onDeletePeakToggle); } catch { }
      try { window.removeEventListener('delete-peak-mode-change', onDeletePeakModeChange); } catch { }
      try { window.removeEventListener('insert-peak-mode', onInsertPeakToggle); } catch { }
      try { window.removeEventListener('split-peak-mode', onSplitPeakToggle); } catch { }
      try { window.removeEventListener('baseline-action', onBaselineAction); } catch { }
      try { window.removeEventListener('delete-peak-action', onDeletePeakAction); } catch { }
      try { window.removeEventListener('insert-peak-action', onInsertPeakAction); } catch { }
      try { window.removeEventListener('split-peak-action', onSplitPeakAction); } catch { }
      rootRef.current = null;
      chartRef.current = null;
      xAxisRef.current = null;
      yAxisRef.current = null;
      mainSeriesRef.current = null;
      baselineByRangeRef.current = new Map();
      baselineColorByRangeRef.current = new Map();
      // Clear comp color/fill caches to avoid cross-file color leakage
      // Clear any scheduled color sync timers
      try {
        (colorSyncTimersRef.current || []).forEach((h) => {
          if (h?.type === 'raf') cancelAnimationFrame(h.id);
          if (h?.type === 'timeout') clearTimeout(h.id);
        });
        colorSyncTimersRef.current = [];
      } catch { }
      try { compColorsByRangeRef.current = new Map(); } catch { }
      compFillsByRangeRef.current = new Map();
      selectedPeakRef.current = null;
      // Clear color allocations for this file to get fresh palette per file
      try {
        const fileKey = fileName || 'default-file';
        peakColorsByFileRef.current?.delete?.(fileKey);
      } catch { }
      // Clear any pending debounced submits
      try {
        submitDebounceMapRef.current.forEach((t) => clearTimeout(t));
        submitDebounceMapRef.current.clear();
      } catch { }
      // Do not clear snapshots here; they are per-file and may be used if returning to this file during the same session.
    };
  }, [fileName, chartRecreationCounter]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div id={chartId} style={{ width: '100%', height: '100%' }} />
      {isChartLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ChartPreview;